/**
 * In-memory MOCK data store for the staff/admin/tracking surfaces (no backend yet — Inky's Go
 * backend lands on inky/go-backend separately). Seeded with a handful of Linn Valley concerns so
 * the queue, detail, timeline, property panel, and tracking view all render real-looking data.
 * Swap for real `fetch` calls when the API lands; the shapes (api/types.ts) already match.
 *
 * The public projection for the tracking view is derived HERE by an allow-list (mirrors
 * @td/shared toPublicRequest): reporter PII + internal events/attachments are dropped so the
 * tracking document structurally cannot leak them (NFR-3).
 */
import type { RequestStatus } from "@td/shared";
import type { PublicTracking } from "./trackingTypes.js";
import type {
  CommitMeetingImportResult,
  MeetingImportDetail,
  MeetingImportRow,
  OrdinanceCodeNode,
  OrdinanceCodeVersion,
  OrdinanceDraftIngestResult,
  OrdinancePublishResult,
  OrdinanceRatingsSummary,
  OrdinanceRef,
  OrdinanceRefEntityType,
  OrdinanceRefsReverseResult,
  OrdinanceReviewProgress,
  OrdinanceSearchHit,
  OrdinanceVersionDetail,
  StartMeetingImportInput,
  StartMeetingImportResult,
  UpdateMeetingImportRowInput,
} from "./types.js";
export type { PublicTracking } from "./trackingTypes.js";
import type {
  AddSubscriberByStaffInput,
  AdvanceCaseInput,
  AdvancePermitInput,
  AttachmentView,
  BroadcastDetail,
  BroadcastRow,
  BulkItemResult,
  CaseFigures,
  CaseQueueRow,
  CaseStatus,
  CategoryKind,
  CategoryView,
  ContractorDetailView,
  ContractorQueueRow,
  ContractorStatus,
  ContractorView,
  CouncilReportFigures,
  CouncilReportResult,
  CouncilReportScope,
  CreateBroadcastInput,
  CreatePermitByStaffInput,
  CreatePermitByStaffResult,
  DeliveryMethod,
  FAQAnalytics,
  FAQView,
  FormValues,
  GroupView,
  Inspection,
  InspectionProgressSummary,
  MailingAddress,
  NotificationTemplateView,
  NotifyLandingResult,
  OcrPermitFieldsInput,
  OcrPermitFieldsResult,
  OpenCaseInput,
  OpenCaseResult,
  NewCaseOpened,
  NewPermitApplication,
  OverdueCase,
  PermitFigures,
  PermitQueueRow,
  PermitStatus,
  PermissionsRegistryModule,
  PreferencesResult,
  PreferencesUpdateResult,
  PublicContractorView,
  PublishFAQInput,
  PutContractorInput,
  RenewContractorInput,
  KORAQueueRow,
  KORAStatus,
  QuestionQueueRow,
  QuestionStatus,
  QueueRow,
  RecordInspectionInput,
  RecordsPreferredFormat,
  RespondKORAInput,
  SavedView,
  SavedViewFilters,
  SendBroadcastResult,
  StaffCaseDetail,
  StaffKORADetail,
  StaffPermitDetail,
  StaffQuestionDetail,
  StaffRequestDetail,
  StaffUserView,
  SubmitPermitInput,
  SubscribeResult,
  SubscriberView,
  SubscriptionStatus,
  SuggestFAQDraft,
  UpdateFAQInput,
  Visibility,
} from "./types.js";
import {
  canTransitionCase,
  canTransitionKORA,
  canTransitionPermit,
  isCaseTerminal,
  isKORATerminal,
  isPermitTerminal,
} from "./types.js";
import { ApiError } from "./client.js";
import type { BrandingCrawlResult, InviteStaffResult, ResendOutcome } from "./staffApi.js";
import type { SubmitQuestionInput } from "./publicApi.js";
import type { SubmitKORAInput } from "./types.js";
import type { SubmitReportResult } from "./publicApi.js";
import { MOCK_QUESTION_TOPICS } from "./questionTopics.js";
import { MOCK_CHANNELS } from "./channels.js";
import { MOCK_KORA_RECORD_TYPES } from "./koraRecordTypes.js";
import { MOCK_CASE_VIOLATION_TYPES } from "./caseViolationTypes.js";
import { MOCK_PERMIT_TYPES, TANK_COUNT_FORM_KEY } from "./permitTypes.js";
import { MOCK_TRADE_CATEGORIES } from "./tradeCategories.js";
import {
  effectiveSLA,
  evaluateSLA,
  evaluateWorkItemDue,
  businessDaysSince,
  DEFAULT_SLA_ACK_DAYS,
  DEFAULT_SLA_RESOLVE_DAYS,
} from "../lib/sla.js";

const now = Date.UTC(2026, 6, 12, 15, 0, 0);
const daysAgo = (d: number) => new Date(now - d * 86_400_000).toISOString();
const hoursAgo = (h: number) => new Date(now - h * 3_600_000).toISOString();

// A tiny inline SVG data-URI stands in for a signed image thumbnail.
const IMG = (hue: number) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><rect width='120' height='120' fill='hsl(${hue} 40% 70%)'/><text x='60' y='66' font-size='40' text-anchor='middle' fill='white'>IMG</text></svg>`,
  )}`;

const STAFF: StaffUserView[] = [
  {
    id: "u-mayor",
    name: "Lister Potter",
    email: "clerk@linnvalley.example.gov",
    role: "admin",
    status: "active",
    mfaEnrolled: true,
    lastActiveAt: hoursAgo(2),
    groupIds: ["tmpl-admin"],
    effectivePermissions: [],
  },
  {
    id: "u-officer",
    name: "Dana Reyes",
    email: "codes@linnvalley.example.gov",
    role: "staff",
    status: "active",
    mfaEnrolled: true,
    lastActiveAt: hoursAgo(20),
    groupIds: ["tmpl-clerk"],
    effectivePermissions: [],
  },
  {
    id: "u-pending",
    name: null,
    email: "publicworks@linnvalley.example.gov",
    role: "staff",
    status: "invited",
    mfaEnrolled: false,
  },
  {
    id: "u-half-state",
    name: null,
    // A pre-#181 half-state row (a directory row with no matching Cognito account, e.g. Lister's
    // td-test-invite@yopmail.com case) — used to exercise the "Resend invite" provision-if-missing
    // recovery path (RESEND_HALF_STATE_IDS below).
    email: "half-state@linnvalley.example.gov",
    role: "staff",
    status: "invited",
    mfaEnrolled: false,
  },
  {
    // A RoleContributor user (agenda-packet.md §3.2) — the "Department Head / Contributor" scoped
    // role. Used to exercise the contributor-only "My reports" surface + nav gating in mocks/tests.
    id: "u-contributor",
    name: "Priya Nair",
    email: "publicworks-head@linnvalley.example.gov",
    role: "contributor",
    status: "active",
    mfaEnrolled: true,
    lastActiveAt: hoursAgo(4),
    department: "Public Works",
  },
];

/** Ids whose mock "Cognito account" doesn't exist yet — resendStaffInvite provisions instead of
 *  resending for these (mirrors the live provision-if-missing fallback). A successful
 *  provision-fallback removes the id from this set, same as the real Cognito state becoming
 *  provisioned server-side. */
const RESEND_HALF_STATE_IDS = new Set<string>(["u-half-state"]);

const staffName = (id: string | null): string | null =>
  id ? (STAFF.find((s) => s.id === id)?.name ?? "Staff") : null;

const REQUESTS: StaffRequestDetail[] = [
  {
    id: "r-1001",
    trackingCode: "LV-7Q4K-9F2X",
    category: "Tall grass / weeds",
    issueType: "Sightline hazard",
    description:
      "Grass and weeds well over 12 inches at the corner lot on Lakeshore. Blocking the sidewalk sightline.",
    status: "InProgress",
    location: { address: "210 Lakeshore Dr", lat: 38.4991, lng: -94.7043 },
    reporterContact: { name: "Marcy Kwan", email: "marcy@example.com", phone: "913-555-0147" },
    assigneeId: "u-officer",
    property: {
      id: "p-lakeshore-210",
      canonicalAddress: "210 Lakeshore Dr",
      priorConcerns: [
        {
          id: "r-0900",
          trackingCode: "LV-2AAA-11BB",
          category: "Tall grass / weeds",
          status: "Closed",
          createdAt: daysAgo(48),
        },
        {
          id: "r-0950",
          trackingCode: "LV-3CCC-22DD",
          category: "Trash / debris",
          status: "Resolved",
          createdAt: daysAgo(20),
        },
      ],
    },
    attachments: [
      {
        id: "a-1",
        url: IMG(90),
        filename: "grass-corner.jpg",
        contentType: "image/jpeg",
        kind: "resident-photo",
        visibility: "public",
        uploaderName: null,
        createdAt: daysAgo(3),
        isImage: true,
      },
      {
        id: "a-2",
        url: IMG(30),
        filename: "site-visit.jpg",
        contentType: "image/jpeg",
        kind: "site-photo",
        visibility: "public",
        uploaderName: "Dana Reyes",
        createdAt: daysAgo(1),
        isImage: true,
      },
      {
        id: "a-3",
        url: "",
        filename: "notice-of-violation.pdf",
        contentType: "application/pdf",
        kind: "letter-sent",
        visibility: "internal",
        uploaderName: "Dana Reyes",
        note: "First notice mailed.",
        createdAt: daysAgo(1),
        isImage: false,
      },
    ],
    events: [
      {
        id: "e-1",
        type: "created",
        visibility: "public",
        body: "Concern received.",
        actorName: null,
        createdAt: daysAgo(3),
      },
      {
        id: "e-2",
        type: "status_change",
        visibility: "public",
        body: "Marked Acknowledged.",
        toStatus: "Acknowledged",
        actorName: "Dana Reyes",
        createdAt: daysAgo(3),
      },
      {
        id: "e-3",
        type: "assignment",
        visibility: "internal",
        body: "Assigned to Dana Reyes.",
        actorName: "Lister Potter",
        createdAt: daysAgo(2),
      },
      {
        id: "e-4",
        type: "note",
        visibility: "internal",
        body: "Owner is a seasonal resident; may need mailed notice.",
        actorName: "Dana Reyes",
        createdAt: daysAgo(2),
      },
      {
        id: "e-5",
        type: "status_change",
        visibility: "public",
        body: "Someone is working on this now.",
        toStatus: "InProgress",
        actorName: "Dana Reyes",
        createdAt: daysAgo(1),
      },
      {
        id: "e-6",
        type: "notification_sent",
        visibility: "internal",
        body: "Email sent to reporter (status update).",
        actorName: "System",
        createdAt: daysAgo(1),
      },
    ],
    createdAt: daysAgo(3),
    updatedAt: daysAgo(1),
  },
  {
    id: "r-1002",
    trackingCode: "LV-8H2M-4T7P",
    category: "Junk / abandoned vehicle",
    description:
      "Abandoned pickup with flat tires parked on the street for 3+ weeks near the boat ramp.",
    status: "New",
    location: { address: "Empty lot next to the boat ramp", lat: 38.5012, lng: -94.7099 },
    reporterContact: null,
    assigneeId: null,
    property: { id: "p-boatramp", canonicalAddress: "Boat ramp area", priorConcerns: [] },
    attachments: [
      {
        id: "a-4",
        url: IMG(210),
        filename: "truck.jpg",
        contentType: "image/jpeg",
        kind: "resident-photo",
        visibility: "public",
        uploaderName: null,
        createdAt: hoursAgo(5),
        isImage: true,
      },
    ],
    events: [
      {
        id: "e-7",
        type: "created",
        visibility: "public",
        body: "Concern received.",
        actorName: null,
        createdAt: hoursAgo(5),
      },
    ],
    createdAt: hoursAgo(5),
    updatedAt: hoursAgo(5),
  },
  {
    id: "r-1003",
    trackingCode: "LV-5N9J-6R3W",
    category: "Pothole / street / drainage",
    issueType: "Drainage",
    description:
      "Large pothole opening up after the rain on Valley Rd, right before the stop sign.",
    status: "Acknowledged",
    location: { address: "Valley Rd & 2nd St" },
    reporterContact: { email: "resident@example.com" },
    assigneeId: null,
    property: null,
    attachments: [],
    events: [
      {
        id: "e-8",
        type: "created",
        visibility: "public",
        body: "Concern received.",
        actorName: null,
        createdAt: daysAgo(1),
      },
      {
        id: "e-9",
        type: "status_change",
        visibility: "public",
        body: "A staff member has seen this and it's in the queue.",
        toStatus: "Acknowledged",
        actorName: "Lister Potter",
        createdAt: hoursAgo(6),
      },
    ],
    createdAt: daysAgo(1),
    updatedAt: hoursAgo(6),
  },
  {
    id: "r-1004",
    trackingCode: "LV-1B7C-8K2L",
    category: "Trash / debris",
    description: "Construction debris dumped in the ditch along the county line road.",
    status: "Resolved",
    location: { address: "County Line Rd near mile marker 4" },
    reporterContact: { name: "Sam Ortiz", phone: "913-555-0190" },
    assigneeId: "u-officer",
    property: null,
    attachments: [],
    events: [
      {
        id: "e-10",
        type: "created",
        visibility: "public",
        body: "Concern received.",
        actorName: null,
        createdAt: daysAgo(9),
      },
      {
        id: "e-11",
        type: "status_change",
        visibility: "public",
        body: "Someone is working on this now.",
        toStatus: "InProgress",
        actorName: "Dana Reyes",
        createdAt: daysAgo(7),
      },
      {
        id: "e-12",
        type: "status_change",
        visibility: "public",
        body: "Debris cleared and hauled off. Thanks for reporting.",
        toStatus: "Resolved",
        actorName: "Dana Reyes",
        createdAt: daysAgo(5),
      },
    ],
    createdAt: daysAgo(9),
    updatedAt: daysAgo(5),
  },
  // StaffOnly work-item row (work-item-due-dates.md) — an internal task-tracker concern (category
  // "Flag Program", staffOnly: true on the CATEGORIES seed below) exercising the due-date UI:
  // created long ago (would be WAY past the citizen ack/resolve SLA) but carries no reporter PII
  // and is overdue ONLY because of its past dueDate, never because of aging in New.
  {
    id: "r-1005",
    trackingCode: "LV-WI01-FLAG",
    category: "Flag Program",
    description: "Flag at the Main St entrance is faded and needs replacing before Veterans Day.",
    status: "New",
    location: { address: "City Hall — Main St entrance" },
    reporterContact: null,
    assigneeId: "u-officer",
    property: null,
    attachments: [],
    events: [
      {
        id: "e-20",
        type: "created",
        visibility: "internal",
        body: "Work item logged.",
        actorName: "Dana Reyes",
        createdAt: daysAgo(14),
      },
    ],
    createdAt: daysAgo(14),
    updatedAt: daysAgo(14),
    dueDate: daysAgo(3).slice(0, 10),
  },
];

let CATEGORIES: CategoryView[] = [
  {
    id: "c-junk",
    label: "Junk / abandoned vehicle",
    department: "Code Enforcement",
    order: 0,
    active: true,
    defaultAssigneeId: "u-officer",
    kind: "concern",
    showOnMap: true,
  },
  {
    id: "c-grass",
    label: "Tall grass / weeds",
    department: "Code Enforcement",
    order: 1,
    active: true,
    defaultAssigneeId: "u-officer",
    kind: "concern",
    showOnMap: true,
  },
  {
    id: "c-street",
    label: "Pothole / street / drainage",
    department: "Public Works",
    order: 2,
    active: true,
    kind: "concern",
    showOnMap: true,
  },
  {
    id: "c-trash",
    label: "Trash / debris",
    department: "Code Enforcement",
    order: 3,
    active: true,
    kind: "concern",
  },
  {
    id: "c-property",
    label: "Property maintenance",
    department: "Code Enforcement",
    order: 4,
    active: true,
    kind: "concern",
  },
  {
    id: "c-other",
    label: "Something else",
    department: "Clerk",
    order: 5,
    active: true,
    kind: "concern",
  },
  // Staff-only concern category (staff-only-concern-categories) — an internal work-item category
  // hidden from the public embed-config/report form (resident mock source = MOCK_CATEGORIES in
  // api/categories.ts, a SEPARATE array that never includes this row) and rejected by the public
  // submit path server-side, but still selectable from the staff "Log a Concern" form.
  {
    id: "c-flag-program",
    label: "Flag Program",
    department: "Clerk",
    order: 6,
    active: true,
    kind: "concern",
    staffOnly: true,
  },
  // Question topics (ask-a-question-faq.md §2.1) — the SAME CategoryView entity, Kind="question",
  // sharing this array + the listCategories/saveCategories mock path with the concern rows above so
  // CategoriesAdmin's Kind-aware tabs exercise one real admin flow, not a parallel mock store. Ids
  // match MOCK_QUESTION_TOPICS so the seeded QUESTIONS/FAQS mock data (topicLabel() below) resolves
  // the same labels whether read through the topics fallback or this live-shaped path.
  ...MOCK_QUESTION_TOPICS.map((t, i) => ({
    id: t.id,
    label: t.label,
    department: "",
    order: i,
    active: true,
    kind: "question" as const,
  })),
  // Subscription channels (stay-informed-channels.md §2.1) — the SAME CategoryView entity,
  // Kind="channel", sharing this array + listCategories/saveCategories with the rows above so
  // CategoriesAdmin's Channels tab exercises one real admin flow. Ids match MOCK_CHANNELS so the
  // subscribe/preferences mock paths (resolveMockChannels below) resolve the same labels/defaultOn
  // whether read through the channels.ts fallback or this live-shaped path.
  ...MOCK_CHANNELS.map((c, i) => ({
    id: c.id,
    label: c.label,
    department: "",
    order: i,
    active: true,
    kind: "channel" as const,
    defaultOn: c.defaultOn,
  })),
  // KORA record-type taxonomy (records-request.md) — the SAME CategoryView entity, Kind="kora",
  // sharing this array + listCategories/saveCategories with the rows above so CategoriesAdmin's
  // KORA tab exercises one real admin flow. Ids match MOCK_KORA_RECORD_TYPES so the seeded
  // KORA_REQUESTS mock data resolves the same labels whether read through the record-types
  // fallback or this live-shaped path.
  ...MOCK_KORA_RECORD_TYPES.map((t, i) => ({
    id: t.id,
    label: t.label,
    department: "",
    order: i,
    active: true,
    kind: "kora" as const,
  })),
  // Code Enforcement violation-type taxonomy — the SAME CategoryView entity, Kind="case", sharing
  // this array + listCategories/saveCategories with the rows above so CategoriesAdmin's "Violation
  // types" tab exercises one real admin flow. Ids match MOCK_CASE_VIOLATION_TYPES so the seeded
  // CASES mock data resolves the same labels whether read through the fallback or this live-shaped
  // path (♻️ mirrors the KORA record-type block above).
  ...MOCK_CASE_VIOLATION_TYPES.map((t, i) => ({
    id: t.id,
    label: t.label,
    department: "",
    order: i,
    active: true,
    kind: "case" as const,
  })),
  // Permit-type taxonomy (Permits & Licensing, roadmap module 6) — the SAME CategoryView entity,
  // Kind="permit", sharing this array + listCategories/saveCategories with the rows above so
  // CategoriesAdmin's "Permit types" tab exercises one real admin flow. Ids match MOCK_PERMIT_TYPES
  // so the seeded PERMITS mock data resolves the same labels whether read through the fallback or
  // this live-shaped path (♻️ mirrors the KORA/case taxonomy blocks above). permit-skid-shed is
  // seeded with a `0` (free) permitAppFeeOverride so the mock data exercises the 0-vs-null distinction
  // (feature/permit-app-fee-override); every other permit type is undefined (inherits the tenant default).
  ...MOCK_PERMIT_TYPES.map((t, i) => ({
    id: t.id,
    label: t.label,
    department: "",
    order: i,
    active: true,
    kind: "permit" as const,
    permitAppFeeOverride: t.id === "permit-skid-shed" ? 0 : undefined,
    inspectionTypes: t.inspectionTypes,
  })),
  // Trade taxonomy (Contractor Registry, docs/design/contractor-registry/spec.md §2.1) — the SAME
  // CategoryView entity, Kind="trade", sharing this array + listCategories with the rows above so
  // a future trade-taxonomy admin tab (not built in v1 — spec §2.1 explicitly defers it) would
  // exercise the same real admin flow. Ids match MOCK_TRADE_CATEGORIES so the seeded CONTRACTORS
  // mock data resolves the same labels whether read through the fallback or this live-shaped path
  // (♻️ mirrors the KORA/case/permit taxonomy blocks above).
  ...MOCK_TRADE_CATEGORIES.map((t, i) => ({
    id: t.id,
    label: t.label,
    department: "",
    order: i,
    active: true,
    kind: "trade" as const,
  })),
];

let TEMPLATES: NotificationTemplateView[] = [
  {
    trigger: "confirmation",
    emailSubject: "We got your concern — {trackingCode}",
    emailBody:
      "Thanks for reporting a concern to {townName}. Your tracking code is {trackingCode}. Check status anytime at {trackingUrl}.",
    smsBody: "{townName}: got your concern. Code {trackingCode}. Status: {trackingUrl}",
    smsEnabled: false,
  },
  {
    trigger: "status-update",
    emailSubject: "Update on your concern — {trackingCode}",
    emailBody:
      "Your concern ({trackingCode}) is now: {status}. {publicUpdate}\n\nView: {trackingUrl}",
    smsBody: "{townName}: {trackingCode} is now {status}. {trackingUrl}",
    smsEnabled: false,
  },
  {
    trigger: "resolution",
    emailSubject: "Your concern is resolved — {trackingCode}",
    emailBody:
      "Good news — your concern ({trackingCode}) is resolved. {publicUpdate}\n\nDetails: {trackingUrl}",
    smsBody: "{townName}: {trackingCode} resolved. {trackingUrl}",
    smsEnabled: false,
  },
];

const delay = <T>(value: T, ms = 250): Promise<T> =>
  new Promise((r) => setTimeout(() => r(value), ms));

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

// ---- Staff-initiated intake (create) ---------------------------------------

export type IntakeChannel = "phone" | "walk_in" | "letter" | "email" | "other";

export interface CreateRequestInput {
  category: string;
  description: string;
  location: { address: string; lat?: number; lng?: number };
  attachments?: string[];
  reporterContact?: { name?: string; email?: string; phone?: string };
  intakeChannel: IntakeChannel;
  intakeNote?: string;
}

export interface CreateRequestResult {
  trackingCode: string;
  requestId: string;
  status: RequestStatus;
}

const CHANNEL_LABEL: Record<IntakeChannel, string> = {
  phone: "phone",
  walk_in: "walk-in",
  letter: "letter",
  email: "email",
  other: "another channel",
};

/** Display tracking code, e.g. LV-7Q4K-9F2X (no ambiguous chars) — mirrors the server format. */
function makeTrackingCode(): string {
  const alphabet = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
  const block = (n: number) =>
    Array.from({ length: n }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join("");
  return `LV-${block(4)}-${block(4)}`;
}

/**
 * Mock POST /api/requests (staff-initiated intake). Seeds a real request into the store so the
 * success-state deep link to /staff/reports/:id resolves. Records the intake channel + note as the
 * opening audit event, and (when a reporter contact was captured) a confirmation-email event.
 */
export function createRequest(input: CreateRequestInput): Promise<CreateRequestResult> {
  const createdAt = new Date().toISOString();
  const id = `r-${Date.now()}`;
  const trackingCode = makeTrackingCode();
  const contact = input.reporterContact;
  const hasContact = Boolean(contact && (contact.name || contact.email || contact.phone));
  const attachments: AttachmentView[] = (input.attachments ?? []).map((s3Key, i) => ({
    id: `a-${Date.now()}-${i}`,
    url: "",
    filename: s3Key.split("/").pop() ?? "attachment",
    contentType: "",
    kind: "site-photo",
    visibility: "public",
    uploaderName: CURRENT_USER.name,
    createdAt,
    isImage: true,
  }));
  const events: StaffRequestDetail["events"] = [
    {
      id: `e-${Date.now()}`,
      type: "created",
      visibility: "internal",
      body: `Logged by ${CURRENT_USER.name ?? "staff"} via ${CHANNEL_LABEL[input.intakeChannel]}.`,
      actorName: CURRENT_USER.name,
      createdAt,
    },
  ];
  if (input.intakeNote?.trim()) {
    events.push({
      id: `e-${Date.now()}-note`,
      type: "note",
      visibility: "internal",
      body: input.intakeNote.trim(),
      actorName: CURRENT_USER.name,
      createdAt,
    });
  }
  if (hasContact) {
    events.push({
      id: `e-${Date.now()}-conf`,
      type: "notification_sent",
      visibility: "internal",
      body: "Confirmation email sent to reporter.",
      actorName: "System",
      createdAt,
    });
  }
  const req: StaffRequestDetail = {
    id,
    trackingCode,
    category: input.category,
    description: input.description,
    status: "New",
    location: input.location,
    reporterContact: hasContact ? { ...contact } : null,
    assigneeId: null,
    attachments,
    events,
    property: null,
    createdAt,
    updatedAt: createdAt,
  };
  REQUESTS.unshift(req);
  return delay({ trackingCode, requestId: id, status: "New" as RequestStatus });
}

// ---- Queue + detail --------------------------------------------------------

/**
 * `q` mirrors the live server's search (shared.MatchesSearch): a case-insensitive substring match
 * against description/address/tracking code/category — reporter name/email/phone are NEVER
 * matched, matching the live allow-list. Filters the FULL store, same as the real endpoint.
 */
export function listQueue(q?: string): Promise<QueueRow[]> {
  const nowDate = new Date();
  const needle = (q ?? "").trim().toLowerCase();
  const source = needle
    ? REQUESTS.filter(
        (r) =>
          r.description.toLowerCase().includes(needle) ||
          r.location.address.toLowerCase().includes(needle) ||
          r.trackingCode.toLowerCase().includes(needle) ||
          r.category.toLowerCase().includes(needle),
      )
    : REQUESTS;
  const rows: QueueRow[] = source.map((r) => {
    // Category is stored as its label on the request (see CATEGORIES seed); resolve the matching
    // category row (if any) for its optional SLA override, same as the live EffectiveSLA lookup.
    const cat = CATEGORIES.find((c) => c.label === r.category);
    // StaffOnly category (work-item-due-dates.md) — an internal work-item row NEVER uses the
    // citizen ack/resolve SLA; overdue is driven by the staff-set dueDate instead
    // (evaluateWorkItemDue, mirroring the server's EvaluateWorkItemDue). Every other (non-StaffOnly)
    // category is COMPLETELY UNCHANGED — evaluateSLA exactly as before.
    const sla = cat?.staffOnly
      ? evaluateWorkItemDue(r.status, r.dueDate, nowDate)
      : evaluateSLA(
          r.status,
          r.createdAt,
          effectiveSLA(
            { ackDays: SETTINGS.slaAckDays, resolveDays: SETTINGS.slaResolveDays },
            cat ? { ackDays: cat.slaAckDays, resolveDays: cat.slaResolveDays } : undefined,
          ),
          nowDate,
        );
    return {
      id: r.id,
      trackingCode: r.trackingCode,
      category: r.category,
      issueType: r.issueType,
      description: r.description,
      status: r.status,
      address: r.location.address,
      attachmentCount: r.attachments.length,
      reporterName: r.reporterContact?.name ?? r.reporterContact?.email ?? null,
      assigneeId: r.assigneeId,
      createdAt: r.createdAt,
      updatedAt: r.updatedAt,
      ageBusinessDays: sla.ageBusinessDays,
      overdue: sla.overdue,
      slaStage: sla.slaStage,
      targetDays: sla.targetDays,
      dueDate: r.dueDate,
    };
  });
  return delay(clone(rows));
}

export function getRequest(id: string): Promise<StaffRequestDetail | null> {
  const found = REQUESTS.find((r) => r.id === id) ?? null;
  if (!found) return delay(null);
  // Mock parity with the live categoryLabel field (handler/staff.go GetRequest,
  // shared.ResolveCategoryLabel) — REQUESTS already stores `category` as its friendly label.
  return delay(clone({ ...found, categoryLabel: found.category }));
}

// ---- Saved views (queue power-tools) ---------------------------------------
//
// PRIVATE per staff user, scoped to CURRENT_USER — mirrors the live server's per-Cognito-sub
// scoping so dev-with-mocks exercises the same "never see another staffer's views" contract.

let SAVED_VIEWS: SavedView[] = [
  {
    id: "sv-unassigned",
    name: "Unassigned, newest first",
    filters: { assigneeId: "unassigned" },
    createdAt: daysAgo(9),
  },
];

export function listSavedViews(): Promise<SavedView[]> {
  return delay(clone(SAVED_VIEWS));
}

export function saveSavedView(name: string, filters: SavedViewFilters): Promise<SavedView> {
  const view: SavedView = {
    id: `sv-${Date.now()}`,
    name,
    filters,
    createdAt: new Date().toISOString(),
  };
  SAVED_VIEWS = [...SAVED_VIEWS, view];
  return delay(clone(view));
}

export function deleteSavedView(id: string): Promise<void> {
  SAVED_VIEWS = SAVED_VIEWS.filter((v) => v.id !== id);
  return delay(undefined);
}

// ---- Bulk queue actions (queue power-tools) --------------------------------
//
// Applies the SAME per-item logic as the single-item mutations above (changeStatus/assignRequest),
// reporting a per-id ok/failed result so a partial failure is always visible — mirrors the live
// bulk endpoints' contract.

export function bulkAssign(
  requestIds: string[],
  assigneeId: string | null,
  actorId: string,
): Promise<BulkItemResult[]> {
  const results = requestIds.map((rid): BulkItemResult => {
    const req = REQUESTS.find((r) => r.id === rid);
    if (!req) return { requestId: rid, ok: false, reason: "not_found" };
    req.assigneeId = assigneeId;
    req.updatedAt = new Date().toISOString();
    req.events.push({
      id: `e-${Date.now()}-${rid}`,
      type: "assignment",
      visibility: "internal",
      body: assigneeId ? `Assigned to ${staffName(assigneeId)}.` : "Assignment cleared.",
      actorName: staffName(actorId),
      createdAt: req.updatedAt,
    });
    return { requestId: rid, ok: true };
  });
  return delay(results);
}

export function bulkChangeStatus(
  requestIds: string[],
  status: RequestStatus,
  actorId: string,
  publicUpdate?: string,
): Promise<BulkItemResult[]> {
  const results = requestIds.map((rid): BulkItemResult => {
    const req = REQUESTS.find((r) => r.id === rid);
    if (!req) return { requestId: rid, ok: false, reason: "not_found" };
    req.status = status;
    req.updatedAt = new Date().toISOString();
    req.events.push({
      id: `e-${Date.now()}-${rid}`,
      type: "status_change",
      visibility: publicUpdate ? "public" : "internal",
      body: publicUpdate || `Status changed to ${status}.`,
      toStatus: status,
      actorName: staffName(actorId),
      createdAt: req.updatedAt,
    });
    return { requestId: rid, ok: true };
  });
  return delay(results);
}

export function assigneeName(id: string | null): string | null {
  return staffName(id);
}

export function changeStatus(
  id: string,
  toStatus: RequestStatus,
  opts: { publicUpdate?: string; reason?: string; actorId: string },
): Promise<StaffRequestDetail | null> {
  const req = REQUESTS.find((r) => r.id === id);
  if (!req) return delay(null);
  req.status = toStatus;
  req.updatedAt = new Date().toISOString();
  const actor = staffName(opts.actorId);
  req.events.push({
    id: `e-${Date.now()}`,
    type: "status_change",
    visibility: opts.publicUpdate ? "public" : "internal",
    body: opts.publicUpdate || opts.reason || `Status changed to ${toStatus}.`,
    toStatus,
    actorName: actor,
    createdAt: req.updatedAt,
  });
  if (opts.publicUpdate && req.reporterContact) {
    req.events.push({
      id: `e-${Date.now()}-n`,
      type: "notification_sent",
      visibility: "internal",
      body: "Email sent to reporter (status update).",
      actorName: "System",
      createdAt: req.updatedAt,
    });
  }
  return delay(clone(req));
}

/**
 * `dueDate` (work-item-due-dates.md) mirrors the live Assign endpoint contract: `undefined` leaves
 * any existing due date untouched (an assigneeId-only call never wipes it), `""` clears it,
 * "YYYY-MM-DD" sets it. Audited as its own internal event, same shape as the assignment event above.
 */
export function assignRequest(
  id: string,
  assigneeId: string | null,
  actorId: string,
  dueDate?: string,
): Promise<StaffRequestDetail | null> {
  const req = REQUESTS.find((r) => r.id === id);
  if (!req) return delay(null);
  req.assigneeId = assigneeId;
  req.updatedAt = new Date().toISOString();
  req.events.push({
    id: `e-${Date.now()}`,
    type: "assignment",
    visibility: "internal",
    body: assigneeId ? `Assigned to ${staffName(assigneeId)}.` : "Assignment cleared.",
    actorName: staffName(actorId),
    createdAt: req.updatedAt,
  });
  if (dueDate !== undefined) {
    req.dueDate = dueDate || undefined;
    req.events.push({
      id: `e-${Date.now()}-dd`,
      type: "assignment",
      visibility: "internal",
      body: dueDate ? `Due date set to ${dueDate}.` : "Due date cleared.",
      actorName: staffName(actorId),
      createdAt: req.updatedAt,
    });
  }
  return delay(clone(req));
}

export function addNote(
  id: string,
  body: string,
  visibility: "public" | "internal",
  actorId: string,
): Promise<StaffRequestDetail | null> {
  const req = REQUESTS.find((r) => r.id === id);
  if (!req) return delay(null);
  req.updatedAt = new Date().toISOString();
  req.events.push({
    id: `e-${Date.now()}`,
    type: "note",
    visibility,
    body,
    actorName: staffName(actorId),
    createdAt: req.updatedAt,
  });
  if (visibility === "public" && req.reporterContact) {
    req.events.push({
      id: `e-${Date.now()}-n`,
      type: "notification_sent",
      visibility: "internal",
      body: "Email sent to reporter (public update).",
      actorName: "System",
      createdAt: req.updatedAt,
    });
  }
  return delay(clone(req));
}

export function addStaffAttachment(
  id: string,
  att: Omit<AttachmentView, "id" | "createdAt">,
  actorId: string,
): Promise<StaffRequestDetail | null> {
  const req = REQUESTS.find((r) => r.id === id);
  if (!req) return delay(null);
  const createdAt = new Date().toISOString();
  req.attachments.push({ ...att, id: `a-${Date.now()}`, createdAt });
  req.updatedAt = createdAt;
  req.events.push({
    id: `e-${Date.now()}`,
    type: "attachment",
    visibility: att.visibility,
    body: `Attached ${att.filename} (${att.kind}).`,
    actorName: staffName(actorId),
    createdAt,
  });
  return delay(clone(req));
}

// ---- Public tracking projection (NFR-3 allow-list) -------------------------

/** Request ids that already have a satisfaction survey on file (mock-only; mirrors the backend's
 *  one-survey-per-request item). Session-scoped, like the rest of this in-memory store. */
const SURVEYED = new Set<string>();

export function getPublicTracking(code: string): Promise<PublicTracking | null> {
  const req = REQUESTS.find((r) => r.trackingCode.toUpperCase() === code.toUpperCase());
  if (req) {
    const publicAttachments = req.attachments.filter((a) => a.visibility === "public");
    const view: PublicTracking = {
      trackingCode: req.trackingCode,
      kind: "concern",
      status: req.status,
      category: req.category,
      description: req.description,
      address: req.location.address,
      hasContact: Boolean(req.reporterContact),
      createdAt: req.createdAt,
      updatedAt: req.updatedAt,
      updates: req.events
        .filter((e) => e.visibility === "public")
        .map((e) => ({
          id: e.id,
          body: e.body,
          toStatus: e.toStatus as RequestStatus | undefined,
          createdAt: e.createdAt,
        })),
      publicAttachments,
      ownSubmission: {
        description: req.description,
        photos: publicAttachments.filter((a) => a.kind === "resident-photo"),
      },
      surveySubmitted: SURVEYED.has(req.id),
    };
    return delay(clone(view));
  }
  // KORA (records-request.md) — a KORA tracking page must render as "kora", not silently fall
  // back to "concern" (a kind the resident never submitted).
  const kora = KORA_REQUESTS.find((r) => r.trackingCode.toUpperCase() === code.toUpperCase());
  if (kora) {
    const view: PublicTracking = {
      trackingCode: kora.trackingCode,
      kind: "kora",
      status: kora.status,
      category: kora.recordType ? recordTypeLabel(kora.recordType) : "Open Records Request",
      description: kora.recordsSought,
      address: "",
      hasContact: Boolean(kora.reporterContact),
      createdAt: kora.createdAt,
      updatedAt: kora.updatedAt,
      updates: kora.events
        .filter((e) => e.type !== "note")
        .map((e) => ({ id: e.id, body: e.body, toKORAStatus: e.toStatus, createdAt: e.createdAt })),
      publicAttachments: [],
      ownSubmission: { description: kora.recordsSought, photos: [] },
    };
    return delay(clone(view));
  }
  // Permit (Permits & Licensing, roadmap module 6) — a permit tracking page must render as
  // "permit", not silently fall back to "concern" (a kind the resident never submitted).
  const permit = PERMITS.find((r) => r.trackingCode.toUpperCase() === code.toUpperCase());
  if (permit) {
    const publicAttachments = permit.attachments.filter((a) => a.visibility === "public");
    const view: PublicTracking = {
      trackingCode: permit.trackingCode,
      kind: "permit",
      status: permit.status,
      category: permitTypeLabel(permit.permitType),
      description: permit.projectDescription,
      address: permit.location.address,
      hasContact: Boolean(permit.reporterContact),
      createdAt: permit.createdAt,
      updatedAt: permit.updatedAt,
      updates: permit.events
        .filter((e) => e.visibility === "public" && e.type !== "note")
        .map((e) => ({
          id: e.id,
          body: e.body,
          toPermitStatus: e.toStatus,
          createdAt: e.createdAt,
        })),
      publicAttachments,
      ownSubmission: { description: permit.projectDescription, photos: publicAttachments },
    };
    return delay(clone(view));
  }
  return delay(null);
}

/**
 * Resident reply/comment (POST .../track/{code}/comments mock). Appends a PUBLIC event tagged
 * type="resident_comment" (mirrors the live AuditEvent — Type=resident_comment, Source=resident,
 * ActorID nil) so the staff timeline can label it "Resident" instead of rendering a blank actor.
 * Rejects an unknown code the same shape the live 404 does, so callers share one error path.
 */
export function addResidentComment(
  code: string,
  body: string,
): Promise<{ id: string; body: string; createdAt: string }> {
  const req = REQUESTS.find((r) => r.trackingCode.toUpperCase() === code.toUpperCase());
  if (!req) return Promise.reject(new ApiError("We couldn't find that.", 404, "not_found", false));
  const createdAt = new Date().toISOString();
  const id = `e-${Date.now()}`;
  req.events.push({
    id,
    type: "resident_comment",
    visibility: "public",
    body,
    actorName: "Resident",
    createdAt,
  });
  req.updatedAt = createdAt;
  return delay({ id, body, createdAt });
}

/**
 * Satisfaction survey (POST .../track/{code}/satisfaction mock). Only accepted once the request is
 * Resolved (mirrors the live handler); one survey per request, last-write-wins.
 */
export function submitSatisfactionSurvey(
  code: string,
  _rating: number,
  _comment?: string,
): Promise<void> {
  const req = REQUESTS.find((r) => r.trackingCode.toUpperCase() === code.toUpperCase());
  if (!req) return Promise.reject(new ApiError("We couldn't find that.", 404, "not_found", false));
  if (req.status !== "Resolved") {
    return Promise.reject(
      new ApiError("This concern isn't resolved yet.", 409, "invalid_transition", false),
    );
  }
  SURVEYED.add(req.id);
  return delay(undefined);
}

// ---- Admin: categories / staff / templates ---------------------------------

export function listStaff(): Promise<StaffUserView[]> {
  return delay(clone(STAFF));
}
/** `kind` filters to just one taxonomy (CategoriesAdmin's concern/question/channel tabs, the
 *  question console's topic picker) — omit for the full mixed list (matches the live GET, which
 *  never filters server-side either; §2.1 "the SAME entity type, filtered by Kind"). */
export function listCategories(kind?: CategoryKind): Promise<CategoryView[]> {
  const all = [...CATEGORIES].sort((a, b) => a.order - b.order);
  const filtered = kind ? all.filter((c) => (c.kind ?? "concern") === kind) : all;
  return delay(clone(filtered));
}
/** Kind=question categories only (staff console topic taxonomy, ♻️ mirrors listCategoriesLive's
 *  question filter). */
export function listQuestionTopics(): Promise<CategoryView[]> {
  return listCategories("question");
}
/**
 * Persists `next` by MERGING into CATEGORIES rather than replacing the whole array: every row in
 * `next` is upserted by id, and any EXISTING row whose id isn't in `next` is left untouched. This
 * matters because CategoriesAdmin's Kind tabs each save only their own subset — a concern-tab save
 * must never wipe out the question topics (and vice versa), same invariant the live per-row POST
 * naturally has (each save touches only the rows it sends).
 */
export function saveCategories(next: CategoryView[]): Promise<CategoryView[]> {
  const nextIds = new Set(next.map((c) => c.id));
  const untouched = CATEGORIES.filter((c) => !nextIds.has(c.id));
  CATEGORIES = clone([...untouched, ...next]);
  const kind = next[0]?.kind ?? "concern";
  return listCategories(kind);
}
export function listTemplates(): Promise<NotificationTemplateView[]> {
  return delay(clone(TEMPLATES));
}
export function saveTemplate(t: NotificationTemplateView): Promise<NotificationTemplateView> {
  TEMPLATES = TEMPLATES.map((x) => (x.trigger === t.trigger ? clone(t) : x));
  return delay(clone(t));
}

// ---- Branding / settings (TenantSettings) ----------------------------------

export interface BrandingSettingsView {
  townName: string;
  themeColor: string | null;
  backgroundColor: string | null;
  logo: string | null;
  /** `{display, body}` — either may be empty string. A legacy plain string is also accepted and
   *  applies to both pickers (#172). */
  fonts: { display?: string; body?: string } | string | null;
  townMailingAddress: string | null;
  townPhone: string | null;
  townEmail: string | null;
  slaAckDays: number;
  slaResolveDays: number;
  /** Council Agenda + Packet public-display toggles (meetings-public-relaunch.md §3) — default
   *  true, matching the live server's resolved-default behavior. */
  publicShowAgendaItemDetails: boolean;
  publicShowAttachments: boolean;
  /** Recap/minutes independent-publish feature — whether unadopted draft minutes show publicly
   *  (clearly labeled). Default true, matching the live server's resolved-default behavior. */
  publicShowDraftMinutes: boolean;
  /** Permit → POA daily digest toggle (permit-poa-digest.md) — optional (defaults false when
   *  absent), mirrors staffApi's BrandingSettingsView so existing test literals need no update. */
  permitDigestNotifyOnSubmit?: boolean;
}

/** In-memory TenantSettings so branding round-trips in mocks/dev (persists for the session). */
let SETTINGS: BrandingSettingsView = {
  townName: "City of Linn Valley",
  themeColor: "#1D4E6F",
  backgroundColor: "#EEF4F8",
  logo: null,
  fonts: null,
  townMailingAddress: null,
  townPhone: null,
  townEmail: null,
  slaAckDays: DEFAULT_SLA_ACK_DAYS,
  slaResolveDays: DEFAULT_SLA_RESOLVE_DAYS,
  publicShowAgendaItemDetails: true,
  publicShowAttachments: true,
  publicShowDraftMinutes: true,
  permitDigestNotifyOnSubmit: false,
};

export function getBranding(): Promise<BrandingSettingsView> {
  return delay(clone(SETTINGS));
}

export function saveBranding(next: BrandingSettingsView): Promise<BrandingSettingsView> {
  SETTINGS = clone(next);
  return delay(clone(SETTINGS));
}

/**
 * Mock of the S3-hosted logo upload (presign → PUT → publicPath). No real bucket in dev-with-mocks,
 * so we just mint a local object URL and hand it back as the "path" — it's a real URL the <img> can
 * render, and it exercises the SAME request → PUT-blob → set-path flow the live client walks.
 */
export function uploadLogo(blob: Blob, _extension: string): Promise<string> {
  const url = URL.createObjectURL(blob);
  return delay(url, 150);
}

/**
 * Mock of the S3-hosted document upload (presign → PUT → publicPath) for the RichTextEditor's
 * "Insert document" affordance. No real bucket in dev-with-mocks, so we mint a deterministic
 * `/docs/linnvalley/<sanitized-filename>` path — a plausible, stable public path (not an object URL,
 * since the inserted link's href is what tests/UI actually assert on, not a renderable resource).
 */
export function uploadDocument(file: File, onProgress?: (pct: number) => void): Promise<string> {
  const sanitized = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
  onProgress?.(100);
  return delay(`/docs/linnvalley/${sanitized}`, 150);
}

// ---- Staff mutations (invite / activate) -----------------------------------

export function inviteStaff(
  email: string,
  role: "admin" | "staff" | "contributor",
  name = "",
  department?: string,
): Promise<InviteStaffResult> {
  // Mirrors the live idempotency contract (admin_staff_provision.go): re-inviting an email that
  // already has a directory row is "already_provisioned", not a fresh invite.
  const existing = STAFF.find((s) => s.email?.toLowerCase() === email.toLowerCase());
  if (existing) {
    return delay({ staff: clone(existing), outcome: "already_provisioned" as const });
  }
  const user: StaffUserView = {
    id: `u-${Date.now()}`,
    name: name || null,
    email,
    role,
    status: "invited",
    mfaEnrolled: false,
    department: role === "contributor" ? department : undefined,
  };
  STAFF.push(user);
  return delay({ staff: clone(user), outcome: "invited" as const });
}

/** Mock of POST /admin/staff resend:true — PROVISION-IF-MISSING (mirrors the live fallback,
 *  admin.go resend branch): a row in RESEND_HALF_STATE_IDS has no mock "Cognito account" yet, so
 *  resend provisions it instead (outcome "invited") and clears the half-state marker; any other
 *  "invited" row gets a plain resend (outcome "resent"). Still rejects for a non-pending (active/
 *  deactivated) staffer, mirroring the server's real posture (nothing to resend/provision for an
 *  already-active or deactivated account). */
export function resendStaffInvite(user: StaffUserView): Promise<ResendOutcome> {
  const u = STAFF.find((x) => x.id === user.id);
  if (!u || u.status !== "invited") {
    return Promise.reject(
      new ApiError("This account hasn't been invited yet.", 409, "not_provisioned"),
    );
  }
  if (RESEND_HALF_STATE_IDS.has(u.id)) {
    RESEND_HALF_STATE_IDS.delete(u.id);
    return delay<ResendOutcome>("invited");
  }
  return delay<ResendOutcome>("resent");
}

export function setStaffActive(id: string, active: boolean): Promise<StaffUserView> {
  const u = STAFF.find((x) => x.id === id);
  if (!u) return Promise.reject(new Error("not found"));
  u.status = active ? "active" : "deactivated";
  return delay(clone(u));
}

export function updateStaff(
  id: string,
  changes: { name?: string; role?: "admin" | "staff" | "contributor"; department?: string },
): Promise<StaffUserView> {
  const u = STAFF.find((x) => x.id === id);
  if (!u) return Promise.reject(new Error("not found"));
  if (changes.name !== undefined) u.name = changes.name.trim() || null;
  if (changes.role !== undefined) u.role = changes.role;
  if (u.role === "contributor") {
    u.department = (changes.department ?? u.department) || undefined;
  } else {
    u.department = undefined;
  }
  return delay(clone(u));
}

export const CURRENT_USER = STAFF[0]!;
/** The seeded RoleContributor mock user (agenda-packet.md §3.2) — StaffAuth's mock login swaps to
 *  this session user for a "contributor@…" login so the contributor-only nav + "My reports" surface
 *  are exercisable offline. */
export const CONTRIBUTOR_USER = STAFF.find((s) => s.role === "contributor")!;

// ---- Branding crawler (mock) -----------------------------------------------

/** A tiny detected "logo" — a solid civic-blue square as an SVG data URL. */
// A raster (PNG) logo — SVG logos are no longer accepted (stored-XSS hardening), and
// applyCrawledLogo runs this through processLogoFile, which rejects non-raster types.
const MOCK_CRAWL_LOGO =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==";

/**
 * Mock of POST /api/admin/branding/crawl. Returns plausible detected branding so the review-strip
 * UI is exercisable offline, and reproduces the two live error codes for URLs that would be blocked
 * or unreachable (so the error paths are testable without a backend).
 */
export function crawlBranding(url: string): Promise<BrandingCrawlResult> {
  const trimmed = url.trim();
  if (!trimmed) {
    return Promise.reject(
      new ApiError("Enter your town's website URL.", 400, "url_required", false),
    );
  }
  // Simulate the SSRF guard for obviously-private targets.
  if (/localhost|127\.0\.0\.1|192\.168\.|10\.|::1|\.local(\b|\/)/i.test(trimmed)) {
    return Promise.reject(new ApiError("That URL can't be fetched.", 400, "blocked_url", false));
  }
  // Simulate an unreachable host.
  if (/nofetch|unreachable|example\.invalid/i.test(trimmed)) {
    return Promise.reject(new ApiError("Couldn't reach that URL.", 400, "fetch_failed", false));
  }
  const result: BrandingCrawlResult = {
    themeColor: "#1f5b8f",
    backgroundColor: "#f4f7fa",
    fonts: "Georgia, serif",
    logo: MOCK_CRAWL_LOGO,
    logoSourceUrl: trimmed.replace(/\/?$/, "/") + "logo.png",
    sourceUrl: trimmed,
    notes: [
      "themeColor from <meta name=theme-color>",
      "backgroundColor from <body> computed style",
      "logo from <link rel=icon>",
    ],
  };
  return delay(clone(result), 400);
}

// ---- Notify: subscribers + broadcasts (ADR-0012) ---------------------------
//
// In-memory Person/Subscription facets + Broadcasts so the whole Notify flow (subscribe → confirm
// → unsubscribe, staff compose → send) runs end to end without Inky's HTTP handler (store-layer
// only so far). Tokens are plain random strings here (real tokens are high-entropy server-side).

interface MockSubscriber {
  personId: string;
  /** Optional — mail-only subscribers (postal-updates.md) have no email at all. */
  email?: string;
  name?: string;
  status: "pending" | "confirmed" | "unsubscribed" | "bounced" | "complained";
  confirmToken: string;
  unsubToken: string;
  createdAt: string;
  /** Channel ids opted into (stay-informed-channels.md §2.2). Empty/undefined = "subscribed to
   *  everything" — mirrors the backend's Subscription.Channels backward-compat rule; never
   *  expanded to the full channel-id list here. */
  channels?: string[];
  /** "" (legacy)/"email"/"mail"/"both" — postal-updates.md; empty reads as "email". */
  deliveryMethod?: DeliveryMethod;
  mailingAddress?: MailingAddress;
}

const SUBSCRIBERS: MockSubscriber[] = [
  // A handful of already-confirmed subscribers so the Broadcast console's audience count and
  // sent-history results read as real (spec §4.1/§4.2 "312 people" pattern, scaled down for mock).
  // Legacy (empty `channels`) — pre-existing subscribers keep getting everything until they set
  // preferences (§2.2 backward-compat rule).
  ...Array.from({ length: 6 }, (_, i) => ({
    personId: `p-${i}`,
    email: `resident${i}@example.com`,
    status: "confirmed" as const,
    confirmToken: `tok-c-${i}`,
    unsubToken: `tok-u-${i}`,
    createdAt: daysAgo(30 - i),
  })),
];

function mockToken(): string {
  return cryptoRandomToken();
}
function cryptoRandomToken(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
}

function confirmedCount(): number {
  return SUBSCRIBERS.filter((s) => s.status === "confirmed").length;
}

/**
 * Mock POST /public/notify/subscribe. Always resolves success-shaped (spec §1.6 anti-enumeration):
 * a brand-new email is created pending; an existing pending/confirmed email is treated identically
 * from the caller's point of view (status reflects the existing record so the UI can pick the
 * "check your email" vs. "already subscribed" copy — never an error).
 */
/** Mirrors resolveSubscribeChannels/filterValidChannelIDs (notify_subscribe.go): validate against
 *  the ACTIVE mock channel list, drop unknown ids, never error. Empty/all-invalid → undefined
 *  (the "everything" sentinel — never expanded to the full active-id list). */
function resolveMockChannels(requested?: string[]): string[] | undefined {
  if (!requested || requested.length === 0) return undefined;
  const validIds = new Set(MOCK_CHANNELS.map((c) => c.id));
  const out = [...new Set(requested.filter((id) => validIds.has(id)))];
  return out.length ? out : undefined;
}

/** Mail-only (no email) is single-opt-in — confirmed immediately, no confirmation email
 *  (postal-updates.md "Mail-only signup (no email)"). Email/both keep double-opt-in unchanged. */
export function subscribe(
  email?: string,
  name?: string,
  channels?: string[],
  deliveryMethod?: DeliveryMethod,
  mailingAddress?: MailingAddress,
): Promise<SubscribeResult> {
  const method: DeliveryMethod = deliveryMethod || "email";
  const trimmed = email?.trim().toLowerCase() || undefined;
  const resolvedChannels = resolveMockChannels(channels);
  const existing = trimmed
    ? SUBSCRIBERS.find((s) => s.email?.toLowerCase() === trimmed)
    : undefined;
  if (existing) {
    if (
      existing.status === "unsubscribed" ||
      existing.status === "bounced" ||
      existing.status === "complained"
    ) {
      // Re-subscribing after opting out re-starts double opt-in. Channels are re-captured from
      // THIS submission, not carried over from before (mirrors the backend re-opt-in branch).
      existing.status = "pending";
      existing.confirmToken = mockToken();
      existing.channels = resolvedChannels;
    }
    existing.deliveryMethod = method;
    if (mailingAddress) existing.mailingAddress = mailingAddress;
    return delay({ status: existing.status === "confirmed" ? "confirmed" : "pending" }, 500);
  }
  const isMailOnly = method === "mail" && !trimmed;
  const sub: MockSubscriber = {
    personId: `p-${Date.now()}`,
    email: trimmed,
    name: name?.trim() || undefined,
    status: isMailOnly ? "confirmed" : "pending",
    confirmToken: mockToken(),
    unsubToken: mockToken(),
    createdAt: new Date().toISOString(),
    channels: resolvedChannels,
    deliveryMethod: method,
    mailingAddress,
  };
  SUBSCRIBERS.push(sub);
  return delay({ status: isMailOnly ? "confirmed" : "pending" }, 500);
}

/** Mock POST /public/notify/subscribe/resend — no-op in mocks beyond a delay (rate-limit UI is
 *  exercised client-side; the mock never actually rejects). */
export function resendConfirmation(_email: string): Promise<void> {
  return delay(undefined, 400);
}

/** Mock GET /public/notify/confirm?token=. Same anti-enumeration wording rule as live (spec §2.2):
 *  an unrecognized token reads identically to an expired one. */
export function confirmSubscription(token: string): Promise<NotifyLandingResult> {
  const sub = SUBSCRIBERS.find((s) => s.confirmToken === token);
  if (!sub) return delay({ state: "expired" }, 300);
  if (sub.status === "confirmed") return delay({ state: "already" }, 300);
  if (sub.status === "unsubscribed") return delay({ state: "unsubscribed-since" }, 300);
  sub.status = "confirmed";
  return delay({ state: "success" }, 300);
}

/** Mock GET /public/notify/unsubscribe?token=. Executes on load (one-click, spec §3.1/§3.2). */
export function unsubscribe(token: string): Promise<NotifyLandingResult> {
  const sub = SUBSCRIBERS.find((s) => s.unsubToken === token);
  if (!sub) return delay({ state: "expired" }, 300);
  if (sub.status === "unsubscribed") return delay({ state: "already" }, 300);
  sub.status = "unsubscribed";
  return delay({ state: "success" }, 300);
}

/** Mock GET /public/preferences?token=. Same token as unsubscribe (the permanent capability URL,
 *  stay-informed-channels.md §3.2) — an unrecognized token reads as "expired" (anti-enumeration),
 *  an already-unsubscribed one reads as "unsubscribed-since" (mirrors Confirm's dead-link state). */
export function getPreferences(token: string): Promise<PreferencesResult> {
  const sub = SUBSCRIBERS.find((s) => s.unsubToken === token);
  if (!sub) return delay({ state: "expired" }, 300);
  if (sub.status === "unsubscribed") return delay({ state: "unsubscribed-since" }, 300);
  return delay(
    {
      state: "success",
      channels: MOCK_CHANNELS,
      selected: sub.channels ?? [],
      allChannels: !sub.channels || sub.channels.length === 0,
      deliveryMethod: sub.deliveryMethod || "email",
      mailingAddress: sub.mailingAddress ?? null,
    },
    300,
  );
}

/** Mock PUT /public/preferences?token=. An empty `channels` array (or one that resolves to zero
 *  valid ids) routes to a REAL unsubscribe — never silently stored as "everything" (§3.2
 *  "uncheck-all → unsubscribe"), mirroring the backend's Preferences PUT branch. `deliveryMethod`/
 *  `mailingAddress` are optional — omitted means no change (postal-updates.md). */
export function updatePreferences(
  token: string,
  channels: string[],
  deliveryMethod?: DeliveryMethod,
  mailingAddress?: MailingAddress,
): Promise<PreferencesUpdateResult> {
  const sub = SUBSCRIBERS.find((s) => s.unsubToken === token);
  if (!sub) return delay({ state: "expired" }, 300);
  if (sub.status === "unsubscribed") return delay({ state: "unsubscribed-since" }, 300);
  if (deliveryMethod) sub.deliveryMethod = deliveryMethod;
  if (mailingAddress) sub.mailingAddress = mailingAddress;
  const valid = resolveMockChannels(channels);
  if (!valid) {
    sub.status = "unsubscribed";
    return delay({ state: "unsubscribed" }, 300);
  }
  sub.channels = valid;
  return delay(
    {
      state: "success",
      selected: valid,
      deliveryMethod: sub.deliveryMethod || "email",
      mailingAddress: sub.mailingAddress ?? null,
    },
    300,
  );
}

/** Mock GET /api/admin/subscribers/mailing-list.csv?channel=<id?> — builds a real CSV Blob client-
 *  side from the in-memory SUBSCRIBERS so the staff export button is exercised end to end in mock
 *  mode. Same row filter as the live handler: confirmed + deliveryMethod ∈ {mail, both} + has a
 *  mailingAddress + (no channel filter OR subscribed to it). */
export function exportMailingListCsv(channel?: string): Promise<Blob> {
  const rows = SUBSCRIBERS.filter((s) => {
    if (s.status !== "confirmed") return false;
    if (s.deliveryMethod !== "mail" && s.deliveryMethod !== "both") return false;
    if (!s.mailingAddress) return false;
    if (channel) {
      const inChannel = !s.channels || s.channels.length === 0 || s.channels.includes(channel);
      if (!inChannel) return false;
    }
    return true;
  });
  const header = "name,line1,line2,city,state,zip,email,channels,deliveryMethod";
  const csvRows = rows.map((s) => {
    const a = s.mailingAddress!;
    const channelsStr = (s.channels ?? []).join(";");
    return [
      csvEscape(s.name ?? ""),
      csvEscape(a.line1),
      csvEscape(a.line2 ?? ""),
      csvEscape(a.city),
      csvEscape(a.state),
      csvEscape(a.zip),
      csvEscape(s.email ?? ""),
      csvEscape(channelsStr),
      csvEscape(s.deliveryMethod ?? "mail"),
    ].join(",");
  });
  const csv = [header, ...csvRows].join("\n") + "\n";
  return delay(new Blob([csv], { type: "text/csv" }), 300);
}

function csvEscape(v: string): string {
  if (/[",\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

// ---- Subscriber self-service (docs/features/subscriber-self-service.md) ----------------------

/** Mock POST /public/notify/manage-link. ALWAYS resolves {status:"ok"} regardless of whether the
 *  address is on file — anti-enumeration, same posture as subscribe/resend (the mock never actually
 *  sends an email; the point is exercising the UI's generic-success + captcha-gated flow). */
export function manageLink(_email: string): Promise<{ status: "ok" }> {
  return delay({ status: "ok" as const }, 400);
}

function toSubscriberView(s: MockSubscriber): SubscriberView {
  return {
    id: s.personId,
    name: s.name,
    email: s.email,
    status: s.status,
    deliveryMethod: s.deliveryMethod || "email",
    channels: s.channels ?? [],
    hasMailingAddress: !!s.mailingAddress,
    createdAt: s.createdAt,
    updatedAt: s.createdAt,
  };
}

/** Mock GET /api/admin/subscribers?q=&status=. Search matches email/name case-insensitively;
 *  includes ALL statuses (subscriber-self-service.md wire contract — no drift). */
export function listSubscribers(
  q?: string,
  status?: SubscriptionStatus,
): Promise<SubscriberView[]> {
  let rows = SUBSCRIBERS;
  if (status) rows = rows.filter((s) => s.status === status);
  const needle = q?.trim().toLowerCase();
  if (needle) {
    rows = rows.filter(
      (s) => s.email?.toLowerCase().includes(needle) || s.name?.toLowerCase().includes(needle),
    );
  }
  return delay(rows.map(toSubscriberView), 300);
}

/** Mock POST /api/admin/subscribers/{id}/unsubscribe. Idempotent — an already-unsubscribed
 *  subscriber reads as success, mirroring the live handler. */
export function unsubscribeSubscriber(id: string): Promise<SubscriberView> {
  const sub = SUBSCRIBERS.find((s) => s.personId === id);
  if (!sub) return Promise.reject(new Error("Subscriber not found."));
  sub.status = "unsubscribed";
  return delay(toSubscriberView(sub), 300);
}

/** Mock POST /api/admin/subscribers/{id}/resend-confirmation. Status is unchanged (still
 *  "pending") — mirrors the live handler, which only re-sends the email. */
export function resendSubscriberConfirmation(id: string): Promise<SubscriberView> {
  const sub = SUBSCRIBERS.find((s) => s.personId === id);
  if (!sub) return Promise.reject(new Error("Subscriber not found."));
  return delay(toSubscriberView(sub), 300);
}

// ---- Broadcasts (staff console, spec §4) ------------------------------------
//
// AUDIENCE_ALL / "channel:<id>" mirror shared.AudienceAllConfirmed / shared.AudienceChannelPrefix
// (services/api/internal/shared/schemas.go) exactly — the wire values a live draft's `audience`
// field carries (stay-informed-channels.md §2.3/§3.3). Each mock broadcast stores the MACHINE
// target in `_audienceTarget` (never on the public BroadcastDetail shape) and audienceLabel()
// below computes the live human-readable display string from it — same "store the target, compute
// the label on read" split as the backend's audienceLabel/withLiveAudience (broadcasts.go).
const AUDIENCE_ALL = "all_confirmed_subscribers";
const AUDIENCE_CHANNEL_PREFIX = "channel:";

interface MockBroadcast extends BroadcastDetail {
  _audienceTarget: string;
}

function channelSubscriberCount(channelId: string): number {
  return SUBSCRIBERS.filter(
    (s) =>
      s.status === "confirmed" &&
      (!s.channels || s.channels.length === 0 || s.channels.includes(channelId)),
  ).length;
}

const BROADCASTS: MockBroadcast[] = [
  {
    id: "b-1",
    subject: "Water main break on Lakeshore Dr — boil advisory",
    body: "<p>Crews are repairing a water main break on Lakeshore Dr near the boat ramp. Residents in the affected area should boil tap water before drinking until further notice.</p><p>We expect repairs complete by end of day. Thank you for your patience.</p>",
    audience: "All confirmed subscribers",
    _audienceTarget: AUDIENCE_ALL,
    status: "sent",
    sentCount: 5,
    bouncedCount: 1,
    complainedCount: 0,
    unsubscribedCount: 1,
    channelEmail: true,
    channelWebsite: true,
    channelAlert: true,
    alertShowUntil: daysAgo(-4),
    websitePostStatus: "posted",
    createdBy: "Lister Potter",
    createdAt: daysAgo(10),
    updatedAt: daysAgo(10),
    sentAt: daysAgo(10),
  },
  {
    id: "b-2",
    subject: "Council meeting — Thursday 7pm at City Hall",
    body: "<p>The Linn Valley City Council meets this Thursday at 7pm at City Hall. Agenda includes the annual budget review and a discussion of the boat ramp repair project.</p>",
    audience: "All confirmed subscribers",
    _audienceTarget: AUDIENCE_ALL,
    status: "draft",
    sentCount: 0,
    bouncedCount: 0,
    complainedCount: 0,
    channelEmail: true,
    channelWebsite: false,
    channelAlert: false,
    createdBy: "Lister Potter",
    createdAt: hoursAgo(4),
    updatedAt: hoursAgo(1),
    sentAt: null,
  },
];

/** Validate a client-supplied audience against the active mock channel list — mirrors
 *  resolveChannelAudience (broadcasts.go): anything that doesn't resolve to an active channel id
 *  falls back to AUDIENCE_ALL, never errors. */
function resolveMockAudience(requested?: string): string {
  if (!requested || !requested.startsWith(AUDIENCE_CHANNEL_PREFIX)) return AUDIENCE_ALL;
  const id = requested.slice(AUDIENCE_CHANNEL_PREFIX.length);
  const active = CATEGORIES.some((c) => c.id === id && c.active && c.kind === "channel");
  return active ? requested : AUDIENCE_ALL;
}

function audienceLabel(target: string): string {
  if (target.startsWith(AUDIENCE_CHANNEL_PREFIX)) {
    const id = target.slice(AUDIENCE_CHANNEL_PREFIX.length);
    const channel = CATEGORIES.find((c) => c.id === id && c.kind === "channel");
    if (channel) return `${channel.label} subscribers (${channelSubscriberCount(id)})`;
  }
  return `All confirmed subscribers (${confirmedCount()})`;
}

export function listBroadcasts(): Promise<BroadcastRow[]> {
  const rows: BroadcastRow[] = BROADCASTS.map(
    ({ body: _body, createdBy: _createdBy, _audienceTarget, ...row }) => ({
      ...row,
      audience: row.status === "draft" ? audienceLabel(_audienceTarget) : row.audience,
    }),
  );
  return delay(clone(rows));
}

export function getBroadcast(id: string): Promise<BroadcastDetail | null> {
  const found = BROADCASTS.find((b) => b.id === id) ?? null;
  if (!found) return delay(null);
  const { _audienceTarget, ...rest } = found;
  return delay(
    clone({
      ...rest,
      audience: found.status === "draft" ? audienceLabel(_audienceTarget) : found.audience,
    }),
  );
}

export function saveBroadcastDraft(
  input: CreateBroadcastInput,
  id?: string,
): Promise<BroadcastDetail> {
  const now = new Date().toISOString();
  if (id) {
    const existing = BROADCASTS.find((b) => b.id === id);
    if (existing) {
      existing.subject = input.subject;
      existing.body = input.body;
      if (input.audience !== undefined)
        existing._audienceTarget = resolveMockAudience(input.audience);
      if (input.channelEmail !== undefined) existing.channelEmail = input.channelEmail;
      if (input.channelWebsite !== undefined) existing.channelWebsite = input.channelWebsite;
      if (input.channelAlert !== undefined) existing.channelAlert = input.channelAlert;
      if (input.alertShowUntil !== undefined) existing.alertShowUntil = input.alertShowUntil;
      existing.updatedAt = now;
      const { _audienceTarget, ...rest } = existing;
      return delay(clone({ ...rest, audience: audienceLabel(_audienceTarget) }));
    }
  }
  const audienceTarget = resolveMockAudience(input.audience);
  const created: MockBroadcast = {
    id: `b-${Date.now()}`,
    subject: input.subject,
    body: input.body,
    audience: audienceLabel(audienceTarget),
    _audienceTarget: audienceTarget,
    status: "draft",
    sentCount: 0,
    bouncedCount: 0,
    complainedCount: 0,
    channelEmail: input.channelEmail ?? true,
    channelWebsite: input.channelWebsite ?? false,
    channelAlert: input.channelAlert ?? false,
    alertShowUntil: input.alertShowUntil ?? null,
    createdBy: CURRENT_USER.name ?? "Staff",
    createdAt: now,
    updatedAt: now,
    sentAt: null,
  };
  BROADCASTS.unshift(created);
  const { _audienceTarget: _t, ...rest } = created;
  return delay(clone(rest));
}

/**
 * Mock POST /broadcasts/{id}/send — NOT optimistic (spec §4.4/§4.6): resolves only after the
 * "enqueue" delay, matching the live contract where the confirm dialog waits for this call.
 * Channel-filtered when the draft's audience targets a channel (stay-informed-channels.md §3.3),
 * same shared.SubscriptionInChannel-equivalent filter as the live count so what staff confirmed
 * sending to is what actually got counted.
 */
export function sendBroadcast(id: string): Promise<SendBroadcastResult> {
  const b = BROADCASTS.find((x) => x.id === id);
  if (!b) return Promise.reject(new ApiError("Broadcast not found.", 404, "not_found", false));
  const channelId = b._audienceTarget.startsWith(AUDIENCE_CHANNEL_PREFIX)
    ? b._audienceTarget.slice(AUDIENCE_CHANNEL_PREFIX.length)
    : null;
  const recipientCount = channelId ? channelSubscriberCount(channelId) : confirmedCount();
  return delay(undefined, 700).then(() => {
    b.status = "sent";
    b.sentCount = recipientCount;
    b.sentAt = new Date().toISOString();
    b.updatedAt = b.sentAt;
    b.audience = audienceLabel(b._audienceTarget);
    // Website-post publish outcome is reported SEPARATELY from the email send (turnkey-cms-ui.md
    // §5.4 — one channel can succeed while the other fails); always "posted" in mock mode — there's
    // no reachable failure path here since the mock has no real site-publish pipeline to fail.
    b.websitePostStatus = b.channelWebsite ? "posted" : undefined;
    return { status: b.status, recipientCount };
  });
}

export function sendBroadcastTest(_id: string): Promise<void> {
  return delay(undefined, 500);
}

// ---- Analytics (GET /api/analytics) -----------------------------------------
//
// The seeded REQUESTS list is tiny (a handful of rows), too small to make a realistic-looking
// mayor dashboard. Instead, synthesize a deterministic (seeded) 26-week volume history from the
// real category/status vocabulary so USE_MOCKS dev renders numbers that look like an actual small
// town's queue. AGGREGATE-ONLY — no per-request identity, no reporter data, ever.
import type { AnalyticsAverages, AnalyticsView, CategoryCount, VolumeTrendPoint } from "./types.js";

/** Small deterministic PRNG (mulberry32) so the mock dataset is stable across renders/tests. */
function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const ANALYTICS_STATUS_WEIGHTS: Array<[RequestStatus, number]> = [
  ["New", 3],
  ["Acknowledged", 2],
  ["InProgress", 3],
  ["Resolved", 5],
  ["Closed", 6],
  ["Duplicate", 1],
  ["Invalid", 1],
];
const OPEN_STATUSES = new Set<RequestStatus>(["New", "Acknowledged", "InProgress"]);

function weekStartOf(d: Date): string {
  const dow = d.getUTCDay(); // 0 = Sunday
  const monOffset = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + monOffset),
  );
  return monday.toISOString().slice(0, 10);
}

/**
 * Build a full synthetic 26-week (~6 month) analytics history once, then slice/aggregate it per
 * requested range. Rebuilt fresh each call (cheap: ~26*~8 rows) rather than module-level so tests
 * that stub `Date` still see a coherent series relative to `now`.
 */
function buildSyntheticSeries(): Array<{
  weekStart: string;
  createdAt: number;
  status: RequestStatus;
  category: string;
  ackDays: number | null;
  resolveDays: number | null;
}> {
  const rand = mulberry32(20260712);
  const weeks = 26;
  // Anchor the trailing 26-week history to the ACTUAL current date, not the frozen seed-mock `now`.
  // The dashboard requests a live window ([today - 90d, today]), so a fixed anchor drifts out of
  // range as the calendar advances, leaving the weekly series empty and the chart unrendered.
  const anchorMs = Date.now();
  const rows: ReturnType<typeof buildSyntheticSeries> = [];
  for (let w = weeks - 1; w >= 0; w--) {
    const weekEndMs = anchorMs - w * 7 * 86_400_000;
    const weekStart = weekStartOf(new Date(weekEndMs));
    const count = 4 + Math.floor(rand() * 7); // 4–10 concerns/week
    for (let i = 0; i < count; i++) {
      const createdAt = weekEndMs - Math.floor(rand() * 7) * 86_400_000;
      const cat = CATEGORIES[Math.floor(rand() * CATEGORIES.length)]!.label;
      const totalWeight = ANALYTICS_STATUS_WEIGHTS.reduce((s, [, wt]) => s + wt, 0);
      let pick = rand() * totalWeight;
      let status: RequestStatus = "New";
      for (const [s, wt] of ANALYTICS_STATUS_WEIGHTS) {
        if (pick < wt) {
          status = s;
          break;
        }
        pick -= wt;
      }
      const acknowledged = status !== "New";
      const resolved = status === "Resolved" || status === "Closed";
      rows.push({
        weekStart,
        createdAt,
        status,
        category: cat,
        ackDays: acknowledged ? 1 + Math.round(rand() * 4 * 10) / 10 : null,
        resolveDays: resolved ? 3 + Math.round(rand() * 9 * 10) / 10 : null,
      });
    }
  }
  return rows;
}

/** Aggregate-only analytics for the Mayor dashboard (staff-auth, tenant-scoped). Defaults to the
 *  trailing 90 days when no range is given, matching the live endpoint's default. */
export async function getAnalytics(start?: string, end?: string): Promise<AnalyticsView> {
  // Default to the trailing 90 days from the ACTUAL current date (matching the live endpoint and the
  // relative anchor of buildSyntheticSeries) — a frozen default drifts out of the synthetic series.
  const endMs = end ? Date.parse(end) : Date.now();
  const startMs = start ? Date.parse(start) : endMs - 90 * 86_400_000;

  const all = buildSyntheticSeries();
  const inRange = all.filter((r) => r.createdAt >= startMs && r.createdAt <= endMs);

  const byStatus: Partial<Record<RequestStatus, number>> = {};
  for (const r of inRange) byStatus[r.status] = (byStatus[r.status] ?? 0) + 1;

  const openCount = inRange.filter((r) => OPEN_STATUSES.has(r.status)).length;
  const overdueCount = inRange.filter(
    (r) => OPEN_STATUSES.has(r.status) && endMs - r.createdAt > 10 * 86_400_000,
  ).length;

  const catCounts = new Map<string, number>();
  for (const r of inRange) catCounts.set(r.category, (catCounts.get(r.category) ?? 0) + 1);
  const sortedCats = [...catCounts.entries()].sort((a, b) => b[1] - a[1]);
  const top = sortedCats.slice(0, 8);
  const rest = sortedCats.slice(8).reduce((s, [, c]) => s + c, 0);
  const byCategory: CategoryCount[] = top.map(([categoryLabel, count]) => ({
    categoryLabel,
    count,
  }));
  if (rest > 0) byCategory.push({ categoryLabel: "Other", count: rest });

  const ackDaysArr = inRange.map((r) => r.ackDays).filter((n): n is number => n != null);
  const resolveDaysArr = inRange.map((r) => r.resolveDays).filter((n): n is number => n != null);
  const avg = (arr: number[]) =>
    arr.length ? Math.round((arr.reduce((s, n) => s + n, 0) / arr.length) * 10) / 10 : null;
  const averages: AnalyticsAverages = {
    avgBusinessDaysToAcknowledge: avg(ackDaysArr),
    avgBusinessDaysToResolve: avg(resolveDaysArr),
    acknowledgedSampleSize: ackDaysArr.length,
    resolvedSampleSize: resolveDaysArr.length,
  };

  const resolvedInRange = inRange.filter((r) => r.resolveDays != null);
  const onTimeCount = resolvedInRange.filter((r) => (r.resolveDays ?? 0) <= 10).length;
  const onTimeResolvedPct = resolvedInRange.length
    ? Math.round((onTimeCount / resolvedInRange.length) * 1000) / 10
    : -1;

  const trendMap = new Map<string, number>();
  for (const r of inRange) trendMap.set(r.weekStart, (trendMap.get(r.weekStart) ?? 0) + 1);
  const volumeTrend: VolumeTrendPoint[] = [...trendMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([weekStart, count]) => ({ weekStart, count }));

  const faq = await getFAQAnalytics();

  return delay({
    range: { start: new Date(startMs).toISOString(), end: new Date(endMs).toISOString() },
    totals: {
      allTime: all.length,
      inRange: inRange.length,
      open: openCount,
      overdueCount,
    },
    averages,
    onTimeResolvedPct,
    onTimeResolvedSampleSize: resolvedInRange.length,
    byStatus,
    byCategory,
    volumeTrend,
    faq,
  });
}

// ---- Ask a Question + FAQ (ask-a-question-faq.md) --------------------------
//
// Mirrors the concern mock store's shape/conventions exactly: an in-memory array + delay(), a
// tracking-code-keyed lookup, and public/staff projections kept structurally separate (no PII on
// the FAQ path; NFR-3). QUESTIONS is seeded with a couple of Linn Valley asks so the question
// console + tracking answer + FAQ browse/search all render real-looking data under USE_MOCKS.

interface MockQuestion {
  id: string;
  trackingCode: string;
  topic: string;
  question: string;
  status: QuestionStatus;
  answer: string;
  answeredAt: string | null;
  faqId: string | null;
  reporterContact: { name?: string; email?: string; phone?: string } | null;
  assigneeId: string | null;
  events: Array<{
    id: string;
    type: "created" | "status_change" | "note" | "assignment";
    visibility?: "public" | "internal";
    body: string;
    actorName?: string | null;
    createdAt: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

const topicLabel = (id: string): string =>
  MOCK_QUESTION_TOPICS.find((t) => t.id === id)?.label ?? id;

const QUESTIONS: MockQuestion[] = [
  {
    id: "q-1",
    trackingCode: "LV-9K2M-QQ11",
    topic: "utilities",
    question: "Who do I call about a water bill that looks way too high this month?",
    status: "QPublished",
    answer:
      "Contact City Hall at (913) 555-0100 and ask for Utility Billing — they can pull your meter reads and check for a leak-adjustment credit.",
    answeredAt: daysAgo(9),
    faqId: "faq-1",
    reporterContact: { email: "resident1@example.com" },
    assigneeId: "u-mayor",
    events: [
      { id: "qe-1", type: "created", body: "Question submitted", createdAt: daysAgo(10) },
      {
        id: "qe-2",
        type: "status_change",
        body: "Contact City Hall at (913) 555-0100 and ask for Utility Billing — they can pull your meter reads and check for a leak-adjustment credit.",
        actorName: "Lister Potter",
        createdAt: daysAgo(9),
      },
    ],
    createdAt: daysAgo(10),
    updatedAt: daysAgo(9),
  },
  {
    id: "q-2",
    trackingCode: "LV-4P7R-QQ22",
    topic: "permits",
    question: "Do I need a permit to build a small shed in my backyard?",
    status: "QAnswered",
    answer:
      "Sheds under 120 sq ft don't need a permit. Anything larger, or anything with plumbing/electrical, does — call City Hall to confirm your setback requirements.",
    answeredAt: daysAgo(2),
    faqId: null,
    reporterContact: null,
    assigneeId: null,
    events: [
      { id: "qe-3", type: "created", body: "Question submitted", createdAt: daysAgo(3) },
      {
        id: "qe-4",
        type: "status_change",
        body: "Sheds under 120 sq ft don't need a permit. Anything larger, or anything with plumbing/electrical, does — call City Hall to confirm your setback requirements.",
        actorName: "Dana Reyes",
        createdAt: daysAgo(2),
      },
    ],
    createdAt: daysAgo(3),
    updatedAt: daysAgo(2),
  },
  {
    id: "q-3",
    trackingCode: "LV-1X5T-QQ33",
    topic: "meetings",
    question: "When does the city council usually meet?",
    status: "QNew",
    answer: "",
    answeredAt: null,
    faqId: null,
    reporterContact: { name: "Pat", email: "pat@example.com" },
    assigneeId: null,
    events: [{ id: "qe-5", type: "created", body: "Question submitted", createdAt: hoursAgo(6) }],
    createdAt: hoursAgo(6),
    updatedAt: hoursAgo(6),
  },
  {
    id: "q-4",
    trackingCode: "LV-7W3B-QQ44",
    topic: "permits",
    question: "Is a permit required for a detached storage shed?",
    status: "QAnswered",
    answer:
      "Same rule as any accessory structure: under 120 sq ft with no plumbing/electrical, no permit needed — otherwise call City Hall.",
    answeredAt: daysAgo(1),
    faqId: null,
    reporterContact: null,
    assigneeId: null,
    events: [
      { id: "qe-6", type: "created", body: "Question submitted", createdAt: daysAgo(2) },
      {
        id: "qe-7",
        type: "status_change",
        body: "Same rule as any accessory structure: under 120 sq ft with no plumbing/electrical, no permit needed — otherwise call City Hall.",
        actorName: "Dana Reyes",
        createdAt: daysAgo(1),
      },
    ],
    createdAt: daysAgo(2),
    updatedAt: daysAgo(1),
  },
];

const FAQS: FAQView[] = [
  {
    id: "faq-1",
    topicId: "utilities",
    topicLabel: topicLabel("utilities"),
    question: "My water bill looks too high — what should I do?",
    answerHtml:
      "<p>Contact City Hall at (913) 555-0100 and ask for Utility Billing. They can pull your recent meter reads and check whether a leak-adjustment credit applies.</p>",
    published: true,
    featured: true,
    order: 0,
    viewCount: 42,
    helpfulYes: 11,
    helpfulNo: 1,
    sourceQuestionIds: ["q-1"],
    createdAt: daysAgo(9),
    updatedAt: daysAgo(9),
    publishedAt: daysAgo(9),
  },
  {
    id: "faq-2",
    topicId: "trash",
    topicLabel: "Trash & recycling",
    question: "When is trash day, and what if I miss it?",
    answerHtml:
      "<p>Trash pickup is every Tuesday. Recycling is every other Tuesday — check the calendar on the city site. Missed pickups can be reported to Public Works.</p><ul><li>Set carts out by 7am</li><li>Bag loose trash</li></ul>",
    published: true,
    featured: false,
    order: 1,
    viewCount: 108,
    helpfulYes: 30,
    helpfulNo: 2,
    createdAt: daysAgo(40),
    updatedAt: daysAgo(40),
    publishedAt: daysAgo(40),
  },
  {
    id: "faq-3",
    topicId: "permits",
    topicLabel: topicLabel("permits"),
    question: "How long does a building permit review take?",
    answerHtml: "<p>Most residential permit reviews take 5–10 business days.</p>",
    published: true,
    featured: false,
    order: 0,
    viewCount: 19,
    helpfulYes: 4,
    helpfulNo: 0,
    createdAt: daysAgo(60),
    updatedAt: daysAgo(60),
    publishedAt: daysAgo(60),
  },
];

function toQuestionQueueRow(q: MockQuestion): QuestionQueueRow {
  return {
    id: q.id,
    trackingCode: q.trackingCode,
    topic: q.topic,
    topicLabel: topicLabel(q.topic),
    question: q.question,
    status: q.status,
    reporterName: q.reporterContact?.name ?? q.reporterContact?.email ?? null,
    assigneeId: q.assigneeId,
    createdAt: q.createdAt,
    updatedAt: q.updatedAt,
  };
}

function toStaffQuestionDetail(q: MockQuestion): StaffQuestionDetail {
  return {
    id: q.id,
    trackingCode: q.trackingCode,
    topic: q.topic,
    topicLabel: topicLabel(q.topic),
    question: q.question,
    status: q.status,
    answer: q.answer,
    answeredAt: q.answeredAt,
    faqId: q.faqId,
    reporterContact: q.reporterContact,
    assigneeId: q.assigneeId,
    events: q.events.map((e) => ({
      id: e.id,
      type: e.type,
      visibility:
        e.visibility ??
        (e.type === "created" || e.type === "status_change" ? "public" : "internal"),
      body: e.body,
      actorName: e.actorName,
      createdAt: e.createdAt,
    })),
    createdAt: q.createdAt,
    updatedAt: q.updatedAt,
  };
}

/** Mock POST /api/public/questions — ♻️ same trackingCode/status/trackingUrl shape as submitReport. */
export function submitQuestion(input: SubmitQuestionInput): Promise<SubmitReportResult> {
  const createdAt = new Date().toISOString();
  const id = `q-${QUESTIONS.length + 1}-${Math.floor(Math.random() * 1e6)}`;
  const trackingCode = makeTrackingCode();
  const q: MockQuestion = {
    id,
    trackingCode,
    topic: input.topic,
    question: input.question,
    status: "QNew",
    answer: "",
    answeredAt: null,
    faqId: null,
    reporterContact: input.reporterContact ?? null,
    assigneeId: null,
    events: [{ id: `${id}-e1`, type: "created", body: "Question submitted", createdAt }],
    createdAt,
    updatedAt: createdAt,
  };
  QUESTIONS.push(q);
  return delay({
    trackingCode,
    status: "QNew",
    trackingUrl: `${window.location.origin}/track/${trackingCode}`,
  });
}

/** Mock GET /api/admin/questions — question console queue (Kind=question, its own bucket). */
export function listQuestionQueue(q?: string): Promise<QuestionQueueRow[]> {
  const needle = (q ?? "").trim().toLowerCase();
  const rows = QUESTIONS.filter(
    (item) =>
      !needle ||
      item.question.toLowerCase().includes(needle) ||
      item.trackingCode.toLowerCase().includes(needle) ||
      topicLabel(item.topic).toLowerCase().includes(needle),
  ).map(toQuestionQueueRow);
  return delay(rows);
}

export function getQuestion(id: string): Promise<StaffQuestionDetail | null> {
  const q = QUESTIONS.find((item) => item.id === id);
  return delay(q ? toStaffQuestionDetail(q) : null);
}

/** Mock POST /api/admin/questions/{id}/answer — write the public answer, New -> Answered. */
export function answerQuestion(
  id: string,
  answer: string,
  actorName: string,
): Promise<StaffQuestionDetail | null> {
  const q = QUESTIONS.find((item) => item.id === id);
  if (!q) return delay(null);
  const now = new Date().toISOString();
  q.answer = answer;
  q.answeredAt = now;
  q.status = "QAnswered";
  q.updatedAt = now;
  q.events.push({
    id: `${id}-e${q.events.length + 1}`,
    type: "status_change",
    body: answer,
    actorName,
    createdAt: now,
  });
  return delay(toStaffQuestionDetail(q));
}

/** Mock POST /api/admin/questions/{id}/close — Answered -> Closed (not FAQ-worthy). */
export function closeQuestion(id: string): Promise<StaffQuestionDetail | null> {
  const q = QUESTIONS.find((item) => item.id === id);
  if (!q) return delay(null);
  const now = new Date().toISOString();
  q.status = "QClosed";
  q.updatedAt = now;
  q.events.push({
    id: `${id}-e${q.events.length + 1}`,
    type: "status_change",
    body: "Closed (not published to FAQ)",
    createdAt: now,
  });
  return delay(toStaffQuestionDetail(q));
}

/** Mock assign (♻️ mirrors assignRequest/assignKORA, question's own store). */
export function assignQuestion(
  id: string,
  assigneeId: string | null,
  actorId: string,
): Promise<StaffQuestionDetail | null> {
  const q = QUESTIONS.find((item) => item.id === id);
  if (!q) return delay(null);
  q.assigneeId = assigneeId;
  const now = new Date().toISOString();
  q.events.push({
    id: `${id}-e${q.events.length + 1}`,
    type: "assignment",
    visibility: "internal",
    body: assigneeId ? `Assigned to ${staffName(assigneeId)}.` : "Assignment cleared.",
    actorName: staffName(actorId),
    createdAt: now,
  });
  q.updatedAt = now;
  return delay(toStaffQuestionDetail(q));
}

// ---- FAQ (public browse/search/vote + staff publish/edit) ------------------

export function browseFAQ(topic?: string): Promise<FAQView[]> {
  const list = FAQS.filter((f) => f.published && (!topic || f.topicId === topic));
  list.sort((a, b) => (a.featured === b.featured ? a.order - b.order : a.featured ? -1 : 1));
  return delay(clone(list));
}

export function searchFAQ(q: string, topic?: string): Promise<FAQView[]> {
  const tokens = q.toLowerCase().trim().split(/\s+/).filter(Boolean);
  const list = FAQS.filter((f) => {
    if (!f.published) return false;
    if (topic && f.topicId !== topic) return false;
    if (!tokens.length) return true;
    const hay = (f.question + " " + f.answerHtml).toLowerCase();
    return tokens.every((t) => hay.includes(t));
  });
  list.sort((a, b) => (a.featured === b.featured ? a.order - b.order : a.featured ? -1 : 1));
  return delay(clone(list));
}

export function getFAQ(id: string): Promise<FAQView | null> {
  const f = FAQS.find((item) => item.id === id && item.published);
  if (f) f.viewCount += 1;
  return delay(f ? clone(f) : null);
}

export function voteFAQ(id: string, helpful: boolean): Promise<void> {
  const f = FAQS.find((item) => item.id === id && item.published);
  if (f) {
    if (helpful) f.helpfulYes += 1;
    else f.helpfulNo += 1;
  }
  return delay(undefined);
}

/** Mock GET /api/admin/faq — list ALL, incl. unpublished (staff FAQ admin table). */
export function listAdminFAQ(): Promise<FAQView[]> {
  return delay(clone(FAQS));
}

/** Mock POST /api/admin/faq — publish (+ duplicate→merge). Server-sanitize is a live-only concern;
 *  the mock trusts the client editor (dev convenience — never the security posture). */
export function publishFAQ(
  input: PublishFAQInput,
): Promise<{ faq: FAQView; mergedQuestionIds: string[] }> {
  const primary = QUESTIONS.find((q) => q.id === input.questionId);
  const now = new Date().toISOString();
  const faq: FAQView = {
    id: `faq-${FAQS.length + 1}`,
    topicId: input.topicId,
    topicLabel: topicLabel(input.topicId),
    question: input.question,
    answerHtml: input.answerHtml,
    published: true,
    featured: Boolean(input.featured),
    order: input.order ?? 0,
    viewCount: 0,
    helpfulYes: 0,
    helpfulNo: 0,
    // Standalone FAQ (no questionId) ⇒ no source questions; filter out the absent primary.
    sourceQuestionIds: [input.questionId, ...(input.mergeQuestionIds ?? [])].filter(
      (x): x is string => Boolean(x),
    ),
    createdAt: now,
    updatedAt: now,
    publishedAt: now,
  };
  FAQS.push(faq);
  const mergedQuestionIds: string[] = [];
  if (primary) {
    primary.status = "QPublished";
    primary.faqId = faq.id;
    primary.updatedAt = now;
    primary.events.push({
      id: `${primary.id}-e${primary.events.length + 1}`,
      type: "status_change",
      body: "Published to FAQ",
      createdAt: now,
    });
  }
  for (const mid of input.mergeQuestionIds ?? []) {
    const m = QUESTIONS.find((q) => q.id === mid);
    if (!m) continue;
    m.status = "QPublished";
    m.faqId = faq.id;
    m.updatedAt = now;
    m.events.push({
      id: `${m.id}-e${m.events.length + 1}`,
      type: "status_change",
      body: "Merged and published to FAQ",
      createdAt: now,
    });
    mergedQuestionIds.push(mid);
  }
  return delay({ faq: clone(faq), mergedQuestionIds });
}

/** Mock PUT /api/admin/faq/{id} — edit/unpublish/reorder/feature. */
export function updateFAQ(id: string, input: UpdateFAQInput): Promise<FAQView | null> {
  const f = FAQS.find((item) => item.id === id);
  if (!f) return delay(null);
  const now = new Date().toISOString();
  const wasPublished = f.published;
  f.topicId = input.topicId;
  f.topicLabel = topicLabel(input.topicId);
  f.question = input.question;
  f.answerHtml = input.answerHtml;
  f.featured = input.featured;
  f.order = input.order;
  f.published = input.published;
  f.updatedAt = now;
  if (input.published && !f.publishedAt) f.publishedAt = now;
  // Unpublish flips the primary source question back to Answered (mirrors UnpublishQuestion).
  if (wasPublished && !input.published) {
    const primaryId = f.sourceQuestionIds?.[0];
    const primary = primaryId ? QUESTIONS.find((q) => q.id === primaryId) : undefined;
    if (primary) {
      primary.status = "QAnswered";
      primary.updatedAt = now;
      primary.events.push({
        id: `${primary.id}-e${primary.events.length + 1}`,
        type: "status_change",
        body: "Unpublished from FAQ",
        createdAt: now,
      });
    }
  }
  return delay(clone(f));
}

/** Mock POST /api/admin/concerns/suggest-faq — draft-only prefill from a Resolved concern. */
export function suggestFAQFromConcern(requestId: string): Promise<SuggestFAQDraft | null> {
  const r = REQUESTS.find((req) => req.id === requestId);
  if (!r || r.status !== "Resolved") return delay(null);
  const lastPublicUpdate =
    [...r.events].reverse().find((e) => e.visibility === "public" && e.type === "status_change")
      ?.body ?? "";
  return delay({
    sourceRequestId: r.id,
    draft: {
      topicId: "",
      question: `What should I know about ${r.category.toLowerCase()} concerns?`,
      answerHtml: lastPublicUpdate,
    },
  });
}

/** FAQ analytics block (aggregate-only — mirrors getAnalytics' posture, ask-a-question-faq.md §5.5).
 *  deflectionRatePct is a documented PROXY (typeahead-shown telemetry is Phase 3, not yet built):
 *  it estimates "published FAQs that already answer a topic" vs. "questions asked in that topic". */
export function getFAQAnalytics(): Promise<FAQAnalytics> {
  const mostViewed = [...FAQS]
    .sort((a, b) => b.viewCount - a.viewCount)
    .slice(0, 5)
    .map((f) => ({ faqId: f.id, question: f.question, viewCount: f.viewCount }));

  const topicCounts = new Map<string, number>();
  for (const q of QUESTIONS) topicCounts.set(q.topic, (topicCounts.get(q.topic) ?? 0) + 1);
  const mostAskedTopics = [...topicCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([id, count]) => ({ topicLabel: topicLabel(id), count }));

  const totalVotes = FAQS.reduce((s, f) => s + f.helpfulYes + f.helpfulNo, 0);
  const helpfulYes = FAQS.reduce((s, f) => s + f.helpfulYes, 0);
  const helpfulRatioPct = totalVotes ? Math.round((helpfulYes / totalVotes) * 1000) / 10 : null;

  const askedTopicsWithFAQ = new Set(FAQS.filter((f) => f.published).map((f) => f.topicId)).size;
  const askedTopics = topicCounts.size;
  const deflectionRatePct = askedTopics
    ? Math.round((askedTopicsWithFAQ / askedTopics) * 1000) / 10
    : null;

  return delay({ mostViewed, mostAskedTopics, helpfulRatioPct, deflectionRatePct });
}

// ---- Open Records (KORA) requests (records-request.md) ---------------------
//
// A NEW citizen intake Kind on the SAME shared intake→track→notify engine (♻️ mirrors how
// "question" was added alongside "concern"). MVP tier only: log → track → respond → audit. KORA
// has its OWN mock store (not REQUESTS/QUESTIONS) because its status value-space is disjoint from
// both RequestStatus and QuestionStatus, and it carries response-specific fields (denyReason,
// redactionNote, fulfilledFormat) that neither sibling shape has. Seeded with a couple of Linn
// Valley requests so the KORA console + tracking + response flow all render real-looking data.

const DEFAULT_KORA_RESPOND_DAYS = 3;

interface MockKORAEvent {
  id: string;
  type: "created" | "status_change" | "note" | "assignment";
  visibility: Visibility;
  body: string;
  toStatus?: KORAStatus;
  actorName?: string | null;
  createdAt: string;
}

interface MockKORA {
  id: string;
  trackingCode: string;
  recordsSought: string;
  recordType?: string;
  dateFrom?: string;
  dateTo?: string;
  preferredFormat: RecordsPreferredFormat;
  status: KORAStatus;
  reporterContact: { name?: string; email?: string; phone?: string } | null;
  assigneeId: string | null;
  attachments: AttachmentView[];
  events: MockKORAEvent[];
  denyReason?: string;
  redactionNote?: string;
  fulfilledFormat?: RecordsPreferredFormat;
  respondedAt?: string;
  createdAt: string;
  updatedAt: string;
}

const recordTypeLabel = (id?: string): string =>
  (id && MOCK_KORA_RECORD_TYPES.find((t) => t.id === id)?.label) || "Open Records Request";

const KORA_REQUESTS: MockKORA[] = [
  {
    id: "kora-1",
    trackingCode: "LV-3F8H-KR11",
    recordsSought: "All incident reports for the corner of 4th and Main from the last 6 months.",
    recordType: "police-reports",
    preferredFormat: "electronic",
    status: "KFulfilled",
    reporterContact: { name: "Jamie Carter", email: "jamie@example.com" },
    assigneeId: "u-officer",
    attachments: [],
    events: [
      {
        id: "ke-1",
        type: "created",
        visibility: "public",
        body: "Records request submitted",
        createdAt: daysAgo(12),
      },
      {
        id: "ke-2",
        type: "status_change",
        visibility: "public",
        body: "Records provided electronically — see attached.",
        toStatus: "KFulfilled",
        actorName: "Dana Reyes",
        createdAt: daysAgo(10),
      },
    ],
    fulfilledFormat: "electronic",
    respondedAt: daysAgo(10),
    createdAt: daysAgo(12),
    updatedAt: daysAgo(10),
  },
  {
    id: "kora-2",
    trackingCode: "LV-6M2Q-KR22",
    recordsSought: "Copy of the city council meeting minutes from the March budget session.",
    recordType: "meeting-minutes",
    preferredFormat: "copies",
    status: "KInProgress",
    reporterContact: null,
    assigneeId: "u-mayor",
    attachments: [],
    events: [
      {
        id: "ke-3",
        type: "created",
        visibility: "public",
        body: "Records request submitted",
        createdAt: daysAgo(3),
      },
      {
        id: "ke-4",
        type: "status_change",
        visibility: "public",
        body: "We're pulling the minutes now.",
        toStatus: "KInProgress",
        actorName: "Lister Potter",
        createdAt: daysAgo(2),
      },
    ],
    createdAt: daysAgo(3),
    updatedAt: daysAgo(2),
  },
  {
    id: "kora-3",
    trackingCode: "LV-9T4L-KR33",
    recordsSought:
      "Any correspondence between the mayor's office and the state municipal league about broadband grants in the last year.",
    recordType: "correspondence",
    dateFrom: "2025-08-01",
    dateTo: "2026-08-01",
    preferredFormat: "inspect",
    status: "KReceived",
    reporterContact: { email: "reporter@example.com" },
    assigneeId: null,
    attachments: [],
    events: [
      {
        id: "ke-5",
        type: "created",
        visibility: "public",
        body: "Records request submitted",
        createdAt: hoursAgo(20),
      },
    ],
    createdAt: hoursAgo(20),
    updatedAt: hoursAgo(20),
  },
  {
    id: "kora-4",
    trackingCode: "LV-2W7Y-KR44",
    recordsSought: "Personnel disciplinary file for a named employee.",
    recordType: "other",
    preferredFormat: "copies",
    status: "KDenied",
    reporterContact: { name: "Robin Shaw" },
    assigneeId: "u-mayor",
    attachments: [],
    events: [
      {
        id: "ke-6",
        type: "created",
        visibility: "public",
        body: "Records request submitted",
        createdAt: daysAgo(6),
      },
      {
        id: "ke-7",
        type: "status_change",
        visibility: "public",
        body: "Personnel records are exempt from disclosure under state law.",
        toStatus: "KDenied",
        actorName: "Lister Potter",
        createdAt: daysAgo(5),
      },
    ],
    denyReason: "Personnel records are exempt from disclosure under KSA 45-221.",
    respondedAt: daysAgo(5),
    createdAt: daysAgo(6),
    updatedAt: daysAgo(5),
  },
];

function koraAge(k: MockKORA): { ageBusinessDays: number; overdue: boolean; targetDays: number } {
  const ageBusinessDays = businessDaysSince(k.createdAt, new Date(now));
  const overdue = !isKORATerminal(k.status) && ageBusinessDays > DEFAULT_KORA_RESPOND_DAYS;
  return { ageBusinessDays, overdue, targetDays: DEFAULT_KORA_RESPOND_DAYS };
}

function toKORAQueueRow(k: MockKORA): KORAQueueRow {
  return {
    id: k.id,
    trackingCode: k.trackingCode,
    recordsSought: k.recordsSought,
    recordType: k.recordType,
    recordTypeLabel: recordTypeLabel(k.recordType),
    status: k.status,
    preferredFormat: k.preferredFormat,
    requesterName: k.reporterContact?.name ?? k.reporterContact?.email ?? null,
    assigneeId: k.assigneeId,
    createdAt: k.createdAt,
    updatedAt: k.updatedAt,
    ...koraAge(k),
  };
}

function toStaffKORADetail(k: MockKORA): StaffKORADetail {
  return {
    id: k.id,
    trackingCode: k.trackingCode,
    recordsSought: k.recordsSought,
    recordType: k.recordType,
    recordTypeLabel: recordTypeLabel(k.recordType),
    dateFrom: k.dateFrom,
    dateTo: k.dateTo,
    preferredFormat: k.preferredFormat,
    status: k.status,
    reporterContact: k.reporterContact,
    assigneeId: k.assigneeId,
    attachments: k.attachments,
    events: k.events.map((e) => ({
      id: e.id,
      type: e.type,
      visibility: e.visibility,
      body: e.body,
      toStatus: e.toStatus,
      actorName: e.actorName,
      createdAt: e.createdAt,
    })),
    denyReason: k.denyReason,
    redactionNote: k.redactionNote,
    fulfilledFormat: k.fulfilledFormat,
    respondedAt: k.respondedAt,
    createdAt: k.createdAt,
    updatedAt: k.updatedAt,
    ...koraAge(k),
  };
}

/** Mock POST /api/public/records-requests — ♻️ same trackingCode/status/trackingUrl shape as
 *  submitReport/submitQuestion. KORA allows anonymous requests (requesterContact optional). */
export function submitKORA(input: SubmitKORAInput): Promise<SubmitReportResult> {
  const createdAt = new Date().toISOString();
  const id = `kora-${KORA_REQUESTS.length + 1}-${Math.floor(Math.random() * 1e6)}`;
  const trackingCode = makeTrackingCode();
  const contact =
    input.requesterContact ?? (input.requesterName ? { name: input.requesterName } : undefined);
  const k: MockKORA = {
    id,
    trackingCode,
    recordsSought: input.recordsSought,
    recordType: input.recordType,
    dateFrom: input.dateFrom,
    dateTo: input.dateTo,
    preferredFormat: input.preferredFormat,
    status: "KReceived",
    reporterContact: contact ?? null,
    assigneeId: null,
    attachments: (input.attachments ?? []).map((s3Key, i) => ({
      id: `${id}-a${i}`,
      url: "",
      filename: s3Key.split("/").pop() ?? "attachment",
      contentType: "",
      kind: "resident-photo",
      visibility: "public",
      createdAt,
      isImage: false,
    })),
    events: [
      {
        id: `${id}-e1`,
        type: "created",
        visibility: "public",
        body: "Records request submitted",
        createdAt,
      },
    ],
    createdAt,
    updatedAt: createdAt,
  };
  KORA_REQUESTS.push(k);
  return delay({
    trackingCode,
    status: k.status,
    trackingUrl: `${typeof window !== "undefined" ? window.location.origin : ""}/track/${trackingCode}`,
  });
}

/** Mock GET /api/admin/kora — the KORA console queue (Kind=kora, its own bucket). */
export function listKORAQueue(q?: string): Promise<KORAQueueRow[]> {
  const needle = q?.trim().toLowerCase();
  return delay(
    KORA_REQUESTS.filter(
      (k) =>
        !needle ||
        k.recordsSought.toLowerCase().includes(needle) ||
        k.trackingCode.toLowerCase().includes(needle) ||
        (k.reporterContact?.name ?? "").toLowerCase().includes(needle),
    ).map(toKORAQueueRow),
  );
}

export function getKORA(id: string): Promise<StaffKORADetail | null> {
  const k = KORA_REQUESTS.find((x) => x.id === id);
  return delay(k ? toStaffKORADetail(k) : null);
}

/** Mock POST /api/admin/kora/{id}/respond — status transition + response fields, notifies. Enforces
 *  the same client-visible invariants as validate.go ValidateRespondKORA: denyReason required when
 *  status=KDenied; publicNote required on a terminal fulfilled/partial/denied transition. */
export function respondKORA(id: string, input: RespondKORAInput): Promise<StaffKORADetail | null> {
  const k = KORA_REQUESTS.find((x) => x.id === id);
  if (!k) return delay(null);
  if (!canTransitionKORA(k.status, input.status)) {
    return Promise.reject(
      new ApiError("That status change isn't allowed.", 400, "invalid_transition", false),
    );
  }
  if (input.status === "KDenied" && !input.denyReason?.trim()) {
    return Promise.reject(
      new ApiError(
        "A reason is required when denying a request.",
        400,
        "deny_reason_required",
        false,
      ),
    );
  }
  if (isKORATerminal(input.status) && input.status !== "KClosed" && !input.publicNote?.trim()) {
    return Promise.reject(
      new ApiError(
        "A public note is required to close out this request.",
        400,
        "public_note_required",
        false,
      ),
    );
  }
  const updatedAt = new Date().toISOString();
  k.status = input.status;
  if (input.denyReason) k.denyReason = input.denyReason;
  if (input.redactionNote) k.redactionNote = input.redactionNote;
  if (input.fulfilledFormat) k.fulfilledFormat = input.fulfilledFormat;
  if (isKORATerminal(input.status)) k.respondedAt = updatedAt;
  k.events.push({
    id: `${id}-e${k.events.length + 1}`,
    type: "status_change",
    visibility: "public",
    body: input.publicNote || input.denyReason || `Status changed to ${input.status}`,
    toStatus: input.status,
    actorName: CURRENT_USER.name,
    createdAt: updatedAt,
  });
  k.updatedAt = updatedAt;
  return delay(clone(toStaffKORADetail(k)));
}

/** Mock assign (♻️ mirrors assignRequest, KORA's own store). */
export function assignKORA(
  id: string,
  assigneeId: string | null,
  actorId: string,
): Promise<StaffKORADetail | null> {
  const k = KORA_REQUESTS.find((x) => x.id === id);
  if (!k) return delay(null);
  k.assigneeId = assigneeId;
  const updatedAt = new Date().toISOString();
  k.events.push({
    id: `${id}-e${k.events.length + 1}`,
    type: "assignment",
    visibility: "internal",
    body: assigneeId ? `Assigned to ${staffName(assigneeId)}` : "Assignment cleared",
    actorName: staffName(actorId),
    createdAt: updatedAt,
  });
  k.updatedAt = updatedAt;
  return delay(clone(toStaffKORADetail(k)));
}

/** Mock add note (♻️ mirrors addNote, KORA's own store). */
export function addKORANote(
  id: string,
  body: string,
  visibility: Visibility,
  actorId: string,
): Promise<StaffKORADetail | null> {
  const k = KORA_REQUESTS.find((x) => x.id === id);
  if (!k) return delay(null);
  const createdAt = new Date().toISOString();
  k.events.push({
    id: `${id}-e${k.events.length + 1}`,
    type: "note",
    visibility,
    body,
    actorName: staffName(actorId),
    createdAt,
  });
  k.updatedAt = createdAt;
  return delay(clone(toStaffKORADetail(k)));
}

/** Mock attach a produced record (♻️ mirrors addStaffAttachment, KORA's own store). */
export function addKORAAttachment(
  id: string,
  attachment: Omit<AttachmentView, "id" | "createdAt">,
  actorId: string,
): Promise<StaffKORADetail | null> {
  const k = KORA_REQUESTS.find((x) => x.id === id);
  if (!k) return delay(null);
  const createdAt = new Date().toISOString();
  const full: AttachmentView = {
    ...attachment,
    id: `${id}-a${k.attachments.length + 1}`,
    createdAt,
  };
  k.attachments.push(full);
  k.events.push({
    id: `${id}-e${k.events.length + 1}`,
    type: "note",
    visibility: attachment.visibility,
    body: `Attached ${attachment.filename}`,
    actorName: staffName(actorId),
    createdAt,
  });
  k.updatedAt = createdAt;
  return delay(clone(toStaffKORADetail(k)));
}

/** Kind=kora categories only (staff console record-type taxonomy, ♻️ mirrors listQuestionTopics). */
export function listKORARecordTypes(): Promise<CategoryView[]> {
  return listCategories("kora");
}

// ---- Code Enforcement (Case) requests ---------------------------------------
//
// A FOURTH intake Kind on the SAME shared intake→track→notify engine (♻️ mirrors how KORA was
// added alongside concern/question). STAFF-ONLY: an officer opens a case, there is no public/
// resident intake for one at all (handler/case.go doc) — so, unlike KORA, there is no
// submitCase/tracking-view wiring here. CASES has its OWN mock store (not REQUESTS/KORA_REQUESTS)
// because its status value-space is disjoint from every sibling, and it carries case-lifecycle
// fields (ordinanceRef, complianceDeadline, cureSteps, ownerName/Address, reinspectionOutcome) that
// no sibling shape has. Seeded with a couple of Linn Valley cases so the case console + lifecycle
// actions all render real-looking data.

interface MockCaseEvent {
  id: string;
  type: "created" | "status_change" | "note" | "assignment" | "attachment";
  visibility: Visibility;
  body: string;
  toStatus?: CaseStatus;
  actorName?: string | null;
  createdAt: string;
}

interface MockCase {
  id: string;
  trackingCode: string;
  violationType?: string;
  description: string;
  status: CaseStatus;
  ordinanceRef?: string;
  ownerName?: string;
  ownerAddress?: string;
  complianceDeadline?: string;
  noticeIssuedAt?: string;
  cureSteps?: string;
  reinspectionOutcome?: "compliant" | "not_compliant" | "";
  sourceRequestId?: string;
  propertyId: string | null;
  assigneeId: string | null;
  attachments: AttachmentView[];
  events: MockCaseEvent[];
  createdAt: string;
  updatedAt: string;
}

const violationTypeLabel = (id?: string): string =>
  (id && MOCK_CASE_VIOLATION_TYPES.find((t) => t.id === id)?.label) || "Code Enforcement Case";

const CASES: MockCase[] = [
  {
    id: "case-1",
    trackingCode: "LV-4K9P-CE11",
    violationType: "junk-vehicle",
    description: "Inoperable sedan on blocks in the side yard, visible from the street.",
    status: "CENotice",
    ordinanceRef: "Ch. 8 §8-45",
    ownerName: "Harold Beckett",
    ownerAddress: "118 Elm St, Linn Valley, KS 66040",
    complianceDeadline: "2026-08-20",
    noticeIssuedAt: daysAgo(4),
    cureSteps:
      "Remove the vehicle from the property or store it in an enclosed garage within 14 days.",
    propertyId: null,
    assigneeId: "u-officer",
    attachments: [],
    events: [
      {
        id: "ce-1",
        type: "created",
        visibility: "internal",
        body: "Case opened",
        createdAt: daysAgo(6),
      },
      {
        id: "ce-2",
        type: "status_change",
        visibility: "internal",
        body: "Notice issued — compliance deadline 2026-08-20.",
        toStatus: "CENotice",
        actorName: "Dana Reyes",
        createdAt: daysAgo(4),
      },
    ],
    createdAt: daysAgo(6),
    updatedAt: daysAgo(4),
  },
  {
    id: "case-2",
    trackingCode: "LV-7R2N-CE22",
    violationType: "tall-grass",
    description: "Grass over 18 inches at the vacant lot next to 210 Lakeshore Dr.",
    status: "CEOpen",
    ownerAddress: "Vacant lot, Lakeshore Dr, Linn Valley, KS 66040",
    sourceRequestId: "r-1001",
    propertyId: null,
    assigneeId: null,
    attachments: [],
    events: [
      {
        id: "ce-3",
        type: "created",
        visibility: "internal",
        body: "Case opened",
        createdAt: hoursAgo(18),
      },
    ],
    createdAt: hoursAgo(18),
    updatedAt: hoursAgo(18),
  },
];

function toCaseQueueRow(c: MockCase): CaseQueueRow {
  return {
    id: c.id,
    trackingCode: c.trackingCode,
    violationType: c.violationType,
    violationTypeLabel: violationTypeLabel(c.violationType),
    description: c.description,
    status: c.status,
    ownerName: c.ownerName,
    propertyId: c.propertyId,
    assigneeId: c.assigneeId,
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
    complianceDeadline: c.complianceDeadline,
    ...caseDeadline(c),
  };
}

function caseDeadline(c: MockCase): { overdue: boolean; daysOverdue: number } {
  if (isCaseTerminal(c.status) || !c.complianceDeadline) return { overdue: false, daysOverdue: 0 };
  const deadline = new Date(`${c.complianceDeadline}T00:00:00Z`).getTime();
  const diffDays = Math.floor((now - deadline) / 86_400_000);
  return diffDays > 0
    ? { overdue: true, daysOverdue: diffDays }
    : { overdue: false, daysOverdue: 0 };
}

function toStaffCaseDetail(c: MockCase): StaffCaseDetail {
  return {
    id: c.id,
    trackingCode: c.trackingCode,
    violationType: c.violationType,
    violationTypeLabel: violationTypeLabel(c.violationType),
    description: c.description,
    status: c.status,
    ordinanceRef: c.ordinanceRef,
    ownerName: c.ownerName,
    ownerAddress: c.ownerAddress,
    complianceDeadline: c.complianceDeadline,
    noticeIssuedAt: c.noticeIssuedAt,
    cureSteps: c.cureSteps,
    reinspectionOutcome: c.reinspectionOutcome ?? "",
    sourceRequestId: c.sourceRequestId,
    propertyId: c.propertyId,
    assigneeId: c.assigneeId,
    attachments: c.attachments,
    events: c.events.map((e) => ({
      id: e.id,
      type: e.type,
      visibility: e.visibility,
      body: e.body,
      toStatus: e.toStatus,
      actorName: e.actorName,
      createdAt: e.createdAt,
    })),
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
    ...caseDeadline(c),
  };
}

/** Mock POST /api/admin/cases — an officer opens a new code-enforcement case. STAFF-ONLY (no
 *  public/resident intake mock path exists for a case, mirroring the live handler). When
 *  sourceRequestId is set, inherits the source concern's propertyId when propertyId wasn't ALSO
 *  explicitly given (♻️ mirrors handler.OpenCase's inheritance rule) and appends an "Escalated to
 *  code-enforcement case" note onto the source concern's own timeline. */
export function openCase(input: OpenCaseInput): Promise<OpenCaseResult> {
  const createdAt = new Date().toISOString();
  const id = `case-${CASES.length + 1}-${Math.floor(Math.random() * 1e6)}`;
  const trackingCode = makeTrackingCode();
  let propertyId = input.propertyId ?? null;
  if (input.sourceRequestId) {
    const source = REQUESTS.find((r) => r.id === input.sourceRequestId);
    if (!propertyId && source?.property) propertyId = source.property.id;
  }
  const c: MockCase = {
    id,
    trackingCode,
    violationType: input.category,
    description: input.description,
    status: "CEOpen",
    ordinanceRef: input.ordinanceRef,
    ownerName: input.ownerName,
    ownerAddress: input.ownerAddress,
    sourceRequestId: input.sourceRequestId,
    propertyId,
    assigneeId: null,
    attachments: [],
    events: [
      { id: `${id}-e1`, type: "created", visibility: "internal", body: "Case opened", createdAt },
    ],
    createdAt,
    updatedAt: createdAt,
  };
  CASES.push(c);
  if (input.sourceRequestId) {
    const source = REQUESTS.find((r) => r.id === input.sourceRequestId);
    if (source) {
      source.events.push({
        id: `${source.id}-e${source.events.length + 1}`,
        type: "note",
        visibility: "internal",
        body: `Escalated to code-enforcement case ${trackingCode}`,
        actorName: CURRENT_USER.name,
        createdAt,
      });
    }
  }
  return delay({
    trackingCode,
    requestId: id,
    status: c.status,
    sourceRequestId: input.sourceRequestId,
  });
}

/** Mock GET /api/admin/cases — the case console queue (Kind=case, its own bucket). */
export function listCases(q?: string): Promise<CaseQueueRow[]> {
  const needle = q?.trim().toLowerCase();
  return delay(
    CASES.filter(
      (c) =>
        !needle ||
        c.description.toLowerCase().includes(needle) ||
        c.trackingCode.toLowerCase().includes(needle) ||
        (c.ownerName ?? "").toLowerCase().includes(needle),
    ).map(toCaseQueueRow),
  );
}

export function getCase(id: string): Promise<StaffCaseDetail | null> {
  const c = CASES.find((x) => x.id === id);
  return delay(c ? toStaffCaseDetail(c) : null);
}

/** Mock POST /api/admin/cases/{id}/advance — status transition + case-field updates, per-status
 *  requirements enforced client-visibly the same as validate.go ValidateAdvanceCase:
 *  complianceDeadline + cureSteps required when issuing a notice (status=CENotice);
 *  reinspectionOutcome required when recording a re-inspection (status=CEReinspected). Stamps
 *  noticeIssuedAt the first (and every) time the case transitions INTO CENotice, mirroring
 *  handler.AdvanceCase. */
export function advanceCase(id: string, input: AdvanceCaseInput): Promise<StaffCaseDetail | null> {
  const c = CASES.find((x) => x.id === id);
  if (!c) return delay(null);
  if (!canTransitionCase(c.status, input.status)) {
    return Promise.reject(
      new ApiError("That status change isn't allowed.", 400, "invalid_transition", false),
    );
  }
  if (input.status === "CENotice" && (!input.complianceDeadline || !input.cureSteps?.trim())) {
    return Promise.reject(
      new ApiError(
        "A compliance deadline and cure steps are required when issuing a notice.",
        400,
        "notice_fields_required",
        false,
      ),
    );
  }
  if (input.status === "CEReinspected" && !input.reinspectionOutcome) {
    return Promise.reject(
      new ApiError(
        "A re-inspection outcome is required.",
        400,
        "reinspection_outcome_required",
        false,
      ),
    );
  }
  const updatedAt = new Date().toISOString();
  c.status = input.status;
  if (input.complianceDeadline) c.complianceDeadline = input.complianceDeadline;
  if (input.cureSteps) c.cureSteps = input.cureSteps;
  if (input.ordinanceRef) c.ordinanceRef = input.ordinanceRef;
  if (input.ownerName) c.ownerName = input.ownerName;
  if (input.ownerAddress) c.ownerAddress = input.ownerAddress;
  if (input.reinspectionOutcome) c.reinspectionOutcome = input.reinspectionOutcome;
  if (input.status === "CENotice") c.noticeIssuedAt = updatedAt;
  c.events.push({
    id: `${id}-e${c.events.length + 1}`,
    type: "status_change",
    visibility: "internal",
    body: input.note?.trim() || `Status changed to ${input.status}`,
    toStatus: input.status,
    actorName: CURRENT_USER.name,
    createdAt: updatedAt,
  });
  c.updatedAt = updatedAt;
  return delay(clone(toStaffCaseDetail(c)));
}

/** Mock assign (♻️ mirrors assignKORA, case's own store). */
export function assignCase(
  id: string,
  assigneeId: string | null,
  actorId: string,
): Promise<StaffCaseDetail | null> {
  const c = CASES.find((x) => x.id === id);
  if (!c) return delay(null);
  c.assigneeId = assigneeId;
  const updatedAt = new Date().toISOString();
  c.events.push({
    id: `${id}-e${c.events.length + 1}`,
    type: "assignment",
    visibility: "internal",
    body: assigneeId ? `Assigned to ${staffName(assigneeId)}` : "Assignment cleared",
    actorName: staffName(actorId),
    createdAt: updatedAt,
  });
  c.updatedAt = updatedAt;
  return delay(clone(toStaffCaseDetail(c)));
}

/** Mock add note (♻️ mirrors addKORANote, case's own store). */
export function addCaseNote(
  id: string,
  body: string,
  visibility: Visibility,
  actorId: string,
): Promise<StaffCaseDetail | null> {
  const c = CASES.find((x) => x.id === id);
  if (!c) return delay(null);
  const createdAt = new Date().toISOString();
  c.events.push({
    id: `${id}-e${c.events.length + 1}`,
    type: "note",
    visibility,
    body,
    actorName: staffName(actorId),
    createdAt,
  });
  c.updatedAt = createdAt;
  return delay(clone(toStaffCaseDetail(c)));
}

/** Mock attach a site photo / certified-mail receipt (♻️ mirrors addKORAAttachment, case's own
 *  store). */
export function addCaseAttachment(
  id: string,
  attachment: Omit<AttachmentView, "id" | "createdAt">,
  actorId: string,
): Promise<StaffCaseDetail | null> {
  const c = CASES.find((x) => x.id === id);
  if (!c) return delay(null);
  const createdAt = new Date().toISOString();
  const full: AttachmentView = {
    ...attachment,
    id: `${id}-a${c.attachments.length + 1}`,
    createdAt,
  };
  c.attachments.push(full);
  c.events.push({
    id: `${id}-e${c.events.length + 1}`,
    type: "attachment",
    visibility: attachment.visibility,
    body: `Attached ${attachment.filename}`,
    actorName: staffName(actorId),
    createdAt,
  });
  c.updatedAt = createdAt;
  return delay(clone(toStaffCaseDetail(c)));
}

/** Kind=case categories only (staff console violation-type taxonomy, ♻️ mirrors listKORARecordTypes). */
export function listCaseViolationTypes(): Promise<CategoryView[]> {
  return listCategories("case");
}

/** Kind=trade categories only (Contractor Registry's tenant-configurable trade taxonomy, spec §2.1
 *  — ♻️ mirrors listCaseViolationTypes/listPermitTypes). */
export function listTradeCategories(): Promise<CategoryView[]> {
  return listCategories("trade");
}

// ---- Permits & Licensing (roadmap module 6) ----------------------------------
//
// A SIXTH intake Kind on the SAME shared intake->track->notify engine (mirrors how KORA/Case were
// added). This COMBINES a public application intake (like KORA - an applicant files their own
// application) with a staff review->approve/deny->inspect->issue workflow (like Case). PERMITS has
// its OWN mock store (not REQUESTS/KORA_REQUESTS/CASES) because its status value-space is disjoint
// from every sibling, and it carries permit-lifecycle fields (conditions, denyReason, feeAmount,
// feePaid, permitNumber, issuedAt) that no sibling shape has. Seeded with a couple of Linn Valley
// permit applications so the permit console + apply + lifecycle actions all render real-looking
// data.

interface MockPermitEvent {
  id: string;
  type: "created" | "status_change" | "note" | "assignment" | "attachment" | "inspection";
  visibility: Visibility;
  body: string;
  toStatus?: PermitStatus;
  actorName?: string | null;
  createdAt: string;
}

interface MockPermit {
  id: string;
  trackingCode: string;
  permitType?: string;
  projectDescription: string;
  location: { address: string; lat?: number; lng?: number };
  status: PermitStatus;
  reporterContact: { name?: string; email?: string; phone?: string } | null;
  conditions?: string;
  denyReason?: string;
  totalFeeAmount: number;
  appFeeAmount: number;
  impactFeeAmount: number;
  feePaid: boolean;
  permitNumber?: string;
  issuedAt?: string;
  assigneeId: string | null;
  attachments: AttachmentView[];
  events: MockPermitEvent[];
  createdAt: string;
  updatedAt: string;
  /** Permits fidelity Phase A (docs/features/permits-fidelity.md §A.2) — the applicant's
   *  schema-driven answers + the schema version they were captured under. Empty/undefined for a
   *  legacy free-text-only application (never seeded here since all six mock types now carry a
   *  schema, but the shape stays optional to match the live wire contract). */
  formValues?: FormValues;
  formSchemaVersion?: number;
  /** Structured Inspections (permit-inspections.md Phase 1) — this permit's own recorded Inspection
   *  rows, mirroring the live shared.Inspection facet rows. Empty for a permit with no inspections
   *  recorded yet. */
  inspections?: Inspection[];
}

/**
 * TS mirror of shared.InspectionProgress (services/api/internal/shared/schemas.go) — derives
 * per-slot latest status + {passed, total} from `expected` (the permit type's InspectionTypes,
 * possibly empty — "no checklist") and `inspections` (every Inspection recorded for one permit, any
 * order). ALWAYS computed fresh — never stored — so the mock path can never drift from the live
 * derivation's contract. "Latest inspection per typeKey wins" (by createdAt); a free-form
 * (typeKey-less) inspection is appended as its own trailing slot, keyed by its own id.
 */
function deriveInspectionProgress(
  expected: { key: string; label: string }[],
  inspections: Inspection[],
): InspectionProgressSummary {
  const latestByKey = new Map<string, Inspection>();
  const freeform: Inspection[] = [];
  for (const insp of inspections) {
    if (!insp.typeKey) {
      freeform.push(insp);
      continue;
    }
    const cur = latestByKey.get(insp.typeKey);
    if (!cur || insp.createdAt >= cur.createdAt) latestByKey.set(insp.typeKey, insp);
  }

  const slots: InspectionProgressSummary["slots"] = [];
  let passed = 0;
  for (const et of expected) {
    const latest = latestByKey.get(et.key);
    if (latest?.status === "passed") passed++;
    slots.push({ typeKey: et.key, label: et.label, latest });
  }
  for (const insp of freeform) {
    slots.push({ label: insp.label || "Inspection", latest: insp });
  }
  return { slots, passed, total: expected.length };
}

const permitTypeLabel = (id?: string): string =>
  (id && MOCK_PERMIT_TYPES.find((t) => t.id === id)?.label) || "Permit Application";

const PERMIT_APP_FEE = 35;
const PERMIT_IMPACT_FEE_PER_TANK = 500;

const PERMITS: MockPermit[] = [
  {
    id: "permit-1",
    trackingCode: "LV-5D3K-PM11",
    permitType: "permit-skid-shed",
    projectDescription:
      "10x12 detached storage shed in the back yard, 5 ft from the property line.",
    location: { address: "412 Willow Ln, Linn Valley, KS 66040" },
    status: "PIssued",
    reporterContact: { name: "Pat Nguyen", email: "pat@example.com" },
    conditions: "Must maintain a 5 ft setback from the rear property line.",
    totalFeeAmount: 75,
    appFeeAmount: PERMIT_APP_FEE,
    impactFeeAmount: 0,
    feePaid: true,
    permitNumber: "BP-2026-014",
    issuedAt: daysAgo(2),
    assigneeId: "u-officer",
    attachments: [],
    events: [
      {
        id: "pe-1",
        type: "created",
        visibility: "public",
        body: "Permit application submitted",
        createdAt: daysAgo(14),
      },
      {
        id: "pe-2",
        type: "status_change",
        visibility: "public",
        body: "Approved with conditions: Must maintain a 5 ft setback from the rear property line.",
        toStatus: "PApproved",
        actorName: "Dana Reyes",
        createdAt: daysAgo(9),
      },
      {
        id: "pe-3",
        type: "inspection",
        visibility: "public",
        body: "Inspection PASSED on 2026-07-30 — setback confirmed, footings approved.",
        actorName: "Dana Reyes",
        createdAt: daysAgo(3),
      },
      {
        id: "pe-4",
        type: "status_change",
        visibility: "public",
        body: "Permit issued — BP-2026-014.",
        toStatus: "PIssued",
        actorName: "Dana Reyes",
        createdAt: daysAgo(2),
      },
    ],
    createdAt: daysAgo(14),
    updatedAt: daysAgo(2),
    formValues: {
      owner_name: "Pat Nguyen",
      owner_mailing_address: "412 Willow Ln, Linn Valley, KS 66040",
      owner_email: "pat@example.com",
      lot_number: "Lot 22",
      contractor_self: "true",
      self_contractor_affidavit_ack: "true",
      shed_dimensions: "10x12",
      shed_description: "Prefab storage shed on a gravel pad.",
      shed_age: "0",
      accessory_affidavit_ack: "true",
    },
    formSchemaVersion: 1,
  },
  {
    id: "permit-2",
    trackingCode: "LV-8H6M-PM22",
    permitType: "permit-building",
    projectDescription: "12x16 screened-in porch addition off the back of the house.",
    location: { address: "27 Lakeshore Dr, Linn Valley, KS 66040" },
    status: "PUnderReview",
    reporterContact: { name: "Robin Shaw", phone: "913-555-0142" },
    totalFeeAmount: 0,
    appFeeAmount: PERMIT_APP_FEE,
    impactFeeAmount: 0,
    feePaid: false,
    assigneeId: "u-mayor",
    attachments: [],
    events: [
      {
        id: "pe-5",
        type: "created",
        visibility: "public",
        body: "Permit application submitted",
        createdAt: daysAgo(3),
      },
      {
        id: "pe-6",
        type: "status_change",
        visibility: "public",
        body: "We're reviewing your plans now.",
        toStatus: "PUnderReview",
        actorName: "Lister Potter",
        createdAt: daysAgo(1),
      },
    ],
    createdAt: daysAgo(3),
    updatedAt: daysAgo(1),
    formValues: {
      owner_name: "Robin Shaw",
      owner_mailing_address: "27 Lakeshore Dr, Linn Valley, KS 66040",
      owner_phone: "913-555-0142",
      owner_email: "robin.shaw@example.com",
      lot_number: "Lot 9",
      contractor_self: "false",
      contractor_name: "Ashcroft Builders LLC",
      contractor_reg_number: "LV-2026-0143",
      lot_type: "building",
      structure_type: "wood_frame",
      foundation_type: "crawl",
      site_elements: "decks_porches",
      estimated_project_cost: "18400",
    },
    formSchemaVersion: 1,
  },
  {
    id: "permit-3",
    trackingCode: "LV-1Q9Z-PM33",
    permitType: "permit-electrical",
    projectDescription: "Upgrade panel to 200 amp service, underground run to the shop.",
    location: { address: "108 Main St, Linn Valley, KS 66040" },
    status: "PSubmitted",
    reporterContact: null,
    totalFeeAmount: 0,
    appFeeAmount: PERMIT_APP_FEE,
    impactFeeAmount: 0,
    feePaid: false,
    assigneeId: null,
    attachments: [],
    events: [
      {
        id: "pe-7",
        type: "created",
        visibility: "public",
        body: "Permit application submitted",
        createdAt: hoursAgo(10),
      },
    ],
    createdAt: hoursAgo(10),
    updatedAt: hoursAgo(10),
    formValues: {
      owner_name: "Casey Whitmore",
      owner_mailing_address: "108 Main St, Linn Valley, KS 66040",
      owner_email: "casey.w@example.com",
      contractor_self: "false",
      contractor_name: "J&R Electric",
      contractor_reg_number: "LV-2025-0087",
      lot_type: "r1_building",
      service_type: "upgrade",
      run_type: "underground",
      amps: "200",
      work_description: "Upgrade panel to 200 amp service, underground run to the shop.",
    },
    formSchemaVersion: 1,
  },
];

function toPermitQueueRow(p: MockPermit): PermitQueueRow {
  return {
    id: p.id,
    trackingCode: p.trackingCode,
    permitType: p.permitType,
    permitTypeLabel: permitTypeLabel(p.permitType),
    projectDescription: p.projectDescription,
    address: p.location.address,
    status: p.status,
    applicantName: p.reporterContact?.name ?? p.reporterContact?.email ?? null,
    totalFeeAmount: p.totalFeeAmount,
    appFeeAmount: p.appFeeAmount,
    impactFeeAmount: p.impactFeeAmount,
    feePaid: p.feePaid,
    assigneeId: p.assigneeId,
    createdAt: p.createdAt,
    updatedAt: p.updatedAt,
  };
}

function toStaffPermitDetail(p: MockPermit): StaffPermitDetail {
  const type = MOCK_PERMIT_TYPES.find((t) => t.id === p.permitType);
  return {
    id: p.id,
    trackingCode: p.trackingCode,
    permitType: p.permitType,
    permitTypeLabel: permitTypeLabel(p.permitType),
    projectDescription: p.projectDescription,
    location: p.location,
    status: p.status,
    reporterContact: p.reporterContact,
    conditions: p.conditions,
    denyReason: p.denyReason,
    totalFeeAmount: p.totalFeeAmount,
    appFeeAmount: p.appFeeAmount,
    impactFeeAmount: p.impactFeeAmount,
    feePaid: p.feePaid,
    permitNumber: p.permitNumber,
    issuedAt: p.issuedAt,
    assigneeId: p.assigneeId,
    attachments: p.attachments,
    events: p.events.map((e) => ({
      id: e.id,
      type: e.type,
      visibility: e.visibility,
      body: e.body,
      toStatus: e.toStatus,
      actorName: e.actorName,
      createdAt: e.createdAt,
    })),
    createdAt: p.createdAt,
    updatedAt: p.updatedAt,
    formSchema: type?.formSchema,
    formValues: p.formValues,
    formSchemaVersion: p.formSchemaVersion,
    requiredDocs: type?.requiredDocs,
    inspections: p.inspections ?? [],
    inspectionTypes: type?.inspectionTypes,
    inspectionProgress: deriveInspectionProgress(type?.inspectionTypes ?? [], p.inspections ?? []),
  };
}

/** Mock POST /api/public/permits - an applicant files their own permit application (unlike Case,
 *  which is staff-only). Applicant name/contact are optional-but-expected (unlike KORA's
 *  anonymous-first posture) - task brief. */
export function submitPermit(input: SubmitPermitInput): Promise<SubmitReportResult> {
  const createdAt = new Date().toISOString();
  const id = `permit-${PERMITS.length + 1}-${Math.floor(Math.random() * 1e6)}`;
  const trackingCode = makeTrackingCode();
  const contact =
    input.applicantContact ?? (input.applicantName ? { name: input.applicantName } : undefined);
  // Impact fee (permits-fidelity.md §A.5) — $500 × the tank_count answer, when present (mirrors
  // handler.computeImpactFee: absent/unparsable/non-positive tank_count → 0, never blocks submit).
  const tankCountRaw = input.formValues?.[TANK_COUNT_FORM_KEY];
  const tankCount = tankCountRaw ? Number.parseInt(tankCountRaw, 10) : 0;
  const impactFeeAmount =
    Number.isFinite(tankCount) && tankCount > 0 ? tankCount * PERMIT_IMPACT_FEE_PER_TANK : 0;
  const p: MockPermit = {
    id,
    trackingCode,
    permitType: input.permitType,
    projectDescription: input.projectDescription,
    location: input.location,
    status: "PSubmitted",
    reporterContact: contact ?? null,
    totalFeeAmount: 0,
    appFeeAmount: PERMIT_APP_FEE,
    impactFeeAmount,
    feePaid: false,
    assigneeId: null,
    attachments: (input.attachments ?? []).map((s3Key, i) => ({
      id: `${id}-a${i}`,
      url: "",
      filename: s3Key.split("/").pop() ?? "attachment",
      contentType: "",
      kind: "resident-photo",
      visibility: "public",
      createdAt,
      isImage: false,
    })),
    events: [
      {
        id: `${id}-e1`,
        type: "created",
        visibility: "public",
        body: "Permit application submitted",
        createdAt,
      },
    ],
    createdAt,
    updatedAt: createdAt,
    formValues: input.formValues,
    formSchemaVersion: MOCK_PERMIT_TYPES.find((t) => t.id === input.permitType)?.formSchema
      ?.version,
  };
  PERMITS.push(p);
  return delay({
    trackingCode,
    status: p.status,
    trackingUrl: `${typeof window !== "undefined" ? window.location.origin : ""}/track/${trackingCode}`,
  });
}

/** Mock GET /api/admin/permits - the permit console queue (Kind=permit, its own bucket). */
export function listPermits(q?: string): Promise<PermitQueueRow[]> {
  const needle = q?.trim().toLowerCase();
  return delay(
    PERMITS.filter(
      (p) =>
        !needle ||
        p.projectDescription.toLowerCase().includes(needle) ||
        p.trackingCode.toLowerCase().includes(needle) ||
        (p.reporterContact?.name ?? "").toLowerCase().includes(needle),
    ).map(toPermitQueueRow),
  );
}

export function getPermit(id: string): Promise<StaffPermitDetail | null> {
  const p = PERMITS.find((x) => x.id === id);
  return delay(p ? toStaffPermitDetail(p) : null);
}

/** Mock POST /api/admin/permits/{id}/advance - status transition + permit-field updates, per-status
 *  requirements enforced client-visibly the same as validate.go ValidateAdvancePermit: denyReason
 *  required when denying, permitNumber required when issuing. Stamps issuedAt the first time the
 *  permit transitions INTO PIssued, notifies the applicant on every transition (mirrors
 *  handler.AdvancePermit - unlike Case, which fires no notify at all). */
export function advancePermit(
  id: string,
  input: AdvancePermitInput,
): Promise<StaffPermitDetail | null> {
  const p = PERMITS.find((x) => x.id === id);
  if (!p) return delay(null);
  if (!canTransitionPermit(p.status, input.status)) {
    return Promise.reject(
      new ApiError("That status change isn't allowed.", 400, "invalid_transition", false),
    );
  }
  if (input.status === "PDenied" && !input.denyReason?.trim()) {
    return Promise.reject(
      new ApiError("A deny reason is required.", 400, "deny_reason_required", false),
    );
  }
  if (input.status === "PIssued" && !input.permitNumber?.trim()) {
    return Promise.reject(
      new ApiError("A permit number is required to issue.", 400, "permit_number_required", false),
    );
  }
  const updatedAt = new Date().toISOString();
  p.status = input.status;
  if (input.conditions !== undefined) p.conditions = input.conditions;
  if (input.denyReason !== undefined) p.denyReason = input.denyReason;
  if (input.totalFeeAmount !== undefined) p.totalFeeAmount = input.totalFeeAmount;
  if (input.impactFeeAmount !== undefined) {
    p.impactFeeAmount = input.impactFeeAmount;
  } else if (input.status === "PApproved" && !p.impactFeeAmount) {
    // Auto-derive on approval when staff didn't override AND it wasn't already stamped at submit
    // (♻️ mirrors handler.AdvancePermit's auto-calc — "omit" never clobbers a nonzero value).
    const tankCountRaw = p.formValues?.[TANK_COUNT_FORM_KEY];
    const tankCount = tankCountRaw ? Number.parseInt(tankCountRaw, 10) : 0;
    if (Number.isFinite(tankCount) && tankCount > 0)
      p.impactFeeAmount = tankCount * PERMIT_IMPACT_FEE_PER_TANK;
  }
  if (input.feePaid !== undefined) p.feePaid = input.feePaid;
  if (input.permitNumber !== undefined) p.permitNumber = input.permitNumber;
  if (input.status === "PIssued") p.issuedAt = updatedAt;
  let body = input.note?.trim() || "";
  if (!body && input.status === "PDenied") body = input.denyReason?.trim() || "";
  if (!body && input.status === "PApproved" && input.conditions?.trim()) {
    body = `Approved with conditions: ${input.conditions.trim()}`;
  }
  if (!body) body = `Status changed to ${input.status}`;
  p.events.push({
    id: `${id}-e${p.events.length + 1}`,
    type: "status_change",
    visibility: "public",
    body,
    toStatus: input.status,
    actorName: CURRENT_USER.name,
    createdAt: updatedAt,
  });
  p.updatedAt = updatedAt;
  return delay(clone(toStaffPermitDetail(p)));
}

/** Mock POST /api/admin/permits/{id}/inspection - append a PUBLIC-visibility inspection event to
 *  the permit's timeline, AND write a first-class Inspection facet row (permit-inspections.md
 *  Phase 1, mirrors handler.RecordInspection). Does NOT change the permit's status. `typeKey`, when
 *  set, must match one of the permit type's inspectionTypes keys; otherwise `label` is required —
 *  mirrors validate.go ValidateRecordInspection's xor rule. */
export function recordPermitInspection(
  id: string,
  input: RecordInspectionInput,
): Promise<StaffPermitDetail | null> {
  const p = PERMITS.find((x) => x.id === id);
  if (!p) return delay(null);
  const type = MOCK_PERMIT_TYPES.find((t) => t.id === p.permitType);
  const typeKey = input.typeKey?.trim();
  const label = input.label?.trim();
  if (typeKey) {
    if (!type?.inspectionTypes?.some((t) => t.key === typeKey)) {
      return Promise.reject(
        new ApiError("Unknown inspection type for this permit.", 400, "validation_error", false),
      );
    }
  } else if (!label) {
    return Promise.reject(
      new ApiError(
        "A label is required for a free-form inspection not on the checklist.",
        400,
        "validation_error",
        false,
      ),
    );
  }
  const createdAt = new Date().toISOString();
  const result = input.result === "fail" ? "FAILED" : "PASSED";
  let body = `Inspection ${result} on ${input.date}`;
  if (input.note?.trim()) body += ` — ${input.note.trim()}`;
  p.events.push({
    id: `${id}-e${p.events.length + 1}`,
    type: "inspection",
    visibility: "public",
    body,
    actorName: CURRENT_USER.name,
    createdAt,
  });
  const inspection: Inspection = {
    id: `${id}-insp${(p.inspections?.length ?? 0) + 1}`,
    typeKey,
    label,
    status: input.result === "fail" ? "failed" : "passed",
    result: input.result,
    note: input.note?.trim() || undefined,
    requestedBy: "staff",
    createdAt,
    updatedAt: createdAt,
  };
  p.inspections = [...(p.inspections ?? []), inspection];
  p.updatedAt = createdAt;
  return delay(clone(toStaffPermitDetail(p)));
}

/** Mock assign (♻️ mirrors assignCase, permit's own store). */
export function assignPermit(
  id: string,
  assigneeId: string | null,
  actorId: string,
): Promise<StaffPermitDetail | null> {
  const p = PERMITS.find((x) => x.id === id);
  if (!p) return delay(null);
  p.assigneeId = assigneeId;
  const updatedAt = new Date().toISOString();
  p.events.push({
    id: `${id}-e${p.events.length + 1}`,
    type: "assignment",
    visibility: "internal",
    body: assigneeId ? `Assigned to ${staffName(assigneeId)}` : "Assignment cleared",
    actorName: staffName(actorId),
    createdAt: updatedAt,
  });
  p.updatedAt = updatedAt;
  return delay(clone(toStaffPermitDetail(p)));
}

/** Mock add note (♻️ mirrors addCaseNote, permit's own store). */
export function addPermitNote(
  id: string,
  body: string,
  visibility: Visibility,
  actorId: string,
): Promise<StaffPermitDetail | null> {
  const p = PERMITS.find((x) => x.id === id);
  if (!p) return delay(null);
  const createdAt = new Date().toISOString();
  p.events.push({
    id: `${id}-e${p.events.length + 1}`,
    type: "note",
    visibility,
    body,
    actorName: staffName(actorId),
    createdAt,
  });
  p.updatedAt = createdAt;
  return delay(clone(toStaffPermitDetail(p)));
}

/** Mock attach a plan/doc (♻️ mirrors addCaseAttachment, permit's own store). */
export function addPermitAttachment(
  id: string,
  attachment: Omit<AttachmentView, "id" | "createdAt">,
  actorId: string,
): Promise<StaffPermitDetail | null> {
  const p = PERMITS.find((x) => x.id === id);
  if (!p) return delay(null);
  const createdAt = new Date().toISOString();
  const full: AttachmentView = {
    ...attachment,
    id: `${id}-a${p.attachments.length + 1}`,
    createdAt,
  };
  p.attachments.push(full);
  p.events.push({
    id: `${id}-e${p.events.length + 1}`,
    type: "attachment",
    visibility: attachment.visibility,
    body: `Attached ${attachment.filename}`,
    actorName: staffName(actorId),
    createdAt,
  });
  p.updatedAt = createdAt;
  return delay(clone(toStaffPermitDetail(p)));
}

/** Kind=permit categories only (staff console permit-type taxonomy, ♻️ mirrors listCaseViolationTypes). */
export function listPermitTypes(): Promise<CategoryView[]> {
  return listCategories("permit");
}

// ---- Permit → POA daily digest — "Log a paper permit" (docs/features/permit-poa-digest.md) ------

/**
 * Mock POST /api/admin/permits/ocr — deterministic fake OCR read for USE_MOCKS/dev/tests. Returns a
 * plausible pre-fill for the Universal base + (for a Building-shaped type) one deliberately
 * low-confidence field, so the review screen's "double-check" flag path is exercisable without a
 * real AI call. Mirrors cleanupAnswerMock's AI-error-path conventions (ApiError with the SAME codes
 * the live endpoint documents) for the handful of magic contentType values tests use to drive each
 * branch.
 */
export function ocrPermitFields(input: OcrPermitFieldsInput): Promise<OcrPermitFieldsResult> {
  // Magic contentType values exercise the documented error codes without needing a real file.
  if (input.contentType === "application/x-not-configured") {
    return Promise.reject(
      new ApiError("AI isn't configured yet.", 503, "ai_not_configured", false),
    );
  }
  if (input.contentType === "application/x-cap-reached") {
    return Promise.reject(
      new ApiError(
        "This month's AI usage cap has been reached.",
        503,
        "ai_monthly_cap_reached",
        false,
      ),
    );
  }
  if (input.contentType === "application/x-unavailable") {
    return Promise.reject(
      new ApiError("AI is temporarily unavailable.", 502, "ai_unavailable", true),
    );
  }
  if (input.contentType === "application/x-bogus") {
    return Promise.reject(
      new ApiError(
        "That file type isn't supported for scanning.",
        400,
        "unsupported_file_type",
        false,
      ),
    );
  }
  const permitTypeCat = MOCK_PERMIT_TYPES.find((t) => t.id === input.permitType);
  if (!permitTypeCat) {
    return Promise.reject(new ApiError("Unknown permit type.", 400, "unknown_permit_type", false));
  }
  const values: FormValues = {
    owner_name: "Dana Whitfield",
    owner_mailing_address: "214 Shawnee Trail, Linn Valley, KS",
    owner_email: "dana.whitfield@example.com",
    contractor_self: "true",
    self_contractor_affidavit_ack: "true",
  };
  const confidence: Record<string, number> = {
    owner_name: 0.96,
    owner_mailing_address: 0.93,
    owner_email: 0.9,
  };
  if (
    permitTypeCat.formSchema?.sections.some((s) => s.fields.some((f) => f.key === "lot_dimensions"))
  ) {
    // Building-shaped: add a low-confidence dimensions read (mirrors the mockup's "24 or 29?" case).
    values.lot_dimensions = "24x30";
    confidence.lot_dimensions = 0.42;
  }
  return delay({ permitType: input.permitType, values, confidence }, 500);
}

/**
 * Mock POST /api/admin/permits — staff-counter/paper/scanned permit intake (♻️ mirrors submitPermit,
 * but the clerk CHOOSES the initial status + Source="staff"/IntakeChannel="paper"). Returns the
 * SAME thin ack shape the live endpoint does (id/trackingCode/status/createdAt) — the caller
 * re-fetches via getPermit(id).
 */
export function createPermitByStaff(
  input: CreatePermitByStaffInput,
): Promise<CreatePermitByStaffResult> {
  const permitTypeCat = MOCK_PERMIT_TYPES.find((t) => t.id === input.permitType);
  if (!permitTypeCat) {
    return Promise.reject(new ApiError("Unknown permit type.", 400, "validation_error", false));
  }
  const createdAt = new Date().toISOString();
  const id = `permit-staff-${PERMITS.length + 1}-${Math.floor(Math.random() * 1e6)}`;
  const trackingCode = makeTrackingCode();
  const tankCountRaw = input.formValues?.[TANK_COUNT_FORM_KEY];
  const tankCount = tankCountRaw ? Number.parseInt(tankCountRaw, 10) : 0;
  const impactFeeAmount =
    Number.isFinite(tankCount) && tankCount > 0 ? tankCount * PERMIT_IMPACT_FEE_PER_TANK : 0;
  const p: MockPermit = {
    id,
    trackingCode,
    permitType: input.permitType,
    projectDescription: input.projectDescription,
    location: input.location,
    status: input.status,
    reporterContact: input.applicantContact ?? null,
    totalFeeAmount: 0,
    appFeeAmount: PERMIT_APP_FEE,
    impactFeeAmount,
    feePaid: false,
    assigneeId: null,
    attachments: [],
    events: [
      {
        id: `${id}-e1`,
        type: "created",
        visibility: "public",
        body: `Permit logged by staff (${input.status})`,
        createdAt,
      },
      {
        id: `${id}-e2`,
        type: "note",
        visibility: "internal",
        body: "Logged by staff (paper intake)",
        actorName: CURRENT_USER.name,
        createdAt,
      },
    ],
    createdAt,
    updatedAt: createdAt,
    formValues: input.formValues,
    formSchemaVersion: permitTypeCat.formSchema?.version,
  };
  PERMITS.push(p);
  return delay({ id, trackingCode, status: p.status, createdAt });
}

/**
 * Mock POST /api/admin/subscribers — staff-added subscriber, confirmed immediately (no confirmation
 * email). Find-or-create by email, mirroring the live handler's dedupe discipline.
 */
export function addSubscriberByStaff(input: AddSubscriberByStaffInput): Promise<SubscriberView> {
  const email = input.email.trim();
  if (!email || !email.includes("@")) {
    return Promise.reject(
      new ApiError("A valid email is required.", 400, "validation_error", false),
    );
  }
  const createdAt = new Date().toISOString();
  const existing = SUBSCRIBERS.find((s) => s.email?.toLowerCase() === email.toLowerCase());
  if (existing) {
    existing.status = "confirmed";
    if (input.name?.trim() && !existing.name) existing.name = input.name.trim();
    if (input.channels?.length) existing.channels = input.channels;
    return delay(toSubscriberView(existing));
  }
  const sub: MockSubscriber = {
    personId: `person-${SUBSCRIBERS.length + 1}-${Math.floor(Math.random() * 1e6)}`,
    name: input.name?.trim() || undefined,
    email,
    status: "confirmed",
    confirmToken: mockToken(),
    unsubToken: mockToken(),
    deliveryMethod: "email",
    channels: input.channels ?? [],
    createdAt,
  };
  SUBSCRIBERS.push(sub);
  return delay(toSubscriberView(sub));
}

// ---- Building & Code Enforcement Council Report (docs/features/council-report.md) -------------
//
// Mock GET /api/admin/reports/council?scope=&from=&to= over the SAME PERMITS/CASES mock stores the
// consoles already render — so USE_MOCKS dev/test exercises a real-looking figures object. Every
// figure here is computed from PERMITS/CASES (never invented), same discipline as getAnalytics.
// UNLIKE the live handler (which derives in-period transitions from status_change audit events),
// the mock ALWAYS falls back to a labelled current-status count (transitionsFromEvents: false) —
// walking each mock event's free-text body to detect a transition would be brittle test fixture
// coupling for zero behavioral payoff in a mock; this is an honest, documented degrade, exactly the
// path council-report.md's "In-period vs at-period-end" note designs for when a transition can't be
// reliably derived. PII-free by construction: PERMITS/CASES rows only ever expose
// address/applicant-display-name, never a reporter/complainant identity (♻️ mirrors the live path's
// AC9 posture).

function councilReportTitle(scope: CouncilReportScope): string {
  switch (scope) {
    case "permits":
      return "Building — Council Report";
    case "cases":
      return "Code Enforcement — Council Report";
    case "combined":
      return "Building & Code Enforcement — Council Report";
  }
}

function inRange(iso: string, startMs: number, endMs: number): boolean {
  const t = Date.parse(iso);
  return t >= startMs && t < endMs;
}

function buildMockPermitFigures(startMs: number, endMs: number): PermitFigures {
  const newApplicationsList: NewPermitApplication[] = [];
  let approved = 0;
  let issued = 0;
  let denied = 0;
  let openAtPeriodEnd = 0;
  let inspectionsPerformed = 0;
  let inspectionsPassed = 0;
  let inspectionsFailed = 0;
  let feesTrackedTotal = 0;

  for (const p of PERMITS) {
    if (!isPermitTerminal(p.status)) openAtPeriodEnd++;
    if (p.status === "PApproved") approved++;
    if (p.status === "PIssued") issued++;
    if (p.status === "PDenied") denied++;
    if (p.totalFeeAmount > 0) feesTrackedTotal += p.totalFeeAmount;

    for (const e of p.events) {
      if (e.type !== "inspection" || !inRange(e.createdAt, startMs, endMs)) continue;
      inspectionsPerformed++;
      if (/^inspection passed/i.test(e.body)) inspectionsPassed++;
      else if (/^inspection failed/i.test(e.body)) inspectionsFailed++;
    }

    if (!inRange(p.createdAt, startMs, endMs)) continue;
    newApplicationsList.push({
      type: permitTypeLabel(p.permitType),
      address: p.location.address,
      date: p.createdAt,
    });
  }

  newApplicationsList.sort((a, b) => a.date.localeCompare(b.date));

  return {
    newApplications: newApplicationsList.length,
    newApplicationsList,
    approved,
    issued,
    denied,
    transitionsFromEvents: false,
    inspectionsPerformed,
    inspectionsPassed,
    inspectionsFailed,
    openAtPeriodEnd,
    overdueOpen: 0,
    overdueTracked: false,
    feesTrackedTotal,
    feesTrackedLabel: "Fees tracked (not collected online)",
  };
}

function buildMockCaseFigures(startMs: number, endMs: number): CaseFigures {
  const newOpenedList: NewCaseOpened[] = [];
  const overdueList: OverdueCase[] = [];
  let noticesIssued = 0;
  let reinspected = 0;
  let resolved = 0;
  let escalated = 0;
  let closed = 0;
  let openAtPeriodEnd = 0;

  for (const c of CASES) {
    if (!isCaseTerminal(c.status)) openAtPeriodEnd++;
    if (c.status === "CENotice") noticesIssued++;
    if (c.status === "CEReinspected") reinspected++;
    if (c.status === "CEResolved") resolved++;
    if (c.status === "CEEscalated") escalated++;
    if (c.status === "CEClosed") closed++;

    const { overdue, daysOverdue } = caseDeadline(c);
    if (overdue) {
      overdueList.push({
        violationType: violationTypeLabel(c.violationType),
        address: c.ownerAddress ?? "",
        daysOverdue,
      });
    }

    if (!inRange(c.createdAt, startMs, endMs)) continue;
    newOpenedList.push({
      violationType: violationTypeLabel(c.violationType),
      address: c.ownerAddress ?? "",
      date: c.createdAt,
    });
  }

  newOpenedList.sort((a, b) => a.date.localeCompare(b.date));
  overdueList.sort((a, b) => b.daysOverdue - a.daysOverdue);

  return {
    newOpened: newOpenedList.length,
    newOpenedList,
    noticesIssued,
    reinspected,
    resolved,
    escalated,
    closed,
    transitionsFromEvents: false,
    openAtPeriodEnd,
    overdue: overdueList.length,
    overdueList,
  };
}

function buildCouncilReportFigures(
  scope: CouncilReportScope,
  from: string,
  to: string,
): CouncilReportFigures {
  const startMs = Date.parse(`${from}T00:00:00Z`);
  const endMs = Date.parse(`${to}T00:00:00Z`) + 86_400_000;

  const figures: CouncilReportFigures = {
    scope,
    title: councilReportTitle(scope),
    period: { from, to, generatedAt: new Date(now).toISOString() },
  };
  if (scope === "permits" || scope === "combined") {
    figures.permits = buildMockPermitFigures(startMs, endMs);
  }
  if (scope === "cases" || scope === "combined") {
    figures.cases = buildMockCaseFigures(startMs, endMs);
  }
  return figures;
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Same HTML-fragment shape the live handler renders (renderCouncilReportHTML) — semantic headings,
 *  real `<table>` with `<th scope>`, no colour-only status meaning (WCAG 2.1 AA). A hand-rolled
 *  fragment (not a template engine) mirrors the Go string-builder render exactly enough for
 *  view/print/export to exercise real markup in dev-with-mocks. */
function renderCouncilReportHtml(f: CouncilReportFigures, townName: string): string {
  const rows: string[] = [];
  rows.push(`<article class="td-council-report" aria-label="${esc(f.title)}">`);
  rows.push(`<header>`);
  if (townName) rows.push(`<p class="td-council-report__town">${esc(townName)}</p>`);
  rows.push(`<h1>${esc(f.title)}</h1>`);
  rows.push(
    `<p class="td-council-report__period">Period: ${esc(f.period.from)} – ${esc(f.period.to)}</p>`,
  );
  rows.push(`<p class="td-council-report__generated">Generated: ${esc(f.period.generatedAt)}</p>`);
  rows.push(`</header>`);

  rows.push(`<section aria-label="Narrative summary">`);
  rows.push(`<h2>Summary</h2>`);
  rows.push(
    `<div class="td-council-report__narrative"><p>This is a mock narrative for local development — the live report's summary is written by AI from the computed figures below and never invents a number.</p></div>`,
  );
  rows.push(`</section>`);

  if (f.permits) {
    const p = f.permits;
    rows.push(`<section aria-label="Permits"><h2>Building — Permits</h2>`);
    rows.push(`<ul class="td-council-report__stats">`);
    rows.push(`<li>New applications received: ${p.newApplications}</li>`);
    rows.push(`<li>Approved (current status, not an in-period count): ${p.approved}</li>`);
    rows.push(`<li>Issued (current status, not an in-period count): ${p.issued}</li>`);
    rows.push(`<li>Denied (current status, not an in-period count): ${p.denied}</li>`);
    rows.push(
      `<li>Inspections performed: ${p.inspectionsPerformed} (pass ${p.inspectionsPassed} / fail ${p.inspectionsFailed})</li>`,
    );
    rows.push(`<li>Open / in-progress at period end: ${p.openAtPeriodEnd}</li>`);
    rows.push(`<li>${esc(p.feesTrackedLabel)}: $${p.feesTrackedTotal}</li>`);
    rows.push(`</ul>`);
    if (p.newApplicationsList.length > 0) {
      rows.push(`<table><caption>New applications received</caption>`);
      rows.push(
        `<thead><tr><th scope="col">Type</th><th scope="col">Address</th><th scope="col">Date</th></tr></thead><tbody>`,
      );
      for (const row of p.newApplicationsList) {
        rows.push(
          `<tr><td>${esc(row.type)}</td><td>${esc(row.address)}</td><td>${esc(row.date)}</td></tr>`,
        );
      }
      rows.push(`</tbody></table>`);
    }
    rows.push(`</section>`);
  }

  if (f.cases) {
    const c = f.cases;
    rows.push(`<section aria-label="Code Enforcement"><h2>Code Enforcement — Cases</h2>`);
    rows.push(`<ul class="td-council-report__stats">`);
    rows.push(`<li>New cases opened: ${c.newOpened}</li>`);
    rows.push(
      `<li>Notices issued (current status, not an in-period count): ${c.noticesIssued}</li>`,
    );
    rows.push(`<li>Re-inspected (current status, not an in-period count): ${c.reinspected}</li>`);
    rows.push(`<li>Resolved (current status, not an in-period count): ${c.resolved}</li>`);
    rows.push(`<li>Escalated (current status, not an in-period count): ${c.escalated}</li>`);
    rows.push(`<li>Closed (current status, not an in-period count): ${c.closed}</li>`);
    rows.push(`<li>Open cases at period end: ${c.openAtPeriodEnd}</li>`);
    rows.push(`<li>Overdue / aging: ${c.overdue}</li>`);
    rows.push(`</ul>`);
    if (c.newOpenedList.length > 0) {
      rows.push(`<table><caption>New cases opened</caption>`);
      rows.push(
        `<thead><tr><th scope="col">Violation type</th><th scope="col">Property address</th><th scope="col">Date</th></tr></thead><tbody>`,
      );
      for (const row of c.newOpenedList) {
        rows.push(
          `<tr><td>${esc(row.violationType)}</td><td>${esc(row.address)}</td><td>${esc(row.date)}</td></tr>`,
        );
      }
      rows.push(`</tbody></table>`);
    }
    if (c.overdueList.length > 0) {
      rows.push(`<table><caption>Overdue / aging cases</caption>`);
      rows.push(
        `<thead><tr><th scope="col">Violation type</th><th scope="col">Property address</th><th scope="col">Days overdue</th></tr></thead><tbody>`,
      );
      for (const row of c.overdueList) {
        rows.push(
          `<tr><td>${esc(row.violationType)}</td><td>${esc(row.address)}</td><td>${row.daysOverdue}</td></tr>`,
        );
      }
      rows.push(`</tbody></table>`);
    }
    rows.push(`</section>`);
  }

  rows.push(`</article>`);
  return rows.join("");
}

/** Mock GET /api/admin/reports/council?scope=&from=&to= — returns BOTH the rendered HTML and the
 *  structured figures (the agenda seam), exactly mirroring the live handler's response shape. */
export function getCouncilReport(
  scope: CouncilReportScope,
  from: string,
  to: string,
): Promise<CouncilReportResult> {
  const figures = buildCouncilReportFigures(scope, from, to);
  const html = renderCouncilReportHtml(figures, SETTINGS.townName);
  return delay({ html, figures }, 200);
}

/** Mock PDF export — no real gofpdf in the browser, so this mints a tiny valid PDF-shaped Blob
 *  (real MIME type, so the browser's download/open behavior is exercised end to end) rather than
 *  faking a byte-identical render; the figures are the source of truth either way. */
export function getCouncilReportPdf(
  scope: CouncilReportScope,
  from: string,
  to: string,
): Promise<Blob> {
  const figures = buildCouncilReportFigures(scope, from, to);
  const text = `%PDF-1.4\n% Mock council report PDF\n% ${figures.title} (${figures.period.from} - ${figures.period.to})\n`;
  return delay(new Blob([text], { type: "application/pdf" }), 200);
}

// ---- Council Agenda + Packet (agenda-packet.md, Phase 3/3b/4) ---------------------------------
//
// Meeting is its OWN top-level aggregate (not a Request) — a fully separate in-memory store, mirrored
// 1:1 against the live wire shapes (types.ts's MeetingView/AgendaItemView/... interfaces).

import type {
  MeetingView,
  MeetingsListPage,
  AgendaItemView,
  MeetingAttachmentView,
  MeetingAttachmentAccessible,
  ContributionView,
  MinutesView,
  MeetingBundle,
  CreateMeetingInput,
  UpdateMeetingInput,
  PublishMeetingInput,
  AgendaItemInput,
  CreateAgendaItemInput,
  ReorderAgendaItemPosition,
  AttachMeetingFileInput,
  AttachMeetingLinkInput,
  UpsertMinutesInput,
  AssignContributionInput,
  SaveContributionInput,
  DraftAgendaSection,
  SummarizeAgendaItemResult,
  SummarizeAttachmentRef,
  PublicMeetingView,
  PublicMeetingDocLink,
  PublicMeetingDetail,
  PublicMinutesView,
  PublicAgendaItemView,
  PublicConcernPin,
  PublicConcernsFilter,
  PublicConcernsView,
  StaffConcernPin,
  StaffConcernsFilter,
  StaffConcernsView,
  MyReportSlot,
  ContributorContributionView,
  ContributorMeetingView,
  ContributorAgendaItemView,
  TranscriptJobState,
  TranscriptJobView,
  RecordingUploadPresign,
  TranscribeMeetingInput,
  ExportPacketInput,
  ExportPacketResult,
  YouTubeStatusView,
  YouTubeAuthorizeView,
  YouTubeTranscriptPullResult,
  MeetingTemplateView,
  MeetingTemplateSectionView,
  CreateMeetingTemplateInput,
  UpdateMeetingTemplateInput,
  MaterializeTemplateInput,
  SaveAsTemplateInput,
} from "./types.js";
import { CAN_TRANSITION_CONTRIBUTION } from "./types.js";

const genId = (prefix: string): string => `${prefix}-${cryptoRandomToken().slice(0, 8)}`;

let MEETINGS: MeetingView[] = [
  {
    id: "mtg-2026-08",
    tenantId: "linnvalley",
    body: "City Council",
    title: "Regular Meeting",
    meetingDate: "2026-08-25",
    startTime: "18:30",
    location: "Linn Valley City Hall, 24435 Melody Ln",
    status: "published",
    agendaState: "tentative",
    publishedAt: daysAgo(6),
    createdBy: "u-mayor",
    createdAt: daysAgo(10),
    updatedAt: daysAgo(6),
  },
  {
    id: "mtg-2026-09",
    tenantId: "linnvalley",
    body: "City Council",
    title: "Regular Meeting",
    meetingDate: "2026-09-22",
    startTime: "18:30",
    location: "Linn Valley City Hall, 24435 Melody Ln",
    status: "draft",
    createdBy: "u-mayor",
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1),
  },
  // A DEDICATED published+TENTATIVE meeting for THE RULE's "editable while tentative" builder
  // tests (header edit / add item) — kept separate from mtg-2026-08, which an earlier "mark final"
  // test mutates to agendaState "final" in the shared module-scoped mock store.
  {
    id: "mtg-2026-12",
    tenantId: "linnvalley",
    body: "City Council",
    title: "Regular Meeting",
    meetingDate: "2026-12-15",
    startTime: "18:30",
    location: "Linn Valley City Hall, 24435 Melody Ln",
    status: "published",
    agendaState: "tentative",
    publishedAt: daysAgo(3),
    createdBy: "u-mayor",
    createdAt: daysAgo(5),
    updatedAt: daysAgo(3),
  },
  {
    id: "mtg-2026-07",
    tenantId: "linnvalley",
    body: "City Council",
    title: "Regular Meeting",
    meetingDate: "2026-07-28",
    startTime: "18:30",
    location: "Linn Valley City Hall, 24435 Melody Ln",
    status: "archived",
    agendaState: "final",
    publishedAt: daysAgo(40),
    createdBy: "u-mayor",
    createdAt: daysAgo(44),
    updatedAt: daysAgo(20),
  },
  // A DEDICATED published+FINAL meeting (agenda-packet.md §10A) — builder READ-ONLY tests use this
  // one (not mtg-2026-08, which is published+tentative and therefore EDITABLE under
  // Meeting.AgendaEditable()) so the "final locks the builder" assertion has a fixture that doesn't
  // collide with mtg-2026-08's own tentative-editing tests. NEVER mutated by a test (read-only
  // assertions only) — mtg-2026-05 below is the dedicated one for the "mark tentative to edit"
  // reopen flow, so a mutating test never invalidates this one's static "still final" assumption.
  {
    id: "mtg-2026-06",
    tenantId: "linnvalley",
    body: "City Council",
    title: "Regular Meeting",
    meetingDate: "2026-06-23",
    startTime: "18:30",
    location: "Linn Valley City Hall, 24435 Melody Ln",
    status: "published",
    agendaState: "final",
    publishedAt: daysAgo(20),
    createdBy: "u-mayor",
    createdAt: daysAgo(24),
    updatedAt: daysAgo(20),
  },
  // A SECOND dedicated published+FINAL meeting, reserved for the "Mark tentative to edit" reopen
  // flow test (mutates status in the shared module-scoped mock store) — kept separate from
  // mtg-2026-06 so that one stays a stable, never-mutated "still final" read-only fixture.
  {
    id: "mtg-2026-05",
    tenantId: "linnvalley",
    body: "City Council",
    title: "Regular Meeting",
    meetingDate: "2026-05-19",
    startTime: "18:30",
    location: "Linn Valley City Hall, 24435 Melody Ln",
    status: "published",
    agendaState: "final",
    publishedAt: daysAgo(50),
    createdBy: "u-mayor",
    createdAt: daysAgo(54),
    updatedAt: daysAgo(50),
  },
  // A DEDICATED draft meeting for attach-by-upload/link + replace builder tests — kept separate from
  // mtg-2026-09 (which other builder tests publish/mutate) so attachment tests never race a
  // status/agenda-item change made by a test earlier in the same file (module-scoped mock store).
  {
    id: "mtg-2026-10",
    tenantId: "linnvalley",
    body: "City Council",
    title: "Regular Meeting",
    meetingDate: "2026-10-27",
    startTime: "18:30",
    location: "Linn Valley City Hall, 24435 Melody Ln",
    status: "draft",
    createdBy: "u-mayor",
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1),
  },
  // A DEDICATED draft meeting for multi-file-upload + "Supporting documents" builder tests — kept
  // separate from mtg-2026-09/10 so those tests' own mutations never race a batch-attach in the same
  // run (module-scoped mock store).
  {
    id: "mtg-2026-11",
    tenantId: "linnvalley",
    body: "City Council",
    title: "Regular Meeting",
    meetingDate: "2026-11-24",
    startTime: "18:30",
    location: "Linn Valley City Hall, 24435 Melody Ln",
    status: "draft",
    createdBy: "u-mayor",
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1),
  },
  // A THIRD dedicated published+FINAL meeting — read-only ContributionSection assertion fixture.
  // Never mutated by any test (mtg-2026-06 is the "still final" read-only fixture for other
  // assertions, but its OWN dedicated reopen test at module scope doesn't mutate it; mtg-2026-05 is
  // the one that gets marked tentative). Kept fully separate anyway so a future reopen test added
  // against mtg-2026-06 can never flip this contribution fixture's read-only assumption.
  {
    id: "mtg-2026-04",
    tenantId: "linnvalley",
    body: "City Council",
    title: "Regular Meeting",
    meetingDate: "2026-04-21",
    startTime: "18:30",
    location: "Linn Valley City Hall, 24435 Melody Ln",
    status: "published",
    agendaState: "final",
    publishedAt: daysAgo(60),
    createdBy: "u-mayor",
    createdAt: daysAgo(64),
    updatedAt: daysAgo(60),
  },
];

let AGENDA_ITEMS: AgendaItemView[] = [
  {
    id: "item-2026-12-1",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-12",
    order: 0,
    title: "Call to Order",
    narrativeHtml: "<p>Roll call and call to order.</p>",
    createdAt: daysAgo(5),
    updatedAt: daysAgo(3),
  },
  {
    id: "item-2026-05-1",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-05",
    order: 0,
    title: "Call to Order",
    narrativeHtml: "<p>Roll call and call to order.</p>",
    createdAt: daysAgo(54),
    updatedAt: daysAgo(50),
  },
  {
    id: "item-2026-06-1",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-06",
    order: 0,
    title: "Call to Order",
    narrativeHtml: "<p>Roll call and call to order.</p>",
    createdAt: daysAgo(24),
    updatedAt: daysAgo(20),
  },
  {
    id: "item-2026-08-1",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-08",
    order: 0,
    title: "Call to Order",
    narrativeHtml: "<p>Roll call and call to order.</p>",
    createdAt: daysAgo(10),
    updatedAt: daysAgo(6),
  },
  {
    id: "item-2026-08-2",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-08",
    order: 1,
    title: "Approval of Minutes — July 28 Regular Meeting",
    narrativeHtml: "<p>Consider approval of the minutes from the July 28 regular meeting.</p>",
    outcome: "Approved",
    vote: { ayes: 5, nays: 0, abstain: 0 },
    createdAt: daysAgo(10),
    updatedAt: daysAgo(6),
  },
  {
    id: "item-2026-08-3",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-08",
    order: 2,
    title: "Public Works — Q3 street maintenance report",
    narrativeHtml: "<p>Update on chip-seal scheduling for the fall maintenance window.</p>",
    presenter: "Public Works",
    createdAt: daysAgo(9),
    updatedAt: daysAgo(6),
  },
  {
    id: "item-2026-08-4",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-08",
    order: 3,
    title: "Ordinance 456 — Nuisance abatement fee schedule",
    narrativeHtml: "<p>Second reading of the updated nuisance abatement fee schedule.</p>",
    outcome: "Passed Ordinance 456",
    vote: { ayes: 4, nays: 1, abstain: 0, detail: "Councilmember Diaz voting nay." },
    createdAt: daysAgo(9),
    updatedAt: daysAgo(6),
  },
  // Council Agenda 10A nested sub-items: "Old Business" is a top-level SECTION with two first-class
  // sub-items — exercises the public nested render (§10A) + the "Actions taken" per-section recap.
  {
    id: "item-2026-08-old-business",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-08",
    order: 4,
    title: "Old Business",
    narrativeHtml: "<p>Carry-over items tabled from prior meetings.</p>",
    createdAt: daysAgo(9),
    updatedAt: daysAgo(6),
  },
  {
    id: "item-2026-08-old-business-1",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-08",
    order: 0,
    parentItemId: "item-2026-08-old-business",
    title: "Tabled: Sidewalk replacement bid — Melody Ln",
    narrativeHtml: "<p>Revisiting the sidewalk replacement bid tabled at the July meeting.</p>",
    outcome: "Tabled again — pending updated bid",
    createdAt: daysAgo(9),
    updatedAt: daysAgo(6),
  },
  {
    id: "item-2026-08-old-business-2",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-08",
    order: 1,
    parentItemId: "item-2026-08-old-business",
    title: "Park pavilion roof repair — final vote",
    narrativeHtml: "<p>Final vote on the park pavilion roof repair contract.</p>",
    outcome: "Approved",
    vote: { ayes: 5, nays: 0, abstain: 0 },
    createdAt: daysAgo(9),
    updatedAt: daysAgo(6),
  },
  {
    id: "item-2026-08-5",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-08",
    order: 5,
    title: "Adjournment",
    narrativeHtml: "<p>Motion to adjourn.</p>",
    outcome: "Approved",
    vote: { ayes: 5, nays: 0, abstain: 0 },
    createdAt: daysAgo(9),
    updatedAt: daysAgo(6),
  },
  {
    id: "item-2026-09-1",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-09",
    order: 0,
    title: "Public Works — Q4 street maintenance report",
    narrativeHtml: "",
    presenter: "Public Works",
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1),
  },
  // A second top-level item on the draft meeting so builder tests can indent it under item-2026-09-1
  // (indent nests under the item immediately above it).
  {
    id: "item-2026-09-2",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-09",
    order: 1,
    title: "Old Business",
    narrativeHtml: "",
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1),
  },
  // A THIRD top-level item that already has a sub-item — an already-nested DRAFT section, so
  // outdent/2-level-cap/delete-non-empty-section builder tests don't need a read-only (published)
  // meeting's items.
  {
    id: "item-2026-09-3",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-09",
    order: 2,
    title: "New Business",
    narrativeHtml: "",
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1),
  },
  {
    id: "item-2026-09-3-1",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-09",
    order: 0,
    parentItemId: "item-2026-09-3",
    title: "Discuss winter salt supply contract",
    narrativeHtml: "",
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1),
  },
  // A childless TOP-LEVEL item that renders right AFTER the New Business section's sub-item — so
  // indenting it must walk UP past that sub-item to nest under "New Business" (the exact case where
  // the row immediately above is a sub-item, not a section — Lister, 2026-08-17).
  {
    id: "item-2026-09-5",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-09",
    order: 3,
    title: "City Hall discussion",
    narrativeHtml: "",
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1),
  },
  // A FOURTH, separate already-nested section+sub-item pair — dedicated to the READ-ONLY 2-level-cap
  // assertion test, kept apart from item-2026-09-3/-3-1 (which the outdent test mutates) so builder
  // tests in the same describe block don't leak state into each other via the shared mock store.
  {
    id: "item-2026-09-4",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-09",
    order: 3,
    title: "Committee Reports",
    narrativeHtml: "",
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1),
  },
  {
    id: "item-2026-09-4-1",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-09",
    order: 0,
    parentItemId: "item-2026-09-4",
    title: "Parks committee update",
    narrativeHtml: "",
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1),
  },
  // mtg-2026-10's single item — dedicated to attach-by-upload/link + replace builder tests (see
  // mtg-2026-10's own doc comment above).
  {
    id: "item-2026-10-1",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-10",
    order: 0,
    title: "Old business",
    narrativeHtml: "",
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1),
  },
  // mtg-2026-11's single item — dedicated to the multi-file-upload + supporting-docs builder tests
  // (see mtg-2026-11's own doc comment above).
  {
    id: "item-2026-11-1",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-11",
    order: 0,
    title: "Water ordinance",
    narrativeHtml: "",
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1),
  },
  // A dedicated top-level item on the draft mtg-2026-09, carrying its own SUBMITTED contribution
  // (contrib-2 below) — kept separate from item-2026-09-1 (whose contrib-1 is "requested", used by
  // the assign-flow + empty-state tests) so the include/exclude transition tests mutate a fixture
  // nobody else reads.
  {
    id: "item-2026-09-6",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-09",
    order: 4,
    title: "Codes — nuisance abatement update",
    narrativeHtml: "",
    presenter: "Codes",
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1),
  },
  // mtg-2026-04's single item — dedicated to the read-only ContributionSection assertion (contrib-3
  // below). mtg-2026-04 is never mutated by any test.
  {
    id: "item-2026-04-1",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-04",
    order: 0,
    title: "Call to Order",
    narrativeHtml: "<p>Roll call and call to order.</p>",
    createdAt: daysAgo(64),
    updatedAt: daysAgo(60),
  },
];

let MEETING_ATTACHMENTS: MeetingAttachmentView[] = [
  {
    id: "matt-1",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-08",
    itemId: "item-2026-08-4",
    s3Key: "linnvalley/meetings/mtg-2026-08/ordinance-456-fee-schedule.pdf",
    isLink: false,
    filename: "ordinance-456-fee-schedule.pdf",
    contentType: "application/pdf",
    description: "Full text of the proposed nuisance abatement fee schedule (Ordinance 456).",
    accessible: "not_verified",
    visibility: "public",
    createdAt: daysAgo(9),
  },
  {
    id: "matt-2",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-08",
    itemId: "item-2026-08-4",
    linkUrl: "https://example.sharepoint.com/sites/linnvalley/ordinance-456-exhibits",
    isLink: true,
    filename: "Ordinance 456 exhibits (SharePoint)",
    description: "Supporting exhibits hosted on the city's SharePoint site.",
    accessible: "not_verified",
    visibility: "public",
    createdAt: daysAgo(9),
  },
  // A SUPPORTING doc on a DIFFERENT item (mtg-2026-08, item-2026-08-3 — has no other seeded
  // attachments) than matt-1/matt-2's INLINE pair (item-2026-08-4) — dedicated to the public
  // "Supporting documents" collapsed-expander tests, kept apart so its ADA-caveat text can't
  // collide with the existing inline-attachment assertions on item-2026-08-4.
  {
    id: "matt-supporting-1",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-08",
    itemId: "item-2026-08-3",
    s3Key: "linnvalley/meetings/mtg-2026-08/ordinance-456-exhibit-c.pdf",
    isLink: false,
    filename: "ordinance-456-exhibit-c.pdf",
    contentType: "application/pdf",
    description: "Exhibit C — supporting technical memo, water ordinance.",
    accessible: "not_verified",
    visibility: "public",
    createdAt: daysAgo(9),
    supporting: true,
  },
  // mtg-2026-10's seeded attachment — dedicated to the Replace builder test (see mtg-2026-10's own
  // doc comment above), so that test doesn't depend on chaining a prior attach in the same run.
  {
    id: "matt-3",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-10",
    itemId: "item-2026-10-1",
    s3Key: "linnvalley/meetings/mtg-2026-10/first.pdf",
    isLink: false,
    filename: "first.pdf",
    contentType: "application/pdf",
    description: "Version one.",
    accessible: "not_verified",
    visibility: "public",
    createdAt: daysAgo(1),
  },
  // mtg-2026-11's seeded attachment — dedicated to the Replace-flips-supporting builder test, kept
  // separate from the meeting's own multi-file-upload tests (which append new attachments) so this
  // test's "find the ONE existing row" query never collides with those.
  {
    id: "matt-4",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-11",
    itemId: "item-2026-11-1",
    s3Key: "linnvalley/meetings/mtg-2026-11/inline-original.pdf",
    isLink: false,
    filename: "inline-original.pdf",
    contentType: "application/pdf",
    description: "Starts inline.",
    accessible: "not_verified",
    visibility: "public",
    createdAt: daysAgo(1),
  },
  // ---- Meeting-level (itemId: "") typed doc links — feed the public LIST's `documents` array
  // (meetings-public-relaunch.md §1/§5), NOT any agenda item's Attachments list. mtg-2026-08
  // (upcoming) seeds Agenda + Packet only (a future meeting rarely has minutes/recap/video yet);
  // mtg-2026-07 (past) seeds all 5 types so the "1 through 5 present" + fixed-order rendering has
  // real coverage in mock mode.
  {
    id: "matt-doc-08-agenda",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-08",
    itemId: "",
    linkUrl: "https://example.com/linnvalley/agendas/2026-08-25-agenda.pdf",
    isLink: true,
    filename: "Agenda",
    description: "Agenda",
    accessible: "not_verified",
    visibility: "public",
    createdAt: daysAgo(9),
  },
  {
    id: "matt-doc-08-packet",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-08",
    itemId: "",
    linkUrl: "https://example.com/linnvalley/agendas/2026-08-25-packet.pdf",
    isLink: true,
    filename: "Packet",
    description: "Packet",
    accessible: "not_verified",
    visibility: "public",
    createdAt: daysAgo(9),
  },
  {
    id: "matt-doc-07-agenda",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-07",
    itemId: "",
    linkUrl: "https://example.com/linnvalley/agendas/2026-07-28-agenda.pdf",
    isLink: true,
    filename: "Agenda",
    description: "Agenda",
    accessible: "not_verified",
    visibility: "public",
    createdAt: daysAgo(43),
  },
  {
    id: "matt-doc-07-packet",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-07",
    itemId: "",
    linkUrl: "https://example.com/linnvalley/agendas/2026-07-28-packet.pdf",
    isLink: true,
    filename: "Packet",
    description: "Packet",
    accessible: "not_verified",
    visibility: "public",
    createdAt: daysAgo(43),
  },
  {
    id: "matt-doc-07-minutes",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-07",
    itemId: "",
    linkUrl: "https://example.com/linnvalley/agendas/2026-07-28-minutes.pdf",
    isLink: true,
    filename: "Minutes",
    description: "Minutes",
    accessible: "not_verified",
    visibility: "public",
    createdAt: daysAgo(20),
  },
  {
    id: "matt-doc-07-recap",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-07",
    itemId: "",
    linkUrl: "https://example.com/linnvalley/agendas/2026-07-28-recap",
    isLink: true,
    filename: "Recap",
    description: "Recap",
    accessible: "not_verified",
    visibility: "public",
    createdAt: daysAgo(20),
  },
  {
    id: "matt-doc-07-video",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-07",
    itemId: "",
    linkUrl: "https://www.youtube.com/watch?v=mock-2026-07-28",
    isLink: true,
    filename: "Watch",
    description: "Video",
    accessible: "not_verified",
    visibility: "public",
    createdAt: daysAgo(20),
  },
];

let CONTRIBUTIONS: ContributionView[] = [
  {
    id: "contrib-1",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-09",
    itemId: "item-2026-09-1",
    department: "Public Works",
    assigneeId: "u-contributor",
    state: "requested",
    requestedBy: "u-mayor",
    requestedAt: daysAgo(1),
    updatedAt: daysAgo(1),
  },
  // A dedicated SUBMITTED contribution (with a report already in) on item-2026-09-6 — the
  // include/exclude transition tests mutate this one's state, kept separate from contrib-1 (which
  // stays "requested" with no report for the assign-flow + empty-state tests).
  {
    id: "contrib-2",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-09",
    itemId: "item-2026-09-6",
    department: "Codes",
    state: "submitted",
    reportHtml: "<p>Nuisance abatement cases are down 12% month over month.</p>",
    requestedBy: "u-mayor",
    requestedAt: daysAgo(2),
    submittedAt: daysAgo(1),
    updatedAt: daysAgo(1),
  },
  // A SUBMITTED contribution on a published+FINAL meeting's item — read-only assertion fixture
  // (THE RULE: no Include/Exclude controls once the agenda is locked). mtg-2026-04 is never
  // mutated by any test (unlike mtg-2026-06, which the "Mark tentative to edit" reopen test flips).
  {
    id: "contrib-3",
    tenantId: "linnvalley",
    meetingId: "mtg-2026-04",
    itemId: "item-2026-04-1",
    department: "Public Works",
    state: "submitted",
    reportHtml: "<p>Routine report, already locked in with the final agenda.</p>",
    requestedBy: "u-mayor",
    requestedAt: daysAgo(21),
    submittedAt: daysAgo(20),
    updatedAt: daysAgo(20),
  },
];

let MINUTES: MinutesView[] = [
  {
    meetingId: "mtg-2026-08",
    tenantId: "linnvalley",
    bodyHtml:
      "<p>Council approved the minutes from the July regular meeting, heard a Public Works update on Q3 street maintenance, passed Ordinance 456 (nuisance abatement fee schedule) 4-1, and adjourned.</p>",
    summaryHtml:
      "<p>The Council approved last month's minutes, reviewed street maintenance plans, and passed an update to nuisance abatement fees.</p>",
    published: true,
    recapPublished: true,
    minutesPublished: true,
    adoptionState: "draft",
    createdAt: daysAgo(6),
    updatedAt: daysAgo(6),
  },
  {
    meetingId: "mtg-2026-07",
    tenantId: "linnvalley",
    bodyHtml:
      "<p>Council approved the June minutes, reviewed the annual budget calendar, and heard a report from Codes on summer nuisance-complaint volume.</p>",
    summaryHtml: "<p>Routine business — budget calendar reviewed, no ordinances on the agenda.</p>",
    published: true,
    recapPublished: true,
    minutesPublished: true,
    adoptionState: "adopted",
    adoptedAt: daysAgo(20),
    createdAt: daysAgo(38),
    updatedAt: daysAgo(20),
  },
];

/** Order is WITHIN A SIBLING GROUP (Council Agenda 10A — a top-level item's order is its position
 *  among top-level items; a sub-item's order is its position among its siblings under the SAME
 *  parent). parentItemId "" (or omitted) means the top-level group. */
function nextItemOrder(meetingId: string, parentItemId = ""): number {
  const siblings = AGENDA_ITEMS.filter(
    (i) => i.meetingId === meetingId && (i.parentItemId ?? "") === parentItemId,
  );
  return siblings.length ? Math.max(...siblings.map((i) => i.order)) + 1 : 0;
}

function toContributorMeeting(m: MeetingView): ContributorMeetingView {
  return {
    id: m.id,
    body: m.body,
    title: m.title,
    meetingDate: m.meetingDate,
    startTime: m.startTime,
    location: m.location,
    status: m.status,
  };
}
function toContributorItem(it: AgendaItemView): ContributorAgendaItemView {
  return { id: it.id, order: it.order, title: it.title };
}
function toContributorContribution(c: ContributionView): ContributorContributionView {
  return {
    id: c.id,
    meetingId: c.meetingId,
    itemId: c.itemId,
    department: c.department,
    state: c.state,
    reportHtml: c.reportHtml,
    requestedAt: c.requestedAt,
    submittedAt: c.submittedAt,
    updatedAt: c.updatedAt,
  };
}

function isAssignedToCaller(caller: StaffUserView, c: ContributionView): boolean {
  if (caller.role === "admin") return true;
  if (caller.status !== "active") return false;
  if (c.assigneeId) return c.assigneeId === caller.id;
  return Boolean(caller.department) && caller.department === c.department;
}

function isUpcomingMeeting(m: MeetingView): boolean {
  if (m.status === "archived") return false;
  const today = new Date(now).toISOString().slice(0, 10);
  return !m.meetingDate || m.meetingDate >= today;
}

// ---- Staff admin (adminAuth) ----------------------------------------------------------------

/** Mirrors handler.Meetings' pagination contract: newest-meeting-first (then by start time
 *  descending, then id, matching MeetingsListPage's own client-side sort — server order already
 *  agrees), sliced to one page. `cursor` is this mock's own opaque offset-encoding (never parsed by
 *  the caller — same "round-trip an opaque string" contract as the live cursor). */
function sortMeetingsNewestFirst(list: MeetingView[]): MeetingView[] {
  return [...list].sort((a, b) => {
    if (a.meetingDate !== b.meetingDate) return b.meetingDate.localeCompare(a.meetingDate);
    const at = a.startTime || "";
    const bt = b.startTime || "";
    if (at !== bt) return bt.localeCompare(at);
    return b.id.localeCompare(a.id);
  });
}

const MOCK_CURSOR_PREFIX = "mockmtg1:";

export function listMeetings(cursor?: string, pageSize = 25): Promise<MeetingsListPage> {
  let offset = 0;
  if (cursor) {
    if (!cursor.startsWith(MOCK_CURSOR_PREFIX)) {
      return Promise.reject(new ApiError("invalid cursor", 400, "invalid_cursor", false));
    }
    offset = Number(cursor.slice(MOCK_CURSOR_PREFIX.length)) || 0;
  }
  const all = sortMeetingsNewestFirst(MEETINGS);
  const end = Math.min(offset + pageSize, all.length);
  const page = all.slice(offset, end);
  const nextCursor = end < all.length ? `${MOCK_CURSOR_PREFIX}${end}` : "";
  return delay({ meetings: clone(page), nextCursor });
}

export function createMeeting(input: CreateMeetingInput): Promise<MeetingView> {
  const nowIso = new Date(now).toISOString();
  const m: MeetingView = {
    id: genId("mtg"),
    tenantId: "linnvalley",
    body: input.body.trim(),
    title: input.title?.trim() || undefined,
    meetingDate: input.meetingDate,
    startTime: input.startTime?.trim() || undefined,
    location: input.location.trim(),
    status: "draft",
    createdBy: CURRENT_USER.id,
    createdAt: nowIso,
    updatedAt: nowIso,
  };
  MEETINGS.push(m);
  return delay(clone(m));
}

export function getMeetingBundle(id: string): Promise<MeetingBundle | null> {
  const m = MEETINGS.find((x) => x.id === id);
  if (!m) return delay(null);
  return delay(
    clone({
      meeting: m,
      items: AGENDA_ITEMS.filter((i) => i.meetingId === id).sort((a, b) => a.order - b.order),
      attachments: MEETING_ATTACHMENTS.filter((a) => a.meetingId === id),
      contributions: CONTRIBUTIONS.filter((c) => c.meetingId === id),
      minutes: MINUTES.find((mm) => mm.meetingId === id) ?? null,
      events: [],
    }),
  );
}

/** Mirrors shared.Meeting.AgendaEditable() (services/api/internal/shared/schemas.go) — the SINGLE
 *  source of truth for whether a meeting's agenda + header may be mutated right now: DRAFT, or
 *  PUBLISHED + agendaState TENTATIVE. Read-only when PUBLISHED + FINAL (reopen via "mark tentative")
 *  or ARCHIVED. */
function agendaEditable(m: MeetingView): boolean {
  if (m.status === "draft") return true;
  if (m.status === "published") return (m.agendaState || "tentative") === "tentative";
  return false;
}

/** Mirrors handler.agendaLockedResponse — the ONE mock error for every agenda-mutating endpoint
 *  when agendaEditable() is false: `agenda_final` (published + final — reopen by marking tentative)
 *  or `meeting_archived` (archived — never editable again). */
function agendaLockedError(m: MeetingView): ApiError {
  if (m.status === "archived") {
    return new ApiError(
      "This meeting is archived and can't be edited.",
      409,
      "meeting_archived",
      false,
    );
  }
  return new ApiError(
    "This agenda is marked final. Mark it tentative to edit.",
    409,
    "agenda_final",
    false,
  );
}

export function updateMeeting(id: string, input: UpdateMeetingInput): Promise<MeetingView> {
  const m = MEETINGS.find((x) => x.id === id);
  if (!m) return Promise.reject(new ApiError("Meeting not found.", 404, "not_found", false));
  if (!agendaEditable(m)) {
    return Promise.reject(agendaLockedError(m));
  }
  m.body = input.body.trim();
  m.title = input.title?.trim() || undefined;
  m.meetingDate = input.meetingDate;
  m.startTime = input.startTime?.trim() || undefined;
  m.location = input.location.trim();
  m.updatedAt = new Date(now).toISOString();
  return delay(clone(m));
}

export function deleteMeeting(id: string): Promise<void> {
  const m = MEETINGS.find((x) => x.id === id);
  if (!m) return Promise.reject(new ApiError("Meeting not found.", 404, "not_found", false));
  if (m.status !== "draft") {
    return Promise.reject(
      new ApiError("Only a draft meeting can be deleted.", 409, "not_deletable", false),
    );
  }
  MEETINGS = MEETINGS.filter((x) => x.id !== id);
  AGENDA_ITEMS = AGENDA_ITEMS.filter((x) => x.meetingId !== id);
  MEETING_ATTACHMENTS = MEETING_ATTACHMENTS.filter((x) => x.meetingId !== id);
  CONTRIBUTIONS = CONTRIBUTIONS.filter((x) => x.meetingId !== id);
  MINUTES = MINUTES.filter((x) => x.meetingId !== id);
  return delay(undefined);
}

/** POST /admin/meetings/{id}/publish (agenda-packet.md §10A tentative/final) — on a DRAFT,
 *  publishes it and sets agendaState (default "tentative" when input/agendaState is omitted); on an
 *  ALREADY-PUBLISHED meeting, just updates agendaState in place (the "mark final" path — stays
 *  published, mirrors the live handler's two-mode contract). */
export function publishMeeting(id: string, input?: PublishMeetingInput): Promise<MeetingView> {
  const m = MEETINGS.find((x) => x.id === id);
  if (!m) return Promise.reject(new ApiError("Meeting not found.", 404, "not_found", false));
  const nowIso = new Date(now).toISOString();
  if (m.status === "draft") {
    m.status = "published";
    m.agendaState = input?.agendaState ?? "tentative";
    if (!m.publishedAt) m.publishedAt = nowIso;
    m.updatedAt = nowIso;
    return delay(clone(m));
  }
  if (m.status === "published") {
    if (input?.agendaState) m.agendaState = input.agendaState;
    m.updatedAt = nowIso;
    return delay(clone(m));
  }
  return Promise.reject(new ApiError("Meeting isn't a draft.", 409, "not_draft", false));
}

export function unpublishMeeting(id: string): Promise<MeetingView> {
  const m = MEETINGS.find((x) => x.id === id);
  if (!m) return Promise.reject(new ApiError("Meeting not found.", 404, "not_found", false));
  if (m.status !== "published") {
    return Promise.reject(new ApiError("Meeting isn't published.", 409, "not_published", false));
  }
  m.status = "draft";
  m.updatedAt = new Date(now).toISOString();
  return delay(clone(m));
}

export function addAgendaItem(
  meetingId: string,
  input: CreateAgendaItemInput,
): Promise<AgendaItemView> {
  const m = MEETINGS.find((x) => x.id === meetingId);
  if (!m) return Promise.reject(new ApiError("Meeting not found.", 404, "not_found", false));
  if (!agendaEditable(m)) return Promise.reject(agendaLockedError(m));
  const parentItemId = input.parentItemId?.trim() || "";
  if (parentItemId) {
    const parent = AGENDA_ITEMS.find((x) => x.meetingId === meetingId && x.id === parentItemId);
    if (!parent) {
      return Promise.reject(
        new ApiError(
          "parentItemId does not name an existing agenda item.",
          400,
          "validation_error",
          false,
        ),
      );
    }
    if (parent.parentItemId) {
      return Promise.reject(
        new ApiError(
          "parentItemId must name a top-level agenda item, not a sub-item.",
          400,
          "validation_error",
          false,
        ),
      );
    }
  }
  const nowIso = new Date(now).toISOString();
  const it: AgendaItemView = {
    id: genId("item"),
    tenantId: "linnvalley",
    meetingId,
    order: nextItemOrder(meetingId, parentItemId),
    parentItemId: parentItemId || undefined,
    title: input.title.trim(),
    narrativeHtml: input.narrativeHtml || undefined,
    presenter: input.presenter?.trim() || undefined,
    outcome: input.outcome?.trim() || undefined,
    vote: input.vote ?? undefined,
    timeboxMinutes: input.timeboxMinutes || undefined,
    createdAt: nowIso,
    updatedAt: nowIso,
  };
  AGENDA_ITEMS.push(it);
  return delay(clone(it));
}

export function updateAgendaItem(
  meetingId: string,
  itemId: string,
  input: AgendaItemInput,
): Promise<AgendaItemView> {
  const it = AGENDA_ITEMS.find((x) => x.meetingId === meetingId && x.id === itemId);
  if (!it) return Promise.reject(new ApiError("Agenda item not found.", 404, "not_found", false));
  const m = MEETINGS.find((x) => x.id === meetingId);
  if (m && !agendaEditable(m)) return Promise.reject(agendaLockedError(m));
  it.title = input.title.trim();
  it.narrativeHtml = input.narrativeHtml || undefined;
  it.presenter = input.presenter?.trim() || undefined;
  it.outcome = input.outcome?.trim() || undefined;
  it.vote = input.vote ?? undefined;
  it.timeboxMinutes = input.timeboxMinutes || undefined;
  it.updatedAt = new Date(now).toISOString();
  return delay(clone(it));
}

/** A non-empty SECTION (a top-level item with ≥1 sub-item still pointing at it) can't be deleted —
 *  409 `section_not_empty` (Council Agenda 10A), mirroring meetings.go's AgendaItemByID DELETE
 *  guard. A sub-item has no children by construction so this only ever fires for a top-level item. */
export function deleteAgendaItem(meetingId: string, itemId: string): Promise<void> {
  const m = MEETINGS.find((x) => x.id === meetingId);
  if (m && !agendaEditable(m)) return Promise.reject(agendaLockedError(m));
  const hasChildren = AGENDA_ITEMS.some(
    (x) => x.meetingId === meetingId && x.parentItemId === itemId,
  );
  if (hasChildren) {
    return Promise.reject(
      new ApiError(
        "remove or move this item's sub-items before deleting it",
        409,
        "section_not_empty",
        false,
      ),
    );
  }
  const before = AGENDA_ITEMS.length;
  AGENDA_ITEMS = AGENDA_ITEMS.filter((x) => !(x.meetingId === meetingId && x.id === itemId));
  if (AGENDA_ITEMS.length === before) {
    return Promise.reject(new ApiError("Agenda item not found.", 404, "not_found", false));
  }
  MEETING_ATTACHMENTS = MEETING_ATTACHMENTS.filter(
    (x) => !(x.meetingId === meetingId && x.itemId === itemId),
  );
  CONTRIBUTIONS = CONTRIBUTIONS.filter((x) => !(x.meetingId === meetingId && x.itemId === itemId));
  return delay(undefined);
}

/** The staff drag/keyboard TREE reorder + reparent (Council Agenda 10A) — mirrors
 *  ReorderAgendaItemsHandler: body carries the FULL new nested ordering (top-level items at the
 *  array root, sub-items nested under `children`); every current agenda item must be named exactly
 *  once across the whole payload. Attribute-only update — parentItemId/order change, id/key don't. */
export function reorderAgendaItems(
  meetingId: string,
  items: ReorderAgendaItemPosition[],
): Promise<void> {
  const m = MEETINGS.find((x) => x.id === meetingId);
  if (m && !agendaEditable(m)) return Promise.reject(agendaLockedError(m));
  const current = AGENDA_ITEMS.filter((x) => x.meetingId === meetingId);
  const positions = new Map<string, { parentItemId: string; order: number }>();
  for (const top of items) {
    positions.set(top.id, { parentItemId: "", order: top.order });
    for (const child of top.children ?? []) {
      positions.set(child.id, { parentItemId: top.id, order: child.order });
    }
  }
  if (positions.size !== current.length || !current.every((it) => positions.has(it.id))) {
    return Promise.reject(
      new ApiError(
        "items must name every current agenda item exactly once.",
        400,
        "validation_error",
        false,
      ),
    );
  }
  const nowIso = new Date(now).toISOString();
  for (const it of current) {
    const pos = positions.get(it.id)!;
    it.parentItemId = pos.parentItemId || undefined;
    it.order = pos.order;
    it.updatedAt = nowIso;
  }
  return delay(undefined);
}

/** POST .../items/{itemId}/attachments — file OR link (exactly one of `s3Key`/`linkUrl`), and
 *  `replaceAttachmentId` updates an EXISTING attachment in place (keeps its position in
 *  MEETING_ATTACHMENTS — never re-pushed to the end of the array) instead of creating a new one. */
export function attachMeetingFile(
  meetingId: string,
  itemId: string,
  input: AttachMeetingFileInput,
): Promise<MeetingAttachmentView> {
  const it = AGENDA_ITEMS.find((x) => x.meetingId === meetingId && x.id === itemId);
  if (!it) return Promise.reject(new ApiError("Agenda item not found.", 404, "not_found", false));
  const attachMeeting = MEETINGS.find((x) => x.id === meetingId);
  if (attachMeeting && !agendaEditable(attachMeeting))
    return Promise.reject(agendaLockedError(attachMeeting));
  if (input.replaceAttachmentId) {
    const existing = MEETING_ATTACHMENTS.find(
      (x) => x.id === input.replaceAttachmentId && x.meetingId === meetingId && x.itemId === itemId,
    );
    if (!existing)
      return Promise.reject(new ApiError("Attachment not found.", 404, "not_found", false));
    existing.s3Key = input.s3Key;
    existing.linkUrl = input.linkUrl;
    existing.isLink = Boolean(input.linkUrl);
    existing.filename = input.filename;
    existing.contentType = input.contentType;
    existing.description = input.description.trim();
    existing.accessible = input.accessible as MeetingAttachmentAccessible;
    existing.supporting = input.supporting;
    return delay(clone(existing));
  }
  const a: MeetingAttachmentView = {
    id: genId("matt"),
    tenantId: "linnvalley",
    meetingId,
    itemId,
    s3Key: input.s3Key,
    linkUrl: input.linkUrl,
    isLink: Boolean(input.linkUrl),
    filename: input.filename,
    contentType: input.contentType,
    description: input.description.trim(),
    accessible: input.accessible as MeetingAttachmentAccessible,
    visibility: "public",
    createdAt: new Date(now).toISOString(),
    supporting: input.supporting,
  };
  MEETING_ATTACHMENTS.push(a);
  return delay(clone(a));
}

/** Mirrors handler.AttachMeetingLink: a manual, MEETING-LEVEL attach (itemId "") — EITHER an
 *  external link (description DERIVED from linkType, store.MeetingLinkDescription's mapping, never
 *  accepted as free text from the client) OR an uploaded file (description falls back to filename,
 *  or "Document" if that's empty too — mirrors the Go handler's own fallback). */
const MOCK_MEETING_LINK_DESCRIPTIONS: Record<string, string> = {
  agenda: "Agenda",
  packet: "Packet",
  minutes: "Minutes",
  recap: "Recap",
  video: "Video",
};

export function attachMeetingLink(
  meetingId: string,
  input: AttachMeetingLinkInput,
): Promise<MeetingAttachmentView> {
  const m = MEETINGS.find((x) => x.id === meetingId);
  if (!m) return Promise.reject(new ApiError("Meeting not found.", 404, "not_found", false));
  if (!agendaEditable(m)) return Promise.reject(agendaLockedError(m));
  const a: MeetingAttachmentView = input.s3Key
    ? {
        id: genId("matt"),
        tenantId: "linnvalley",
        meetingId,
        itemId: "",
        s3Key: input.s3Key,
        isLink: false,
        filename: input.filename,
        contentType: input.contentType,
        description: input.filename?.trim() || "Document",
        accessible: "not_verified",
        visibility: "public",
        createdAt: new Date(now).toISOString(),
      }
    : {
        id: genId("matt"),
        tenantId: "linnvalley",
        meetingId,
        itemId: "",
        linkUrl: input.linkUrl,
        isLink: true,
        description: MOCK_MEETING_LINK_DESCRIPTIONS[input.linkType ?? ""] ?? "Document",
        accessible: "not_verified",
        visibility: "public",
        createdAt: new Date(now).toISOString(),
      };
  MEETING_ATTACHMENTS.push(a);
  return delay(clone(a));
}

export function removeMeetingFile(
  meetingId: string,
  itemId: string,
  attachmentId: string,
): Promise<void> {
  const m = MEETINGS.find((x) => x.id === meetingId);
  if (m && !agendaEditable(m)) return Promise.reject(agendaLockedError(m));
  const i = MEETING_ATTACHMENTS.findIndex(
    (x) => x.id === attachmentId && x.meetingId === meetingId && x.itemId === itemId,
  );
  if (i === -1)
    return Promise.reject(new ApiError("Attachment not found.", 404, "not_found", false));
  MEETING_ATTACHMENTS.splice(i, 1);
  return delay(undefined);
}

export function saveMeetingMinutes(
  meetingId: string,
  input: UpsertMinutesInput,
): Promise<MinutesView> {
  const m = MEETINGS.find((x) => x.id === meetingId);
  if (!m) return Promise.reject(new ApiError("Meeting not found.", 404, "not_found", false));
  const nowIso = new Date(now).toISOString();
  let mins = MINUTES.find((x) => x.meetingId === meetingId);
  if (!mins) {
    mins = {
      meetingId,
      tenantId: "linnvalley",
      published: false,
      recapPublished: false,
      minutesPublished: false,
      createdAt: nowIso,
      updatedAt: nowIso,
    };
    MINUTES.push(mins);
  }
  if (input.bodyHtml) mins.bodyHtml = input.bodyHtml;
  if (input.summaryHtml) mins.summaryHtml = input.summaryHtml;
  // Recap/minutes independent-publish — legacy `publish` maps to BOTH gates (server-mirrored
  // backward-compat, see shared.UpsertMinutesInput's doc comment); one-directional either way.
  if (input.publishRecap || input.publish) mins.recapPublished = true;
  if (input.publishMinutes || input.publish) mins.minutesPublished = true;
  if (input.publish) mins.published = true;
  mins.updatedAt = nowIso;
  return delay(clone(mins));
}

/** POST .../minutes/adopt mock — marks the FORMAL minutes adopted (one-directional). */
export function adoptMeetingMinutes(meetingId: string): Promise<MinutesView> {
  const mins = MINUTES.find((x) => x.meetingId === meetingId);
  if (!mins) return Promise.reject(new ApiError("Minutes not found.", 404, "not_found", false));
  const nowIso = new Date(now).toISOString();
  mins.adoptionState = "adopted";
  if (!mins.adoptedAt) mins.adoptedAt = nowIso;
  mins.updatedAt = nowIso;
  return delay(clone(mins));
}

export function assignContribution(
  meetingId: string,
  itemId: string,
  input: AssignContributionInput,
): Promise<ContributionView> {
  const it = AGENDA_ITEMS.find((x) => x.meetingId === meetingId && x.id === itemId);
  if (!it) return Promise.reject(new ApiError("Agenda item not found.", 404, "not_found", false));
  const nowIso = new Date(now).toISOString();
  const dept = input.department.trim();
  let c = CONTRIBUTIONS.find(
    (x) => x.meetingId === meetingId && x.itemId === itemId && x.department === dept,
  );
  if (c) {
    c.assigneeId = input.assigneeId?.trim() || undefined;
    c.updatedAt = nowIso;
  } else {
    c = {
      id: genId("contrib"),
      tenantId: "linnvalley",
      meetingId,
      itemId,
      department: dept,
      assigneeId: input.assigneeId?.trim() || undefined,
      state: "requested",
      requestedBy: CURRENT_USER.id,
      requestedAt: nowIso,
      updatedAt: nowIso,
    };
    CONTRIBUTIONS.push(c);
  }
  return delay(clone(c));
}

export function saveContributionByClerk(
  meetingId: string,
  itemId: string,
  department: string,
  input: SaveContributionInput,
): Promise<ContributionView> {
  const c = CONTRIBUTIONS.find(
    (x) => x.meetingId === meetingId && x.itemId === itemId && x.department === department,
  );
  if (!c) return Promise.reject(new ApiError("Contribution not found.", 404, "not_found", false));
  if (input.reportHtml) c.reportHtml = input.reportHtml;
  if (input.transitionTo) {
    if (!CAN_TRANSITION_CONTRIBUTION(c.state, input.transitionTo)) {
      return Promise.reject(
        new ApiError(`${c.state} → ${input.transitionTo}`, 409, "invalid_transition", false),
      );
    }
    c.state = input.transitionTo;
    if (input.transitionTo === "submitted") c.submittedAt = new Date(now).toISOString();
  }
  c.updatedAt = new Date(now).toISOString();
  return delay(clone(c));
}

// ---- Meeting Templates (ADR-0021, docs/features/meeting-templates.md) — staff console ----------
//
// A mock, in-memory analog of the four template endpoints (list/create, read/update/delete,
// materialize, save-as-template) — mirrors the live handler's own semantics (full-replace PUT,
// 404-on-missing, materialize mints a fresh DRAFT Meeting + AgendaItem tree + Contribution slots).

let MEETING_TEMPLATES: MeetingTemplateView[] = [];

export function listMeetingTemplates(): Promise<MeetingTemplateView[]> {
  return delay(clone(MEETING_TEMPLATES));
}

export function createMeetingTemplate(
  input: CreateMeetingTemplateInput,
): Promise<MeetingTemplateView> {
  const nowIso = new Date(now).toISOString();
  const t: MeetingTemplateView = {
    id: genId("mtgtpl"),
    tenantId: "linnvalley",
    name: input.name.trim(),
    body: input.body?.trim() || undefined,
    title: input.title?.trim() || undefined,
    location: input.location?.trim() || undefined,
    startTime: input.startTime?.trim() || undefined,
    sections: input.sections ?? [],
    createdBy: CURRENT_USER.id,
    createdAt: nowIso,
    updatedAt: nowIso,
  };
  MEETING_TEMPLATES.push(t);
  return delay(clone(t));
}

export function getMeetingTemplate(templateId: string): Promise<MeetingTemplateView | null> {
  const t = MEETING_TEMPLATES.find((x) => x.id === templateId);
  return delay(t ? clone(t) : null);
}

export function updateMeetingTemplate(
  templateId: string,
  input: UpdateMeetingTemplateInput,
): Promise<MeetingTemplateView> {
  const t = MEETING_TEMPLATES.find((x) => x.id === templateId);
  if (!t) return Promise.reject(new ApiError("Template not found.", 404, "not_found", false));
  t.name = input.name.trim();
  t.body = input.body?.trim() || undefined;
  t.title = input.title?.trim() || undefined;
  t.location = input.location?.trim() || undefined;
  t.startTime = input.startTime?.trim() || undefined;
  t.sections = input.sections ?? [];
  t.updatedAt = new Date(now).toISOString();
  return delay(clone(t));
}

export function deleteMeetingTemplate(templateId: string): Promise<void> {
  const t = MEETING_TEMPLATES.find((x) => x.id === templateId);
  if (!t) return Promise.reject(new ApiError("Template not found.", 404, "not_found", false));
  MEETING_TEMPLATES = MEETING_TEMPLATES.filter((x) => x.id !== templateId);
  return delay(undefined);
}

/** POST .../materialize — mints a fresh DRAFT Meeting + its AgendaItem tree + Contribution slots
 *  (state "requested") from the template's embedded sections, preserving order/nesting. */
export function materializeMeetingTemplate(
  templateId: string,
  input: MaterializeTemplateInput,
): Promise<MeetingView> {
  const t = MEETING_TEMPLATES.find((x) => x.id === templateId);
  if (!t) return Promise.reject(new ApiError("Template not found.", 404, "not_found", false));
  const nowIso = new Date(now).toISOString();
  const m: MeetingView = {
    id: genId("mtg"),
    tenantId: "linnvalley",
    body: t.body || "City Council",
    title: t.title,
    meetingDate: input.meetingDate,
    startTime: input.startTime?.trim() || t.startTime,
    location: input.location?.trim() || t.location || "",
    status: "draft",
    createdBy: CURRENT_USER.id,
    createdAt: nowIso,
    updatedAt: nowIso,
  };
  MEETINGS.push(m);
  t.sections.forEach((section, sIdx) => {
    const sectionId = genId("item");
    AGENDA_ITEMS.push({
      id: sectionId,
      tenantId: "linnvalley",
      meetingId: m.id,
      order: sIdx,
      title: section.title,
      narrativeHtml: section.narrativeHtml,
      presenter: section.presenter,
      timeboxMinutes: section.timeboxMinutes || undefined,
      createdAt: nowIso,
      updatedAt: nowIso,
    });
    (section.departments ?? []).forEach((department) => {
      CONTRIBUTIONS.push({
        id: genId("contrib"),
        tenantId: "linnvalley",
        meetingId: m.id,
        itemId: sectionId,
        department,
        state: "requested",
        requestedBy: CURRENT_USER.id,
        requestedAt: nowIso,
        updatedAt: nowIso,
      });
    });
    (section.items ?? []).forEach((item, iIdx) => {
      AGENDA_ITEMS.push({
        id: genId("item"),
        tenantId: "linnvalley",
        meetingId: m.id,
        order: iIdx,
        parentItemId: sectionId,
        title: item.title,
        narrativeHtml: item.narrativeHtml,
        presenter: item.presenter,
        timeboxMinutes: item.timeboxMinutes || undefined,
        createdAt: nowIso,
        updatedAt: nowIso,
      });
    });
  });
  return delay(clone(m));
}

/** POST /admin/meetings/{id}/save-as-template — projects the meeting's agenda tree (title/
 *  narrative/presenter per node; department keys from each section's existing contribution slots)
 *  into a new template, dropping all instance data (dates/outcomes/votes/attachments/minutes). */
export function saveMeetingAsTemplate(
  meetingId: string,
  input: SaveAsTemplateInput,
): Promise<MeetingTemplateView> {
  const m = MEETINGS.find((x) => x.id === meetingId);
  if (!m) return Promise.reject(new ApiError("Meeting not found.", 404, "not_found", false));
  const items = AGENDA_ITEMS.filter((i) => i.meetingId === meetingId).sort(
    (a, b) => a.order - b.order,
  );
  const nowIso = new Date(now).toISOString();
  const sections: MeetingTemplateSectionView[] = items
    .filter((i) => !i.parentItemId)
    .map((section) => ({
      title: section.title,
      narrativeHtml: section.narrativeHtml,
      presenter: section.presenter,
      timeboxMinutes: section.timeboxMinutes || undefined,
      departments: Array.from(
        new Set(
          CONTRIBUTIONS.filter((c) => c.meetingId === meetingId && c.itemId === section.id).map(
            (c) => c.department,
          ),
        ),
      ),
      items: items
        .filter((i) => i.parentItemId === section.id)
        .map((item) => ({
          title: item.title,
          narrativeHtml: item.narrativeHtml,
          presenter: item.presenter,
          timeboxMinutes: item.timeboxMinutes || undefined,
        })),
    }));
  const t: MeetingTemplateView = {
    id: genId("mtgtpl"),
    tenantId: "linnvalley",
    name: input.name.trim(),
    body: m.body,
    title: m.title,
    location: m.location,
    startTime: m.startTime,
    sections,
    createdBy: CURRENT_USER.id,
    createdAt: nowIso,
    updatedAt: nowIso,
  };
  MEETING_TEMPLATES.push(t);
  return delay(clone(t));
}

// ---- Council Agenda + Packet — 10B recording->minutes/summary pipeline (agenda-packet.md §5.2) --// ---- Council Agenda + Packet — 10B recording->minutes/summary pipeline (agenda-packet.md §5.2) --
// A mock, in-memory analog of the async pipeline: transcribeMeeting advances a per-meeting
// TranscriptJob through queued -> transcribing -> drafting -> ready on a short timer (so the staff
// console's poll loop has real states to exercise offline), and writes Minutes.bodyHtml/summaryHtml
// on "ready" exactly like the real worker would (staged from Minutes.RecordingS3Key, never
// auto-published — mirrors AdminMeetingTranscribe's stageRecordingOnMinutes).

const TRANSCRIPT_JOBS: Record<string, TranscriptJobView> = {};

/** POST .../recording/upload-init — the mock never touches real S3; it fabricates a same-shape
 *  presign + a deterministic s3Key under this tenant's recordings prefix (mirrors
 *  store.PresignRecordingUpload's key convention closely enough for the UI to round-trip it). */
export function presignRecordingUpload(
  meetingId: string,
  filename: string,
  _contentType: string,
): Promise<RecordingUploadPresign> {
  const m = MEETINGS.find((x) => x.id === meetingId);
  if (!m) return Promise.reject(new ApiError("Meeting not found.", 404, "not_found", false));
  const s3Key = `recordings/linnvalley/${genId("rec")}-${filename}`;
  return delay({ uploadUrl: `mock://upload/${s3Key}`, s3Key });
}

/** Full mock recording upload — presign, "PUT" (no-op), return the s3Key, matching
 *  uploadRecordingLive's shape so the builder component works identically against either. */
export async function uploadRecording(
  meetingId: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<string> {
  const presign = await presignRecordingUpload(meetingId, file.name, file.type || "audio/mpeg");
  onProgress?.(100);
  return presign.s3Key;
}

const TRANSCRIBE_STEP_MS = 900;

/** POST .../transcribe — enqueue the pipeline, EITHER from an uploaded recording (`recordingS3Key`,
 *  the fallback path — starts at "queued" and runs the full transcribing -> drafting -> ready
 *  timeline) OR a pasted transcript (`transcript`, the PRIMARY path — no audio to transcribe, so the
 *  job starts straight at "drafting" and flows through the SAME ready/failed states + minutes-review
 *  UI). Mirrors AdminMeetingTranscribe's conflict rules (409 while transcribing/drafting, 409
 *  "already_transcribed" once ready). */
export function transcribeMeeting(
  meetingId: string,
  input: TranscribeMeetingInput,
): Promise<{ state: TranscriptJobState }> {
  const m = MEETINGS.find((x) => x.id === meetingId);
  if (!m) return Promise.reject(new ApiError("Meeting not found.", 404, "not_found", false));
  const existing = TRANSCRIPT_JOBS[meetingId];
  if (existing?.state === "transcribing" || existing?.state === "drafting") {
    return Promise.reject(
      new ApiError(
        "Transcription is already in progress.",
        409,
        "transcription_in_progress",
        false,
      ),
    );
  }
  if (existing?.state === "ready") {
    return Promise.reject(
      new ApiError("This meeting has already been transcribed.", 409, "already_transcribed", false),
    );
  }
  const nowIso = new Date(now).toISOString();
  const isPaste = "transcript" in input;
  const startState: TranscriptJobState = isPaste ? "drafting" : "queued";
  TRANSCRIPT_JOBS[meetingId] = { state: startState, startedAt: nowIso, updatedAt: nowIso };
  runMockTranscribePipeline(meetingId, startState);
  return delay({ state: startState }, 100);
}

function runMockTranscribePipeline(meetingId: string, startState: TranscriptJobState): void {
  const advance = (state: TranscriptJobState, next?: () => void) => {
    window.setTimeout(() => {
      const job = TRANSCRIPT_JOBS[meetingId];
      // A retry/new submission may have reset the job in the meantime — don't resurrect a stale run.
      if (!job) return;
      job.state = state;
      job.updatedAt = new Date(now).toISOString();
      if (state === "ready") {
        const nowIso = new Date(now).toISOString();
        let mins = MINUTES.find((x) => x.meetingId === meetingId);
        if (!mins) {
          mins = {
            meetingId,
            tenantId: "linnvalley",
            published: false,
            recapPublished: false,
            minutesPublished: false,
            createdAt: nowIso,
            updatedAt: nowIso,
          };
          MINUTES.push(mins);
        }
        mins.bodyHtml =
          "<h2>Draft Minutes (AI-transcribed)</h2><p>Roll call: all members present. Motion to approve the prior meeting's minutes carried unanimously. Public Works reported Q3 chip-seal scheduling on track.</p>";
        mins.summaryHtml =
          "<p>The Council met and approved last month's minutes, heard a Public Works update on fall street maintenance, and discussed upcoming business. See the full minutes for details.</p>";
        mins.adoptionState = "draft";
        mins.updatedAt = nowIso;
      }
      next?.();
    }, TRANSCRIBE_STEP_MS);
  };
  // Pasted-transcript jobs start AT "drafting" (no audio to transcribe) — skip straight to the
  // drafting -> ready leg; uploaded-recording jobs run the full transcribing -> drafting -> ready
  // timeline.
  if (startState === "drafting") {
    advance("drafting", () => advance("ready"));
  } else {
    advance("transcribing", () => advance("drafting", () => advance("ready")));
  }
}

/** GET .../transcribe/status — state:"" (no TranscriptJob yet) is the normal "no recording provided
 *  yet" case, never an error (mirrors AdminMeetingTranscribeStatus). */
export function getTranscribeStatus(meetingId: string): Promise<TranscriptJobView> {
  const job = TRANSCRIPT_JOBS[meetingId];
  return delay(clone(job ?? { state: "" as TranscriptJobState }), 150);
}

/** POST .../transcribe/retry — only legal from `failed` (mirrors AdminMeetingTranscribeRetry's
 *  409 "job_not_failed" otherwise). */
export function retryTranscribe(meetingId: string): Promise<{ state: TranscriptJobState }> {
  const job = TRANSCRIPT_JOBS[meetingId];
  if (!job || job.state !== "failed") {
    return Promise.reject(
      new ApiError("That job isn't in a failed state.", 409, "job_not_failed", false),
    );
  }
  const nowIso = new Date(now).toISOString();
  job.state = "queued";
  job.error = undefined;
  job.updatedAt = nowIso;
  runMockTranscribePipeline(meetingId, "queued");
  return delay({ state: "queued" as TranscriptJobState }, 100);
}

/** Test/demo hook — force a meeting's mock TranscriptJob into "failed" (there's no organic failure
 *  path in the happy-path timer above; component tests that exercise Retry call this directly). */
export function _mockFailTranscription(meetingId: string, error = "Transcription failed."): void {
  const nowIso = new Date(now).toISOString();
  TRANSCRIPT_JOBS[meetingId] = { state: "failed", error, startedAt: nowIso, updatedAt: nowIso };
}

// Custom-vocabulary admin (getTranscribeVocabulary/saveTranscribeVocabulary) was removed from the
// frontend — Linn Valley drafts minutes from a pasted YouTube transcript, not AWS Transcribe, so
// tuning its custom vocabulary is moot here. The backend endpoint stays dormant.

// ---- YouTube transcript integration (2026-08-21 reversal) --------------------------------------
// Mirrors handler.youtube_oauth.go + handler.meeting_youtube_transcript.go's contract closely
// enough to exercise the connect/status/disconnect settings UI and the meeting-builder pull button
// fully offline: connect/disconnect flip a single in-memory tenant connection; the transcript pull
// keys off the "not_connected" test hook + the mock meeting's own seeded Watch/Video link, and
// stages a fixed transcript into the SAME TRANSCRIPT_JOBS + MINUTES slot the paste path uses.

let YOUTUBE_CONNECTED = false;
let YOUTUBE_CHANNEL_TITLE: string | undefined = "City of Linn Valley";
// Test/demo hook — force the NEXT pullYouTubeTranscript call to reject with a specific error code
// (e.g. "asr_download_failed", "no_caption_tracks") instead of the happy path.
let YOUTUBE_NEXT_PULL_ERROR: { status: number; code: string; message: string } | null = null;

/** GET /api/admin/youtube/connect — the mock never actually talks to Google; it returns a
 *  same-shape fake authorize URL so the "Connect YouTube" button's redirect wiring is exercisable
 *  (component tests stub `window.location` rather than following the real hop). */
export function getYouTubeAuthorizeUrl(): Promise<YouTubeAuthorizeView> {
  return delay({ authorizeUrl: "https://accounts.google.com/o/oauth2/mock-authorize?state=mock" });
}

/** GET /api/admin/youtube/status. */
export function getYouTubeStatus(): Promise<YouTubeStatusView> {
  return delay(
    YOUTUBE_CONNECTED
      ? { connected: true, channelTitle: YOUTUBE_CHANNEL_TITLE }
      : { connected: false },
  );
}

/** DELETE /api/admin/youtube/disconnect. */
export function disconnectYouTube(): Promise<YouTubeStatusView> {
  YOUTUBE_CONNECTED = false;
  return delay({ connected: false });
}

/** Test/demo hook — set the mock connection state directly (settings-page tests that need to start
 *  already-connected, or force a reset between cases). */
export function _mockSetYouTubeConnected(connected: boolean, channelTitle?: string): void {
  YOUTUBE_CONNECTED = connected;
  YOUTUBE_CHANNEL_TITLE = channelTitle ?? (connected ? "City of Linn Valley" : undefined);
}

/** Test/demo hook — the next pullYouTubeTranscript call rejects with this ApiError instead of
 *  succeeding; cleared after it fires once. */
export function _mockFailNextYouTubePull(status: number, code: string, message: string): void {
  YOUTUBE_NEXT_PULL_ERROR = { status, code, message };
}

const MOCK_YOUTUBE_TRANSCRIPT_TEXT =
  "Mayor: called to order. Council: roll call complete. Public Works reported Q3 chip-seal scheduling on track. Motion to approve carried unanimously.";

/** POST /api/admin/meetings/{id}/youtube-transcript — mirrors the real handler's checks in order:
 *  meeting exists -> has a meeting-level Video/Watch link -> tenant is YouTube-connected -> (mock)
 *  an injected failure, if any -> success, staging the transcript into the drafting slot exactly
 *  like a pasted transcript would (runMockTranscribePipeline). */
export function pullYouTubeTranscript(meetingId: string): Promise<YouTubeTranscriptPullResult> {
  const m = MEETINGS.find((x) => x.id === meetingId);
  if (!m) return Promise.reject(new ApiError("Meeting not found.", 404, "not_found", false));

  const videoLink = MEETING_ATTACHMENTS.find(
    (a) =>
      a.meetingId === meetingId &&
      !a.itemId &&
      !a.contributionDepartment &&
      (a.description === "Video" || a.description === "Watch"),
  );
  if (!videoLink) {
    return Promise.reject(
      new ApiError(
        "This meeting has no YouTube video link — attach a Watch link first",
        400,
        "no_youtube_link",
        false,
      ),
    );
  }

  if (!YOUTUBE_CONNECTED) {
    return Promise.reject(
      new ApiError(
        "Connect your YouTube account to pull captions",
        409,
        "youtube_not_connected",
        false,
      ),
    );
  }

  if (YOUTUBE_NEXT_PULL_ERROR) {
    const { status, code, message } = YOUTUBE_NEXT_PULL_ERROR;
    YOUTUBE_NEXT_PULL_ERROR = null;
    return Promise.reject(new ApiError(message, status, code, false));
  }

  const nowIso = new Date(now).toISOString();
  const existing = TRANSCRIPT_JOBS[meetingId];
  if (existing?.state === "transcribing" || existing?.state === "drafting") {
    return Promise.reject(
      new ApiError(
        "Transcription is already in progress.",
        409,
        "transcription_in_progress",
        false,
      ),
    );
  }
  TRANSCRIPT_JOBS[meetingId] = { state: "drafting", startedAt: nowIso, updatedAt: nowIso };
  runMockTranscribePipeline(meetingId, "drafting");

  return delay({
    state: "drafting" as TranscriptJobState,
    source: "youtube_manual",
    transcript: MOCK_YOUTUBE_TRANSCRIPT_TEXT,
  });
}

/** Mock AI "Draft from last meeting" — no network call; returns a fixed, plausible section skeleton
 *  so the accept/edit/dismiss review UI is fully exercisable offline (agenda-packet.md §5.1). Empty
 *  `carryOverHint` / `priorTranscript` still succeeds (bootstrap/no-prior-minutes case; both are
 *  optional and purely additive). */
export function draftAgenda(
  meetingId: string,
  _carryOverHint?: string,
  _priorTranscript?: string,
): Promise<DraftAgendaSection[]> {
  const m = MEETINGS.find((x) => x.id === meetingId);
  if (!m) return Promise.reject(new ApiError("Meeting not found.", 404, "not_found", false));
  const sections: DraftAgendaSection[] = [
    { title: "Call to Order", narrativeHtml: "<p>Roll call and call to order.</p>" },
    {
      title: "Approval of Minutes",
      narrativeHtml: `<p>Consider approval of the minutes from the ${m.body}'s previous meeting.</p>`,
    },
    {
      title: "Old / Unfinished Business",
      narrativeHtml: "<p>Carry-over items from the prior meeting's action list.</p>",
    },
    { title: "New Business", narrativeHtml: "<p></p>" },
    { title: "Adjournment", narrativeHtml: "<p>Motion to adjourn.</p>" },
  ];
  return delay(clone(sections), 400);
}

/** Mock "Summarize this item from its attachments" — no network/model call; resolves the selected
 *  attachment IDs against this item's mock attachments, treats uploaded files (`isLink: false`) as
 *  readable and link attachments as skipped ("external link (not read)"), and returns a canned
 *  summary mentioning the read filenames so the offline review panel has something real. Rejects
 *  422 "no_readable_attachments" when nothing readable was selected — mirrors the live contract. */
export function summarizeAgendaItem(
  meetingId: string,
  itemId: string,
  attachmentIds: string[],
  _hint?: string,
): Promise<SummarizeAgendaItemResult> {
  const m = MEETINGS.find((x) => x.id === meetingId);
  if (!m) return Promise.reject(new ApiError("Meeting not found.", 404, "not_found", false));
  const selected = new Set(attachmentIds);
  const onItem = MEETING_ATTACHMENTS.filter(
    (a) => a.meetingId === meetingId && a.itemId === itemId && selected.has(a.id),
  );
  const used: SummarizeAttachmentRef[] = [];
  const skipped: SummarizeAttachmentRef[] = [];
  for (const a of onItem) {
    const name = a.filename || "attachment";
    if (a.isLink) skipped.push({ id: a.id, filename: name, reason: "external link (not read)" });
    else used.push({ id: a.id, filename: name });
  }
  if (used.length === 0) {
    return Promise.reject(
      new ApiError(
        "Couldn't read the selected attachment(s): " +
          skipped.map((s) => `${s.filename} (${s.reason})`).join("; "),
        422,
        "no_readable_attachments",
        false,
      ),
    );
  }
  const names = used.map((u) => u.filename).join(", ");
  const result: SummarizeAgendaItemResult = {
    summaryHtml: `<p>Summary drafted from ${used.length} attachment(s): ${names}.</p>`,
    used,
    skipped,
  };
  return delay(clone(result), 500);
}

/** Mock "Export packet" — no network call, no real merge; returns a fake presigned downloadUrl
 *  (a data: URL of the agenda PDF the caller already generated, so the download button has something
 *  real to point at offline) plus a plausible included/skipped split derived from this meeting's
 *  actual mock attachments: uploaded files (`isLink: false`) "merge" cleanly, link attachments split
 *  — most included, the LAST one skipped (an unreachable external link) so the UI's skipped-summary
 *  toast is exercisable offline too. `input.convertedDocx` (client-converted .docx -> PDF S3 keys, see
 *  staff/docxToPdf.ts) is accepted for interface parity with the live server contract; any attachment
 *  named in it is treated as included (its converted PDF "merged") rather than run through the
 *  isLink/lastLink heuristic below — mirrors the real server, which merges the converted PDF instead
 *  of skipping the original .docx. */
export function exportMeetingPacket(
  meetingId: string,
  input: ExportPacketInput,
): Promise<ExportPacketResult> {
  const m = MEETINGS.find((x) => x.id === meetingId);
  if (!m) return Promise.reject(new ApiError("Meeting not found.", 404, "not_found", false));
  const atts = MEETING_ATTACHMENTS.filter((a) => a.meetingId === meetingId);
  const converted = input.convertedDocx ?? {};
  const links = atts.filter((a) => a.isLink && !(a.id in converted));
  const lastLink = links[links.length - 1];
  const included: ExportPacketResult["included"] = [];
  const skipped: ExportPacketResult["skipped"] = [];
  for (const a of atts) {
    const title = a.filename || (a.isLink ? a.linkUrl || "Linked document" : "Attachment");
    const type = a.isLink ? "link" : "file";
    if (a.id in converted) {
      included.push({ title, type: "file" });
    } else if (a.isLink && a === lastLink) {
      skipped.push({ title, type, reason: "Couldn't fetch this link server-side." });
    } else {
      included.push({ title, type });
    }
  }
  included.unshift({ title: "Agenda", type: "agenda" });
  const dataUrl = `data:application/pdf;base64,${input.agendaPdfBase64}`;
  const filename =
    `${m.body}${m.title ? ` - ${m.title}` : ""} - ${m.meetingDate} - packet.pdf`.replace(
      /[^\w \-—().]+/g,
      "",
    );
  return delay({ downloadUrl: dataUrl, filename, included, skipped }, 500);
}

// ---- Public /meetings (no auth) --------------------------------------------------------------

/** Mirrors handler/meetings_public.go's `publicMeetingDocumentType` — maps a meeting-level
 *  attachment's Description (the same closed vocabulary MOCK_MEETING_LINK_DESCRIPTIONS/
 *  attachMeetingLink write) to its canonical `type` + public-facing `label`. "Video" renders as
 *  **"Watch"**, matching the server's own public-projection wording (meetings-public-relaunch.md
 *  §1.3). An unrecognized description (e.g. the "Document (other)" fallback) is excluded, same as
 *  the real server. */
function publicMeetingDocType(
  description: string,
): { type: PublicMeetingDocLink["type"]; label: string } | null {
  switch (description) {
    case "Agenda":
      return { type: "agenda", label: "Agenda" };
    case "Packet":
      return { type: "packet", label: "Packet" };
    case "Minutes":
      return { type: "minutes", label: "Minutes" };
    case "Recap":
      return { type: "recap", label: "Recap" };
    case "Video":
      return { type: "video", label: "Watch" };
    default:
      return null;
  }
}

/** Meeting-level (itemId "") typed doc links for the public list card — one entry per type that
 *  actually exists, first-encountered wins for a duplicate type, in the fixed
 *  Agenda→Packet→Minutes→Recap→Watch order (meetings-public-relaunch.md §1.4). */
function buildPublicMeetingDocuments(meetingId: string): PublicMeetingDocLink[] {
  const ORDER: PublicMeetingDocLink["type"][] = ["agenda", "packet", "minutes", "recap", "video"];
  const byType = new Map<PublicMeetingDocLink["type"], PublicMeetingDocLink>();
  for (const a of MEETING_ATTACHMENTS) {
    if (a.meetingId !== meetingId || a.itemId !== "" || a.contributionDepartment) continue;
    const mapped = publicMeetingDocType(a.description);
    if (!mapped || byType.has(mapped.type)) continue;
    const url = a.isLink ? a.linkUrl : `/docs/${a.tenantId}/${a.id}-${a.filename ?? "attachment"}`;
    if (!url) continue;
    byType.set(mapped.type, { type: mapped.type, label: mapped.label, url });
  }
  return ORDER.filter((t) => byType.has(t)).map((t) => byType.get(t)!);
}

/** Mirrors handler.addEntityDocLinks — fills a "recap"/"minutes" slot with an IN-PAGE anchor to
 *  this meeting's own detail page (`/meetings/<id>/#recap`/`#minutes`) when a published Recap /
 *  publicly-visible Minutes ENTITY exists but no real Recap/Minutes DOCUMENT attachment already
 *  occupies that slot (2026-08-28 — "fast access to recap/minutes from the list + in-page
 *  bookmarks"). A real document link always wins its slot — this only fills the GAP. */
function addEntityDocLinks(
  documents: PublicMeetingDocLink[],
  meetingId: string,
  mins: MinutesView | undefined,
): PublicMeetingDocLink[] {
  if (!mins) return documents;
  const hasType = (t: PublicMeetingDocLink["type"]) => documents.some((d) => d.type === t);
  const pub = publicMinutesFor(mins);
  if (!pub) return documents;
  const out = [...documents];
  const url = `/meetings/${meetingId}/`;
  if (pub.summaryHtml && !hasType("recap")) {
    out.push({ type: "recap", label: "Recap", url: `${url}#recap`, isPage: true });
  }
  if (pub.bodyHtml && !hasType("minutes")) {
    out.push({ type: "minutes", label: "Minutes", url: `${url}#minutes`, isPage: true });
  }
  return out;
}

function toPublicMeeting(m: MeetingView): PublicMeetingView {
  const mins = MINUTES.find((x) => x.meetingId === m.id);
  const pub = publicMinutesFor(mins);
  const documents = addEntityDocLinks(buildPublicMeetingDocuments(m.id), m.id, mins);
  // RECONCILED hasMinutes (mirrors handler.PublicMeetings exactly): true when EITHER a
  // publicly-visible Minutes ENTITY exists OR the meeting has at least one minutes-type
  // DOCUMENT attachment (already reflected in `documents`).
  const hasMinutes = pub !== null || documents.some((d) => d.type === "minutes");
  return {
    id: m.id,
    body: m.body,
    title: m.title,
    meetingDate: m.meetingDate,
    startTime: m.startTime,
    location: m.location,
    status: m.status as "published" | "archived",
    agendaState: m.agendaState,
    hasMinutes,
    documents,
  };
}

export function listPublicMeetings(): Promise<PublicMeetingView[]> {
  const published = MEETINGS.filter((m) => m.status !== "draft").map(toPublicMeeting);
  const today = new Date(now).toISOString().slice(0, 10);
  published.sort((a, b) => {
    const aUp = a.meetingDate >= today;
    const bUp = b.meetingDate >= today;
    if (aUp !== bUp) return aUp ? -1 : 1;
    return aUp
      ? a.meetingDate.localeCompare(b.meetingDate)
      : b.meetingDate.localeCompare(a.meetingDate);
  });
  return delay(clone(published));
}

// ---- Public "Reported Concerns" map (public-concerns-map.md / ADR-0023) -----------------------
//
// Mock dev/test data only — a handful of fixed, already-"fuzzed-looking" points around Linn
// Valley, KS (no real resident data, no real fuzz algorithm needed here: the point of the mock is
// to exercise the UI, not re-implement shared.FuzzCoord). opaque refs are plain mock ids, never a
// real tracking code.
const MOCK_CONCERN_PINS: PublicConcernPin[] = [
  {
    ref: "mock-pin-1",
    category: "Junk / abandoned vehicle",
    categoryId: "junk-vehicle",
    type: "Construction cleanup",
    status: "open",
    lat: 38.1726,
    lng: -94.7513,
    location: "Oak St",
    dateReported: "2026-09-02T14:00:00Z",
    dateResolved: null,
  },
  {
    ref: "mock-pin-2",
    category: "Tall grass / weeds",
    categoryId: "tall-grass",
    type: "Road repair",
    status: "in-progress",
    lat: 38.1689,
    lng: -94.7462,
    location: "Fawn Valley Dr",
    dateReported: "2026-09-10T18:30:00Z",
    dateResolved: null,
  },
  {
    ref: "mock-pin-3",
    category: "Pothole / street / drainage",
    categoryId: "street-drainage",
    // Deliberately no `type` set — exercises the "omit gracefully when empty" path alongside the
    // other pins' type chips.
    status: "resolved",
    lat: 38.1752,
    lng: -94.7571,
    // Deliberately no street could be safely extracted — exercises the em-dash path alongside the
    // other pins' "near {street}" rows (ADR-0023's two-tier location).
    location: "",
    dateReported: "2026-08-20T12:00:00Z",
    dateResolved: "2026-09-01T16:00:00Z",
  },
  {
    ref: "mock-pin-4",
    category: "Pothole / street / drainage",
    categoryId: "street-drainage",
    type: "Road repair",
    status: "resolved",
    lat: 38.1701,
    lng: -94.7489,
    location: "Maple Ave",
    dateReported: "2026-09-18T09:15:00Z",
    dateResolved: "2026-09-25T11:00:00Z",
  },
  {
    ref: "mock-pin-5",
    category: "Junk / abandoned vehicle",
    categoryId: "junk-vehicle",
    type: "Hydrant",
    status: "open",
    lat: 38.1668,
    lng: -94.7538,
    location: "Oak St",
    dateReported: "2026-09-28T08:45:00Z",
    dateResolved: null,
  },
];

/** Mirrors handler.PublicConcerns's filter + k-anonymity-suppression contract (no fuzzing/ref
 *  derivation here — mock data is already "pre-fuzzed", see MOCK_CONCERN_PINS's comment) so the UI
 *  exercises the SAME shape (pins/summary/suppressed) in mock mode as it does against the live API. */
export function listPublicConcerns(filter: PublicConcernsFilter = {}): Promise<PublicConcernsView> {
  const MIN_COUNT = 4; // mirrors DefaultConcernMapMinCount (concerns_map.go)
  let pins = MOCK_CONCERN_PINS.filter((p) => {
    // The real `category` filter param (and the embed/URL `?category=` deep-link) is the raw
    // category ID, not the label — match against `categoryId` (falling back to `category` for any
    // mock pin that predates that field, so nothing silently stops matching).
    if (filter.category && (p.categoryId ?? p.category) !== filter.category) return false;
    if (filter.status && p.status !== filter.status) return false;
    const reportedDate = p.dateReported.slice(0, 10);
    if (filter.from && reportedDate < filter.from) return false;
    if (filter.to && reportedDate > filter.to) return false;
    return true;
  });
  pins = [...pins].sort((a, b) => b.dateReported.localeCompare(a.dateReported));
  const reported = pins.length;
  const resolved = pins.filter((p) => p.status === "resolved").length;
  if (pins.length < MIN_COUNT) {
    return delay({ pins: [], summary: { reported, resolved }, suppressed: true });
  }
  return delay({ pins: clone(pins), summary: { reported, resolved }, suppressed: false });
}

// ---- STAFF concerns map (staff-concerns-map.md) -----------------------------------------------
//
// Mock dev/test data only — the internal counterpart to MOCK_CONCERN_PINS above: EXACT (never
// fuzzed) coordinates, the FULL internal RequestStatus lifecycle (not the public 3-way collapse),
// and at least one staffOnly/internal-category pin (work-item-due-dates.md) so the UI exercises the
// "internal view" ring/badge distinction even in mock mode.
const MOCK_STAFF_CONCERN_PINS: StaffConcernPin[] = [
  {
    id: "req-1",
    trackingCode: "TD-1001",
    category: "Junk / abandoned vehicle",
    categoryId: "junk-vehicle",
    type: "Construction cleanup",
    status: "Acknowledged",
    lat: 38.17263,
    lng: -94.75131,
    location: "Oak St",
    address: "412 Oak St",
    staffOnly: false,
    assignee: "Jamie Alvarez",
    dateReported: "2026-09-02T14:00:00Z",
    dateResolved: null,
  },
  {
    id: "req-2",
    trackingCode: "TD-1002",
    category: "Tall grass / weeds",
    categoryId: "tall-grass",
    type: "Road repair",
    status: "InProgress",
    lat: 38.16889,
    lng: -94.74619,
    location: "Fawn Valley Dr",
    address: "208 Fawn Valley Dr",
    staffOnly: false,
    dateReported: "2026-09-10T18:30:00Z",
    dateResolved: null,
  },
  {
    id: "req-3",
    trackingCode: "TD-1003",
    category: "Pothole / street / drainage",
    categoryId: "street-drainage",
    status: "Resolved",
    lat: 38.17524,
    lng: -94.75709,
    location: "",
    address: "",
    staffOnly: false,
    assignee: "Pat Nguyen",
    dateReported: "2026-08-20T12:00:00Z",
    dateResolved: "2026-09-01T16:00:00Z",
  },
  {
    id: "req-4",
    trackingCode: "TD-1004",
    category: "Pothole / street / drainage",
    categoryId: "street-drainage",
    type: "Road repair",
    status: "Closed",
    lat: 38.17014,
    lng: -94.74889,
    location: "Maple Ave",
    address: "901 Maple Ave",
    staffOnly: false,
    dateReported: "2026-09-18T09:15:00Z",
    dateResolved: "2026-09-25T11:00:00Z",
  },
  {
    id: "req-5",
    trackingCode: "TD-1005",
    category: "Junk / abandoned vehicle",
    categoryId: "junk-vehicle",
    type: "Hydrant",
    status: "New",
    lat: 38.16679,
    lng: -94.75377,
    location: "Oak St",
    address: "117 Oak St",
    staffOnly: false,
    dateReported: "2026-09-28T08:45:00Z",
    dateResolved: null,
  },
  // Internal/engineering work item — StaffOnly category, never shown on the public map at all. The
  // reason this page exists: staff need to see internal items alongside citizen reports, visually
  // distinguished (ring/badge), not mixed in indistinguishably.
  {
    id: "req-6",
    trackingCode: "TD-1006",
    category: "Work Orders / Maintenance",
    categoryId: "work-orders",
    type: "",
    status: "InProgress",
    lat: 38.17401,
    lng: -94.75025,
    location: "City Park",
    address: "City Park — north lift station",
    staffOnly: true,
    assignee: "Pat Nguyen",
    lineId: "line-20-1141L-07",
    dateReported: "2026-10-05T10:00:00Z",
    dateResolved: null,
  },
];

/** Mirrors GET /api/requests/map's filter contract — concerns:read-gated server-side (same
 *  ModuleConcerns/ActionRead the concern queue requires); the mock store applies no such gate (the
 *  page itself is reached only behind staff auth either way). */
export function listStaffConcernsMap(
  filter: StaffConcernsFilter = {},
): Promise<StaffConcernsView> {
  let pins = MOCK_STAFF_CONCERN_PINS.filter((p) => {
    if (filter.category && (p.categoryId ?? p.category) !== filter.category) return false;
    if (filter.status && p.status !== filter.status) return false;
    const reportedDate = p.dateReported.slice(0, 10);
    if (filter.from && reportedDate < filter.from) return false;
    if (filter.to && reportedDate > filter.to) return false;
    return true;
  });
  pins = [...pins].sort((a, b) => b.dateReported.localeCompare(a.dateReported));
  return delay({ pins: clone(pins) });
}

/** Mirrors handler/meetings_public.go's server-side enforcement of the two public-display toggles
 *  (meetings-public-relaunch.md §3): `publicShowAgendaItemDetails: false` omits NarrativeHTML,
 *  `publicShowAttachments: false` omits the per-item attachments list entirely — both read from the
 *  same in-memory SETTINGS a clerk edits via BrandingAdmin, resolved default-true. */
function toPublicAgendaItem(it: AgendaItemView): PublicAgendaItemView {
  const showDetails = SETTINGS.publicShowAgendaItemDetails !== false;
  const showAttachments = SETTINGS.publicShowAttachments !== false;
  return {
    id: it.id,
    order: it.order,
    title: it.title,
    narrativeHtml: showDetails ? it.narrativeHtml : undefined,
    presenter: it.presenter,
    outcome: it.outcome,
    vote: it.vote,
    attachments: showAttachments
      ? MEETING_ATTACHMENTS.filter((a) => a.itemId === it.id && !a.contributionDepartment).map(
          (a) => ({
            id: a.id,
            filename: a.filename,
            contentType: a.contentType,
            description: a.description,
            accessible: a.accessible,
            isLink: a.isLink,
            // A link attachment resolves to its external URL as entered; a file attachment mirrors the
            // live CloudFront public path convention (`/docs/<tenantId>/<hash>-<file>`, uploadDocument's
            // mock analog above) — server-set, never derived client-side.
            url: a.isLink ? a.linkUrl : `/docs/${a.tenantId}/${a.id}-${a.filename ?? "attachment"}`,
            supporting: a.supporting,
          }),
        )
      : [],
  };
}

/** Builds the two-level public agenda tree (Council Agenda 10A) — mirrors
 *  handler/meetings_public.go's buildPublicAgendaTree: bucket-by-parent-then-sort-by-order, with an
 *  ORPHAN FALLBACK (a sub-item whose declared parent isn't a current top-level item renders as its
 *  own top-level entry instead of silently vanishing — open-records concern). `children` is
 *  OMITTED (undefined) when a top-level item has no sub-items, never an empty array. */
function buildPublicAgendaTree(meetingId: string): PublicAgendaItemView[] {
  const all = AGENDA_ITEMS.filter((i) => i.meetingId === meetingId);
  const topLevelIds = new Set(all.filter((i) => !i.parentItemId).map((i) => i.id));
  const childrenByParent = new Map<string, AgendaItemView[]>();
  const orphans: AgendaItemView[] = [];
  for (const it of all) {
    if (!it.parentItemId) continue;
    if (!topLevelIds.has(it.parentItemId)) {
      orphans.push(it);
      continue;
    }
    const siblings = childrenByParent.get(it.parentItemId) ?? [];
    siblings.push(it);
    childrenByParent.set(it.parentItemId, siblings);
  }
  const allTop = [...all.filter((i) => !i.parentItemId), ...orphans].sort(
    (a, b) => a.order - b.order,
  );
  return allTop.map((top) => {
    const projected = toPublicAgendaItem(top);
    const kids = (childrenByParent.get(top.id) ?? []).sort((a, b) => a.order - b.order);
    if (kids.length > 0) projected.children = kids.map(toPublicAgendaItem);
    return projected;
  });
}

/** Mirrors shared.Minutes.ResolvedRecapPublished/ResolvedMinutesPublished/MinutesPubliclyVisible
 *  (server-side backward compat + independent recap/minutes publish gates) — recap has no adoption
 *  dependency; the formal minutes additionally require adoption OR the tenant's
 *  publicShowDraftMinutes setting. Legacy `published` is the fallback when the two new *Published
 *  fields were never explicitly set (mirrors the server's *bool-vs-bool resolution, simplified here
 *  to boolean-or-undefined since the mock's MinutesView always carries a definite boolean). */
function publicMinutesFor(mins: MinutesView | undefined): PublicMinutesView | null {
  if (!mins) return null;
  const adoptionState = mins.adoptionState || "draft";
  const showDraftMinutes = SETTINGS.publicShowDraftMinutes !== false;
  const showRecap = mins.recapPublished ?? mins.published;
  const minutesPublished = mins.minutesPublished ?? mins.published;
  const showBody = minutesPublished && (adoptionState === "adopted" || showDraftMinutes);
  if (!showRecap && !showBody) return null;
  return {
    bodyHtml: showBody ? mins.bodyHtml : undefined,
    summaryHtml: showRecap ? mins.summaryHtml : undefined,
    adoptionState,
  };
}

export function getPublicMeeting(id: string): Promise<PublicMeetingDetail | null> {
  const m = MEETINGS.find((x) => x.id === id);
  if (!m || m.status === "draft") return delay(null);
  const items = buildPublicAgendaTree(id);
  const mins = MINUTES.find((x) => x.meetingId === id);
  const minutes = publicMinutesFor(mins);
  return delay(
    clone({
      meeting: toPublicMeeting(m),
      items,
      minutes,
    }),
  );
}

// ---- Contributor "My reports" (contributorAuth: contributor or admin) -------------------------

export function listMyReports(): Promise<MyReportSlot[]> {
  const caller = CURRENT_USER;
  const slots: MyReportSlot[] = [];
  for (const m of MEETINGS) {
    if (!isUpcomingMeeting(m)) continue;
    for (const c of CONTRIBUTIONS.filter((x) => x.meetingId === m.id)) {
      if (!isAssignedToCaller(caller, c)) continue;
      const it = AGENDA_ITEMS.find((x) => x.id === c.itemId);
      if (!it) continue;
      slots.push({
        contribution: toContributorContribution(c),
        meeting: toContributorMeeting(m),
        item: toContributorItem(it),
      });
    }
  }
  slots.sort((a, b) => {
    if (a.meeting.meetingDate !== b.meeting.meetingDate) {
      return a.meeting.meetingDate.localeCompare(b.meeting.meetingDate);
    }
    return a.item.order - b.item.order;
  });
  return delay(clone(slots));
}

function resolveOwnContribution(
  meetingId: string,
  itemId: string,
): { m: MeetingView; c: ContributionView } | null {
  const caller = CURRENT_USER;
  const m = MEETINGS.find((x) => x.id === meetingId);
  if (!m || !isUpcomingMeeting(m)) return null;
  const c = CONTRIBUTIONS.find(
    (x) => x.meetingId === meetingId && x.itemId === itemId && isAssignedToCaller(caller, x),
  );
  if (!c) return null;
  return { m, c };
}

export function getMyContribution(
  meetingId: string,
  itemId: string,
): Promise<{
  contribution: ContributorContributionView;
  meeting: ContributorMeetingView;
  item: ContributorAgendaItemView;
} | null> {
  const found = resolveOwnContribution(meetingId, itemId);
  if (!found) return delay(null);
  const it = AGENDA_ITEMS.find((x) => x.id === itemId)!;
  return delay(
    clone({
      contribution: toContributorContribution(found.c),
      meeting: toContributorMeeting(found.m),
      item: toContributorItem(it),
    }),
  );
}

export function saveMyContribution(
  meetingId: string,
  itemId: string,
  input: SaveContributionInput,
): Promise<ContributorContributionView> {
  const found = resolveOwnContribution(meetingId, itemId);
  if (!found) return Promise.reject(new ApiError("Not found.", 404, "not_found", false));
  const c = found.c;
  if (input.reportHtml) c.reportHtml = input.reportHtml;
  if (c.state === "requested") c.state = "drafted";
  c.updatedAt = new Date(now).toISOString();
  return delay(clone(toContributorContribution(c)));
}

export function submitMyContribution(
  meetingId: string,
  itemId: string,
): Promise<ContributorContributionView> {
  const found = resolveOwnContribution(meetingId, itemId);
  if (!found) return Promise.reject(new ApiError("Not found.", 404, "not_found", false));
  const c = found.c;
  if (c.state === "included" || !CAN_TRANSITION_CONTRIBUTION(c.state, "submitted")) {
    return Promise.reject(new ApiError(`${c.state} → submitted`, 409, "invalid_transition", false));
  }
  const nowIso = new Date(now).toISOString();
  c.state = "submitted";
  c.submittedAt = nowIso;
  c.updatedAt = nowIso;
  return delay(clone(toContributorContribution(c)));
}

export function attachMyContributionFile(
  meetingId: string,
  itemId: string,
  input: AttachMeetingFileInput,
): Promise<MeetingAttachmentView> {
  const found = resolveOwnContribution(meetingId, itemId);
  if (!found) return Promise.reject(new ApiError("Not found.", 404, "not_found", false));
  const a: MeetingAttachmentView = {
    id: genId("matt"),
    tenantId: "linnvalley",
    meetingId,
    itemId,
    contributionDepartment: found.c.department,
    s3Key: input.s3Key,
    linkUrl: input.linkUrl,
    isLink: Boolean(input.linkUrl),
    filename: input.filename,
    contentType: input.contentType,
    description: input.description.trim(),
    accessible: input.accessible as MeetingAttachmentAccessible,
    visibility: "public",
    createdAt: new Date(now).toISOString(),
  };
  MEETING_ATTACHMENTS.push(a);
  return delay(clone(a));
}

// ---- Council Meetings historical importer — staff console (meetings-embed-and-import.md §2) ----
//
// Seeds one in-progress import that resolves to "ready" a beat after start (screen 2 briefly
// visible, then screen 3 has real rows to review) — mirrors the ordinance mock's "resolves
// immediately, progress is a UX courtesy" posture (no real long-poll needed in mock mode). Rows
// cover every state Sue's mockup calls out: high-confidence clean row, a duplicate (merges, warning
// tint), a low-confidence ambiguous date, a missing-artifact medium-confidence row, and an
// AI-excluded-by-default "not a real meeting" row (§2.3 rule #2).

const MEETING_IMPORTS: Record<string, MeetingImportDetail> = {};

function meetingImportRow(
  rowId: string,
  meetingDate: string,
  overrides: Partial<MeetingImportRow> = {},
): MeetingImportRow {
  return {
    rowId,
    meetingDate,
    body: "City Council",
    confidence: 0.95,
    flags: [],
    included: true,
    links: [
      {
        url: "https://linnvalley.gov/agenda.pdf",
        text: "Agenda",
        type: "agenda",
        confidence: 0.95,
      },
      { url: "https://linnvalley.gov/packet.pdf", text: "Packet", type: "packet", confidence: 0.9 },
      {
        url: "https://linnvalley.gov/minutes.pdf",
        text: "Minutes",
        type: "minutes",
        confidence: 0.9,
      },
    ],
    ...overrides,
  };
}

function seedMeetingImportRows(): MeetingImportRow[] {
  return [
    meetingImportRow("row-1", "2026-07-27"),
    meetingImportRow("row-2", "2026-06-22", {
      links: [
        {
          url: "https://linnvalley.gov/agenda-0622.pdf",
          text: "Agenda",
          type: "agenda",
          confidence: 0.95,
        },
        {
          url: "https://linnvalley.gov/packet-0622.pdf",
          text: "Packet",
          type: "packet",
          confidence: 0.9,
        },
        {
          url: "https://linnvalley.gov/minutes-0622.pdf",
          text: "Minutes",
          type: "minutes",
          confidence: 0.9,
        },
        {
          url: "https://youtube.com/watch?v=mock0622",
          text: "Watch",
          type: "watch",
          confidence: 0.85,
        },
      ],
    }),
    meetingImportRow("row-3", "2026-05-18", {
      flags: ["duplicate"],
      duplicateOfMeetingId: "mtg-2026-05",
      duplicateOfLabel: "Special Meeting — Budget Work Session",
      links: [
        {
          url: "https://linnvalley.gov/agenda-0518.pdf",
          text: "Agenda",
          type: "agenda",
          confidence: 0.95,
        },
        {
          url: "https://linnvalley.gov/minutes-0518.pdf",
          text: "Minutes",
          type: "minutes",
          confidence: 0.9,
        },
      ],
    }),
    meetingImportRow("row-4", "2025-11-10", {
      confidence: 0.35,
      flags: ["low_confidence"],
      flagNote: 'Ambiguous date "11/10" — confirm before including',
      links: [
        {
          url: "https://linnvalley.gov/agenda-1110.pdf",
          text: "Agenda",
          type: "agenda",
          confidence: 0.6,
        },
        {
          url: "https://linnvalley.gov/packet-1110.pdf",
          text: "Packet",
          type: "packet",
          confidence: 0.55,
        },
        {
          url: "https://linnvalley.gov/minutes-1110.pdf",
          text: "Minutes",
          type: "minutes",
          confidence: 0.5,
        },
        {
          url: "https://youtube.com/watch?v=mock1110",
          text: "Watch",
          type: "watch",
          confidence: 0.5,
        },
      ],
    }),
    meetingImportRow("row-5", "2025-10-13", {
      confidence: 0.62,
      flags: ["low_confidence"],
      flagNote: "Minutes link not found on source page",
      links: [
        {
          url: "https://linnvalley.gov/agenda-1013.pdf",
          text: "Agenda",
          type: "agenda",
          confidence: 0.8,
        },
      ],
    }),
    meetingImportRow("row-6", "2025-09-08", {
      confidence: 0.3,
      flags: ["not_a_meeting"],
      flagNote:
        "Excluded — page fragment looked like a cancelled-meeting notice, not a real meeting",
      included: false,
      links: [
        {
          url: "https://linnvalley.gov/agenda-0908.pdf",
          text: "Agenda",
          type: "agenda",
          confidence: 0.4,
        },
      ],
    }),
  ];
}

function meetingImportCounts(meetings: MeetingImportRow[]): MeetingImportDetail["counts"] {
  return {
    total: meetings.length,
    included: meetings.filter((r) => r.included).length,
    flagged: meetings.filter((r) =>
      r.flags.some((f) => f === "low_confidence" || f === "not_a_meeting"),
    ).length,
    duplicates: meetings.filter((r) => r.flags.includes("duplicate")).length,
  };
}

/** POST /admin/meetings/import mock — no real crawl in mock mode; seeds the row set immediately but
 *  reports "extracting" for one poll so screen 2's stage-rail/log briefly renders before "ready". */
export function startMeetingImport(
  input: StartMeetingImportInput,
): Promise<StartMeetingImportResult> {
  const importId = genId("mtgimport");
  const meetings = seedMeetingImportRows();
  MEETING_IMPORTS[importId] = {
    importId,
    sourceUrl: input.sourceUrl,
    body: input.body || "City Council",
    status: "extracting",
    meetings,
    counts: meetingImportCounts(meetings),
  };
  // Flip to "ready" shortly after start so a caller polling every couple seconds sees the
  // extracting -> ready transition without a real backend.
  setTimeout(() => {
    const rec = MEETING_IMPORTS[importId];
    if (rec) rec.status = "ready";
  }, 1200);
  return delay({ importId, status: "extracting" as const }, 150);
}

export function getMeetingImport(importId: string): Promise<MeetingImportDetail> {
  const rec = MEETING_IMPORTS[importId];
  if (!rec) return Promise.reject(new ApiError("Not found.", 404, "not_found", false));
  return delay(clone(rec));
}

export function updateMeetingImportRow(
  importId: string,
  rowId: string,
  input: UpdateMeetingImportRowInput,
): Promise<MeetingImportRow> {
  const rec = MEETING_IMPORTS[importId];
  if (!rec) return Promise.reject(new ApiError("Not found.", 404, "not_found", false));
  const row = rec.meetings.find((r) => r.rowId === rowId);
  if (!row) return Promise.reject(new ApiError("Not found.", 404, "not_found", false));
  if (input.meetingDate !== undefined) row.meetingDate = input.meetingDate;
  if (input.body !== undefined) row.body = input.body;
  if (input.included !== undefined) row.included = input.included;
  if (input.links !== undefined) row.links = input.links;
  rec.counts = meetingImportCounts(rec.meetings);
  return delay(clone(row));
}

export function commitMeetingImport(importId: string): Promise<CommitMeetingImportResult> {
  const rec = MEETING_IMPORTS[importId];
  if (!rec) return Promise.reject(new ApiError("Not found.", 404, "not_found", false));
  const included = rec.meetings.filter((r) => r.included);
  const merged = included.filter((r) => r.flags.includes("duplicate")).length;
  const created = included.length - merged;
  const skipped = rec.meetings.length - included.length;
  return delay({ createdCount: created, mergedCount: merged, skippedCount: skipped }, 400);
}

// ---- Ordinance Library — Phase B4 staff console (ordinance-library-phaseB.md §1.3/§1.5) --------
//
// Mirrors the live gate logic (store.CodeVersionPublishable) exactly: publishable only when
// status="previewing" AND every type="section" node's permalink is in reviewedSectionPermalinks
// AND every severity="warn" parseNote index is in addressedWarningIndexes. Seeds one already-LIVE
// version (so the History screen + "current live version" context strip have something to show)
// plus a draft the ingest flow mutates in place.

const ORDINANCE_TENANT = "linnvalley";

function ordinanceSection(
  versionId: string,
  nodeId: string,
  number: string,
  heading: string,
  permalink: string,
  order: number,
  extra: Partial<OrdinanceCodeNode> = {},
): OrdinanceCodeNode {
  return {
    tenantId: ORDINANCE_TENANT,
    versionId,
    nodeId,
    type: "section",
    number,
    heading,
    permalink,
    order,
    bodyHtml: `<p>${heading} — mock ordinance text for the staff console preview.</p>`,
    ...extra,
  };
}

const LIVE_VERSION_ID = "codever-2266";
const ORDINANCE_VERSIONS: OrdinanceCodeVersion[] = [
  {
    tenantId: ORDINANCE_TENANT,
    versionId: LIVE_VERSION_ID,
    label: "Current through Ord. 2266 (May 2026)",
    sourceFormat: "pdf",
    status: "live",
    isLive: true,
    sectionCount: 406,
    publishedAt: "2026-06-02T00:00:00.000Z",
    publishedBy: "J. Alvarez",
    createdAt: "2026-06-01T00:00:00.000Z",
    createdBy: "staff-jalvarez",
    warningCount: 0,
  },
  {
    tenantId: ORDINANCE_TENANT,
    versionId: "codever-2144",
    label: "Current through Ord. 2144 (2018 codification)",
    sourceFormat: "html",
    status: "superseded",
    isLive: false,
    sectionCount: 398,
    publishedAt: "2018-11-14T00:00:00.000Z",
    publishedBy: "R. Citycode (import)",
    createdAt: "2018-11-10T00:00:00.000Z",
    warningCount: 0,
  },
];

const ORDINANCE_NODES: Record<string, OrdinanceCodeNode[]> = {
  [LIVE_VERSION_ID]: [
    ordinanceSection(LIVE_VERSION_ID, "n-8-4-2", "8-4-2", "Weeds and tall grass", "8-4-2", 1),
  ],
};

/** POST /admin/ordinances/upload-presign mock — no real S3 in mock mode; just hand back a fake key
 *  the mock ingest step recognizes. */
export function presignOrdinanceUpload(
  filename: string,
): Promise<{ uploadUrl: string; s3Key: string }> {
  return delay({ uploadUrl: "", s3Key: `ingest/${ORDINANCE_TENANT}/mock-${filename}` }, 60);
}

/** Mock "upload": no real network PUT — resolves immediately, optionally reporting 100% progress so
 *  callers driving a progress bar see it complete. */
export async function uploadOrdinanceFile(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<string> {
  onProgress?.(100);
  const { s3Key } = await presignOrdinanceUpload(file.name);
  return s3Key;
}

const MOCK_PARSE_NOTES = [
  {
    file: "LinnValley_MunicipalCode_Supplement14.docx",
    anchor: "6-2-14",
    severity: "warn" as const,
    message: "§ 6-2-14 — heading level unclear, structured as a subsection. Confirm in review.",
  },
  {
    file: "LinnValley_MunicipalCode_Supplement14.docx",
    anchor: "4-1-2",
    severity: "warn" as const,
    message: '"see § 11-3" in § 4-1-2 doesn\'t match any section in this version.',
  },
  {
    file: "LinnValley_MunicipalCode_Supplement14.docx",
    anchor: "8-4-9",
    severity: "warn" as const,
    message: "§ 8-4-9 — fee table structure ambiguous, rendered as a two-column list.",
  },
];

/** POST /admin/ordinances/ingest mock — synthesizes a fresh draft CodeVersion + a small changed-
 *  section set (enough for the review-gate UI to exercise "mark reviewed"/"address warning" against
 *  real per-section/per-warning state), matching the server's ParseNotes/WarningCount shape. */
export function draftIngestOrdinance(
  input:
    | { s3Key: string; filename: string; contentType: string; label?: string }
    | { citycode: true; label?: string },
): Promise<OrdinanceDraftIngestResult> {
  const versionId = genId("codever");
  const label =
    input.label?.trim() ||
    ("citycode" in input && input.citycode
      ? "Re-pulled from citycode.net"
      : "Current through Ord. 280 (June 2026)");
  const nodes: OrdinanceCodeNode[] = [
    ordinanceSection(versionId, "n-4-1-2", "4-1-2", "Enforcement authority", "4-1-2", 1, {
      crossRefs: [{ toPermalink: "11-3", rawText: "§ 11-3", resolved: false }],
    }),
    ordinanceSection(
      versionId,
      "n-6-2-14",
      "6-2-14",
      "Sidewalk obstruction — snow & ice removal",
      "6-2-14",
      2,
    ),
    ordinanceSection(versionId, "n-8-4-2", "8-4-2", "Weeds and tall grass", "8-4-2", 3),
    ordinanceSection(versionId, "n-8-4-9", "8-4-9", "Abatement fee schedule", "8-4-9", 4),
  ];
  const version: OrdinanceCodeVersion = {
    tenantId: ORDINANCE_TENANT,
    versionId,
    label,
    sourceFormat:
      "contentType" in input ? (input.contentType.includes("pdf") ? "pdf" : "word") : "html",
    status: "draft",
    isLive: false,
    sectionCount: nodes.length,
    createdAt: new Date(now).toISOString(),
    createdBy: CURRENT_USER.id,
    parseNotes: MOCK_PARSE_NOTES,
    warningCount: MOCK_PARSE_NOTES.filter((n) => n.severity === "warn").length,
    reviewedSectionPermalinks: [],
    addressedWarningIndexes: [],
  };
  ORDINANCE_VERSIONS.unshift(version);
  ORDINANCE_NODES[versionId] = nodes;
  return delay(
    clone({
      versionId,
      sectionCount: version.sectionCount,
      warningCount: version.warningCount,
      parseNotes: version.parseNotes ?? [],
    }),
  );
}

export function listOrdinanceVersions(): Promise<OrdinanceCodeVersion[]> {
  return delay(clone(ORDINANCE_VERSIONS));
}

/** Mirrors store.CodeVersionPublishable exactly — the mock's version of the "same read the gate
 *  uses" contract (phaseB.md §1.5). */
function ordinancePublishable(
  version: OrdinanceCodeVersion,
  nodes: OrdinanceCodeNode[],
): { publishable: boolean; blockedReason: string } {
  if (version.status !== "previewing") {
    return { publishable: false, blockedReason: "version is not in previewing status" };
  }
  const reviewed = new Set(version.reviewedSectionPermalinks ?? []);
  for (const n of nodes) {
    if (n.type === "section" && !reviewed.has(n.permalink)) {
      return { publishable: false, blockedReason: "not all sections have been reviewed" };
    }
  }
  const addressed = new Set(version.addressedWarningIndexes ?? []);
  for (const [i, note] of (version.parseNotes ?? []).entries()) {
    if (note.severity === "warn" && !addressed.has(i)) {
      return { publishable: false, blockedReason: "not all warnings have been addressed" };
    }
  }
  return { publishable: true, blockedReason: "" };
}

export function getOrdinanceVersion(versionId: string): Promise<OrdinanceVersionDetail> {
  const version = ORDINANCE_VERSIONS.find((v) => v.versionId === versionId);
  if (!version) return Promise.reject(new ApiError("Not found.", 404, "not_found", false));
  const nodes = ORDINANCE_NODES[versionId] ?? [];
  const { publishable, blockedReason } = ordinancePublishable(version, nodes);
  return delay(clone({ version, nodes, publishable, blockedReason }));
}

export function markOrdinanceSectionReviewed(
  versionId: string,
  permalink: string,
): Promise<OrdinanceReviewProgress> {
  const version = ORDINANCE_VERSIONS.find((v) => v.versionId === versionId);
  if (!version) return Promise.reject(new ApiError("Not found.", 404, "not_found", false));
  if (version.status === "live" || version.status === "superseded") {
    return Promise.reject(new ApiError("Not reviewable.", 409, "version_not_reviewable", false));
  }
  const set = new Set(version.reviewedSectionPermalinks ?? []);
  set.add(permalink);
  version.reviewedSectionPermalinks = [...set];
  // Mirrors store.MarkSectionReviewed — first review tick flips draft -> previewing.
  if (version.status === "draft") version.status = "previewing";
  return delay(
    clone({
      versionId,
      status: version.status,
      reviewedSectionPermalinks: version.reviewedSectionPermalinks,
    }),
  );
}

export function addressOrdinanceWarning(
  versionId: string,
  warningIndex: number,
): Promise<OrdinanceReviewProgress> {
  const version = ORDINANCE_VERSIONS.find((v) => v.versionId === versionId);
  if (!version) return Promise.reject(new ApiError("Not found.", 404, "not_found", false));
  if (version.status === "live" || version.status === "superseded") {
    return Promise.reject(new ApiError("Not reviewable.", 409, "version_not_reviewable", false));
  }
  const total = version.parseNotes?.length ?? 0;
  if (warningIndex < 0 || warningIndex >= total) {
    return Promise.reject(
      new ApiError(
        "warningIndex is out of range for this version.",
        400,
        "invalid_warning_index",
        false,
      ),
    );
  }
  const set = new Set(version.addressedWarningIndexes ?? []);
  set.add(warningIndex);
  version.addressedWarningIndexes = [...set];
  return delay(
    clone({
      versionId,
      status: version.status,
      addressedWarningIndexes: version.addressedWarningIndexes,
    }),
  );
}

export function publishOrdinanceVersion(versionId: string): Promise<OrdinancePublishResult> {
  const version = ORDINANCE_VERSIONS.find((v) => v.versionId === versionId);
  if (!version) return Promise.reject(new ApiError("Not found.", 404, "not_found", false));
  const nodes = ORDINANCE_NODES[versionId] ?? [];
  const { publishable, blockedReason } = ordinancePublishable(version, nodes);
  if (!publishable) {
    return Promise.reject(new ApiError(blockedReason, 409, "not_publishable", false));
  }
  const prior = ORDINANCE_VERSIONS.find((v) => v.isLive);
  const nowIso = new Date(now).toISOString();
  if (prior) {
    prior.isLive = false;
    prior.status = "superseded";
  }
  version.isLive = true;
  version.status = "live";
  version.publishedAt = nowIso;
  version.publishedBy = CURRENT_USER.name ?? undefined;
  return delay(
    clone({
      versionId,
      status: "live" as const,
      supersededId: prior?.versionId ?? "",
      sitePages: nodes.length,
      filesSynced: nodes.length + 2,
      corpusSections: nodes.filter((n) => n.type === "section").length,
      invalidationId: `mock-inval-${versionId}`,
    }),
  );
}

// ---- Ordinance Library — R3 cross-module reference layer ---------------------------------------
//
// A flat in-memory list of OrdinanceRef, keyed by (entityType, entityId, permalink) — mirrors the
// live store's dedupe-on-attach / 404-on-detach-miss / 20-ref cap behavior closely enough to
// exercise the picker + chip-list UI end to end against mock data.

const MOCK_ORDINANCE_SECTIONS: {
  permalink: string;
  number: string;
  heading: string;
  text: string;
}[] = [
  {
    permalink: "8-4-2",
    number: "8-4-2",
    heading: "Weeds and tall grass",
    text: "No owner or occupant of any lot shall permit weeds or grass to exceed eight (8) inches in height.",
  },
  {
    permalink: "8-4-9",
    number: "8-4-9",
    heading: "Abatement fee schedule",
    text: "The City may abate a violation after notice and recover costs per the fee schedule adopted by resolution.",
  },
  {
    permalink: "6-2-14",
    number: "6-2-14",
    heading: "Sidewalk obstruction — snow & ice removal",
    text: "Owners and occupants shall remove snow and ice from abutting sidewalks within 24 hours of accumulation.",
  },
  {
    permalink: "4-1-2",
    number: "4-1-2",
    heading: "Enforcement authority",
    text: "Code enforcement officers are authorized to issue notices of violation and pursue compliance under this chapter.",
  },
];

const MOCK_ORDINANCE_REFS: OrdinanceRef[] = [
  {
    tenantId: ORDINANCE_TENANT,
    entityType: "cases",
    entityId: "case-1",
    permalink: "8-4-2",
    sectionLabel: "§8-4-2",
    createdBy: CURRENT_USER.id,
    createdAt: "2026-08-10T14:00:00.000Z",
  },
];

/** POST /public/ordinance/ask mock — the picker's "search a question or a section number" step.
 *  Mirrors the real endpoint's officialText[] shape closely enough for the picker to exercise a
 *  real search->pick loop against mock data (a simple substring match over number/heading/text,
 *  not a real retrieval/AI round-trip). */
export function searchOrdinanceSections(query: string): Promise<OrdinanceSearchHit[]> {
  const q = query.trim().toLowerCase();
  if (!q) return delay([], 40);
  const hits = MOCK_ORDINANCE_SECTIONS.filter(
    (s) =>
      s.permalink.toLowerCase().includes(q) ||
      s.number.toLowerCase().includes(q) ||
      s.heading.toLowerCase().includes(q) ||
      s.text.toLowerCase().includes(q),
  );
  return delay(clone(hits), 250);
}

const MAX_ORDINANCE_REFS_PER_ENTITY = 20;

/** POST /admin/{entityType}/{id}/ordinance-refs mock. */
export function attachOrdinanceRef(
  entityType: OrdinanceRefEntityType,
  entityId: string,
  permalink: string,
  sectionLabel: string,
): Promise<OrdinanceRef> {
  const known = MOCK_ORDINANCE_SECTIONS.some((s) => s.permalink === permalink);
  if (!known) {
    return Promise.reject(new ApiError("Not a live section.", 400, "unknown_permalink", false));
  }
  const existing = MOCK_ORDINANCE_REFS.filter(
    (r) => r.entityType === entityType && r.entityId === entityId,
  );
  if (existing.some((r) => r.permalink === permalink)) {
    return Promise.reject(new ApiError("Already attached.", 409, "already_attached", false));
  }
  if (existing.length >= MAX_ORDINANCE_REFS_PER_ENTITY) {
    return Promise.reject(
      new ApiError("Too many ordinance references attached.", 400, "validation_error", false),
    );
  }
  const ref: OrdinanceRef = {
    tenantId: ORDINANCE_TENANT,
    entityType,
    entityId,
    permalink,
    sectionLabel,
    createdBy: CURRENT_USER.id,
    createdAt: new Date(now).toISOString(),
  };
  MOCK_ORDINANCE_REFS.push(ref);
  return delay(clone(ref));
}

/** DELETE /admin/{entityType}/{id}/ordinance-refs/{permalink} mock. */
export function detachOrdinanceRef(
  entityType: OrdinanceRefEntityType,
  entityId: string,
  permalink: string,
): Promise<{ removed: boolean }> {
  const i = MOCK_ORDINANCE_REFS.findIndex(
    (r) => r.entityType === entityType && r.entityId === entityId && r.permalink === permalink,
  );
  if (i === -1) return Promise.reject(new ApiError("Not found.", 404, "not_found", false));
  MOCK_ORDINANCE_REFS.splice(i, 1);
  return delay({ removed: true });
}

/** GET /admin/{entityType}/{id}/ordinance-refs mock. */
export function listOrdinanceRefs(
  entityType: OrdinanceRefEntityType,
  entityId: string,
): Promise<OrdinanceRef[]> {
  return delay(
    clone(
      MOCK_ORDINANCE_REFS.filter((r) => r.entityType === entityType && r.entityId === entityId),
    ),
  );
}

/** GET /admin/ordinance/refs?permalink=… mock — staff reverse lookup (has entityId). */
export function getOrdinanceRefsReverse(permalink: string): Promise<OrdinanceRefsReverseResult> {
  return delay(
    clone({ permalink, refs: MOCK_ORDINANCE_REFS.filter((r) => r.permalink === permalink) }),
  );
}

/** GET /admin/ordinance/ratings mock — the "Answer quality" panel's 👍/👎 tally + recent
 *  downvotes, illustrative fixture data matching OrdinanceRatingsSummary exactly. */
const MOCK_ORDINANCE_RATINGS: OrdinanceRatingsSummary = {
  version: "v2026-08-19",
  up: 41,
  down: 9,
  recentDownvotes: [
    {
      query: "can I keep chickens in my backyard",
      sections: [],
      confident: false,
      createdAt: "2026-08-17T14:22:00Z",
    },
    {
      query: "how tall can my fence be on a corner lot",
      sections: ["12.6"],
      confident: true,
      createdAt: "2026-08-15T09:05:00Z",
    },
    {
      query: "do I need a permit for a shed",
      sections: [],
      confident: false,
      createdAt: "2026-08-12T18:40:00Z",
    },
  ],
};

export function getOrdinanceRatings(): Promise<OrdinanceRatingsSummary> {
  return delay(clone(MOCK_ORDINANCE_RATINGS));
}

// ---- Users & Permissions (ADR-0016 §5, RBAC Phase 3 — mocks) ---------------
//
// Mirrors the live registry (services/api/internal/auth/perms/registry.go) verbatim — 10 modules,
// each with baseline read/write/delete (settings is read/write only) plus its own special
// actions. Kept in sync by hand (no codegen across the Go/TS boundary yet); the frontend's OWN
// source of truth for a live tenant is always the GET /admin/permissions/registry response, this
// is just the mock's stand-in for that call.
const MOCK_REGISTRY: PermissionsRegistryModule[] = [
  { module: "concerns", actions: ["read", "write", "delete", "assign"] },
  { module: "questions", actions: ["read", "write", "delete", "publish"] },
  { module: "kora", actions: ["read", "write", "delete", "respond"] },
  { module: "permits", actions: ["read", "write", "delete", "advance"] },
  { module: "enforcement", actions: ["read", "write", "delete", "advance"] },
  { module: "ordinances", actions: ["read", "write", "delete", "publish"] },
  { module: "meetings", actions: ["read", "write", "delete", "publish", "import", "contribute"] },
  { module: "subscribers", actions: ["read", "write", "delete", "send", "read_directory"] },
  { module: "settings", actions: ["read", "write"] },
  { module: "users", actions: ["read", "write", "delete"] },
  { module: "contractors", actions: ["read", "write", "delete"] },
];

function allRegistryPermissions(): string[] {
  return MOCK_REGISTRY.flatMap((m) => m.actions.map((a) => `${m.module}:${a}`));
}

// The 4 shipped DefaultTemplates (perms/templates.go), computed the same way the live registry
// does — "every module" / "every operational module" derived from MOCK_REGISTRY above rather than
// hand-duplicated, so adding a module/action here stays consistent automatically.
const OPERATIONAL_MODULES = [
  "concerns",
  "questions",
  "kora",
  "permits",
  "enforcement",
  "ordinances",
  "meetings",
  "subscribers",
  "contractors",
];

const GROUPS: GroupView[] = [
  {
    id: "tmpl-admin",
    name: "Admin",
    description: "Full access to every module, including staff & permissions management.",
    permissions: allRegistryPermissions(),
    system: true,
  },
  {
    id: "tmpl-clerk",
    name: "Clerk",
    description:
      "Read/write/delete + special actions on every operational module, plus settings. Cannot manage staff or permissions.",
    permissions: [
      ...MOCK_REGISTRY.filter((m) => OPERATIONAL_MODULES.includes(m.module)).flatMap((m) =>
        m.actions
          .filter((a) => !(m.module === "subscribers" && a === "read_directory"))
          .map((a) => `${m.module}:${a}`),
      ),
      "settings:read",
      "settings:write",
    ],
    system: false,
  },
  {
    id: "tmpl-council-member",
    name: "Council Member",
    description: "Read-only: meetings (including drafts), concerns, and open records.",
    permissions: ["meetings:read", "concerns:read", "kora:read"],
    system: false,
  },
  {
    id: "tmpl-viewer",
    name: "Viewer",
    description: "Read-only access to every module.",
    permissions: MOCK_REGISTRY.map((m) => `${m.module}:read`),
    system: false,
  },
];

function groupsById(): Map<string, GroupView> {
  return new Map(GROUPS.map((g) => [g.id, g]));
}

/** Recompute every STAFF row's effectivePermissions as the union of its groupIds' Permissions —
 *  called after any group/membership mutation so listStaff() always reflects the current state
 *  (♻️ mirrors the live handler's effectivePermissionsList join). */
function recomputeEffectivePermissions(): void {
  const byId = groupsById();
  for (const u of STAFF) {
    const set = new Set<string>();
    for (const gid of u.groupIds ?? []) {
      const g = byId.get(gid);
      if (!g) continue;
      for (const p of g.permissions) set.add(p);
    }
    u.effectivePermissions = Array.from(set).sort();
  }
}
recomputeEffectivePermissions();

/** Whether removing `permissions` from group `gid` (or deleting it outright, permissions=null)
 *  would drop the tenant's last users:write holder — mirrors the live wouldLockOutUsersWrite
 *  guardrail (ADR-0016 §6) closely enough for mock/test purposes (a full simulation of "what if
 *  this membership changes" isn't needed here — the mock only needs to block the SAME two
 *  scenarios the live API blocks: deleting/narrowing the Admin group, and clearing the last
 *  users:write holder's groups). */
function wouldLockOutUsersWriteMock(
  candidateGroups: GroupView[],
  excludeStaffId?: string,
  overrideGroupIdsFor?: { staffId: string; groupIds: string[] },
): boolean {
  const byId = new Map(candidateGroups.map((g) => [g.id, g]));
  const holders = STAFF.filter((u) => {
    if (u.status === "deactivated") return false;
    const gids =
      overrideGroupIdsFor && overrideGroupIdsFor.staffId === u.id
        ? overrideGroupIdsFor.groupIds
        : (u.groupIds ?? []);
    return gids.some((gid) => byId.get(gid)?.permissions.includes("users:write"));
  });
  const remaining = excludeStaffId ? holders.filter((u) => u.id !== excludeStaffId) : holders;
  return remaining.length === 0 && holders.length > 0 === false
    ? false
    : excludeStaffId
      ? holders.length <= 1 && holders.some((u) => u.id === excludeStaffId)
      : holders.length === 0;
}

export function getPermissionsRegistry(): Promise<PermissionsRegistryModule[]> {
  return delay(clone(MOCK_REGISTRY));
}

export function listGroups(): Promise<GroupView[]> {
  return delay(clone(GROUPS));
}

export function createGroup(
  name: string,
  description: string,
  permissions: string[],
): Promise<GroupView> {
  const g: GroupView = {
    id: `grp-${Math.random().toString(36).slice(2, 10)}`,
    name,
    description,
    permissions: Array.from(new Set(permissions)),
    system: false,
  };
  GROUPS.push(g);
  recomputeEffectivePermissions();
  return delay(clone(g));
}

export function updateGroup(
  id: string,
  name: string,
  description: string,
  permissions: string[],
): Promise<GroupView> {
  const g = GROUPS.find((x) => x.id === id);
  if (!g) return Promise.reject(new ApiError("Group not found.", 404, "not_found"));
  const nextPerms = Array.from(new Set(permissions));
  if (g.system) {
    const allPerms = allRegistryPermissions();
    const missing = allPerms.some((p) => !nextPerms.includes(p));
    if (missing) {
      return Promise.reject(
        new ApiError(
          "The Admin group must retain every permission.",
          400,
          "system_group_permissions_protected",
        ),
      );
    }
  }
  g.name = name || g.name;
  g.description = description;
  g.permissions = nextPerms;
  recomputeEffectivePermissions();
  return delay(clone(g));
}

export function deleteGroup(id: string): Promise<void> {
  const g = GROUPS.find((x) => x.id === id);
  if (!g) return Promise.reject(new ApiError("Group not found.", 404, "not_found"));
  if (g.system) {
    return Promise.reject(
      new ApiError("This group is protected and cannot be deleted.", 400, "system_group_protected"),
    );
  }
  if (wouldLockOutUsersWriteMock(GROUPS.filter((x) => x.id !== id))) {
    return Promise.reject(
      new ApiError(
        "Deleting this group would remove the last person who can manage staff & permissions.",
        400,
        "would_lock_out_users_write",
      ),
    );
  }
  const idx = GROUPS.findIndex((x) => x.id === id);
  GROUPS.splice(idx, 1);
  for (const u of STAFF) u.groupIds = (u.groupIds ?? []).filter((gid) => gid !== id);
  recomputeEffectivePermissions();
  return delay(undefined);
}

/** Mirrors PUT /api/admin/staff/{id}/groups — replaces the ENTIRE membership list. `actingUserId`
 *  (defaults to CURRENT_USER, resolved lazily since CURRENT_USER isn't defined until below) is who
 *  the "no self-lockout" guardrail is checked against. */
export function setStaffGroups(
  staffId: string,
  groupIds: string[],
  actingUserId?: string,
): Promise<GroupView[]> {
  const u = STAFF.find((x) => x.id === staffId);
  if (!u) return Promise.reject(new ApiError("Staff member not found.", 404, "not_found"));
  const known = new Set(GROUPS.map((g) => g.id));
  for (const gid of groupIds) {
    if (!known.has(gid)) {
      return Promise.reject(new ApiError("Unknown group.", 400, "unknown_group_id"));
    }
  }
  const acting = actingUserId ?? STAFF[0]!.id;
  if (staffId === acting && wouldLockOutUsersWriteMock(GROUPS, undefined, { staffId, groupIds })) {
    return Promise.reject(
      new ApiError(
        "You can't remove your own access to manage staff & permissions.",
        400,
        "would_lock_out_self",
      ),
    );
  }
  if (wouldLockOutUsersWriteMock(GROUPS, undefined, { staffId, groupIds })) {
    return Promise.reject(
      new ApiError(
        "This would remove the last person who can manage staff & permissions.",
        400,
        "would_lock_out_users_write",
      ),
    );
  }
  u.groupIds = Array.from(new Set(groupIds));
  recomputeEffectivePermissions();
  return delay(clone(GROUPS));
}

/** GET /api/admin/me/permissions mock — the signed-in mock user's own effective permissions.
 *  usePermissions() calls this in mock mode via CURRENT_USER (StaffAuth's mock session), so it
 *  always reflects whichever mock account is "signed in". */
export function getMyPermissions(userId: string): Promise<string[]> {
  const u = STAFF.find((x) => x.id === userId);
  return delay(clone(u?.effectivePermissions ?? []));
}

// ---- Contractor Registry (docs/design/contractor-registry/spec.md, PR 2/4 — web) ----------------
//
// A contractor is an Org/Person entity on the shared CRM (ADR-0011) — its OWN mock store (not
// REQUESTS/PERMITS) since it carries a disjoint field set (insurance/bond/registration) and status
// value-space (Approved/Pending/Suspended/Expired) from every sibling Request Kind. Seeded with a
// mix covering every acceptance-criteria case the spec calls out: a plain Approved+Public row, one
// Approved+Public row within 30 days of its insurance cert expiring (the "expiring soon" warning
// chip), one Approved+PRIVATE row (must never appear in the public mock directory or picker), one
// Pending row, one Suspended row, and one Expired row (status stored as Approved but past its cert
// date — EXERCISES the read-time-derived EvaluateContractorStatus equivalent below, never a
// pre-flipped stored status, mirroring the live handler's own posture).

function isoDaysFromNow(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Mirrors shared.EvaluateContractorStatus (spec §6) — Expired is DERIVED from the insurance-cert
 *  clock at read time, never mutated into the stored record by a batch job. Mock parity with the
 *  live handler matters here: a test asserting the list/detail/public surfaces all agree on
 *  "Expired" would otherwise silently pass in mock mode while drifting from the real backend. */
function effectiveContractorStatus(c: ContractorView, todayISO = new Date().toISOString().slice(0, 10)): ContractorStatus {
  if (c.status !== "Approved") return c.status;
  if (!c.insuranceCertExpiresAt) return c.status;
  return todayISO > c.insuranceCertExpiresAt ? "Expired" : c.status;
}

/** Mirrors shared.IsContractorPubliclyListed (spec §5) — "visibility == Public AND EFFECTIVE status
 *  == Approved", never the raw stored status. */
function isContractorPubliclyListed(c: ContractorView): boolean {
  return c.visibility === "Public" && effectiveContractorStatus(c) === "Approved";
}

function toPublicContractor(c: ContractorView): PublicContractorView {
  return {
    id: c.id,
    businessName: c.businessName,
    contactName: c.contactName,
    phone: c.phone,
    trades: c.trades ?? [],
    registered: true,
  };
}

let CONTRACTORS: ContractorView[] = [
  {
    id: "contractor-1",
    tenantId: "linnvalley",
    businessName: "All Under One Roof",
    contactName: "Bryan Delap",
    phone: "913-205-5848",
    email: "bryan@allunderoneroof.example",
    mailingAddress: "100 Main St, Linn Valley, KS",
    registrationNumber: "#26033",
    // "trade-other" included deliberately (2026 contractor-list import) — exercises the public
    // directory's + staff console's id→label resolution for the catch-all trade end-to-end.
    trades: ["general-construction", "electrical", "plumbing", "hvac", "roofing", "trade-other"],
    status: "Approved",
    visibility: "Public",
    insuranceCarrier: "Farm Bureau Financial",
    insurancePolicyNumber: "FBF-1120934",
    insuranceCertExpiresAt: isoDaysFromNow(210),
    bondAmount: "$10,000",
    bondSurety: "Western Surety",
    registrationDate: "2026-01-12",
    notes: "",
    createdAt: "2026-01-12T00:00:00Z",
    updatedAt: "2026-01-12T00:00:00Z",
  },
  {
    id: "contractor-2",
    tenantId: "linnvalley",
    businessName: "Lancaster Brothers Heating & Cooling",
    contactName: "Chris Lancaster",
    phone: "913-851-3399",
    email: "chris@lancasterbros.example",
    mailingAddress: "212 Meadowlark Dr, Linn Valley, KS",
    registrationNumber: "#26002",
    trades: ["hvac"],
    status: "Approved",
    visibility: "Public",
    insuranceCarrier: "Farm Bureau Financial",
    insurancePolicyNumber: "FBF-8827341",
    // Within 30 days — exercises the staff list's "⚠ Expires in N days" warning chip and the
    // detail page's expiring-soon banner (spec §3a/§3b).
    insuranceCertExpiresAt: isoDaysFromNow(20),
    bondAmount: "$10,000",
    bondSurety: "Western Surety",
    registrationDate: "2026-01-02",
    notes: "2026-10-20 — Called to confirm renewal paperwork is in progress; insurance cert is being re-issued this week. — PM",
    createdAt: "2026-01-02T00:00:00Z",
    updatedAt: "2026-01-02T00:00:00Z",
  },
  {
    id: "contractor-3",
    tenantId: "linnvalley",
    businessName: "CDL Electric",
    contactName: "Adela Milburn",
    phone: "913-555-0142",
    email: "adela@cdlelectric.example",
    mailingAddress: "88 Shawnee Trl, Linn Valley, KS",
    registrationNumber: "#26042",
    trades: ["electrical", "plumbing", "hvac"],
    status: "Approved",
    // PRIVATE — approved but opted out of the public directory/picker (spec §5's "Private+Approved
    // is absent from the public directory" acceptance criterion).
    visibility: "Private",
    insuranceCarrier: "State Farm",
    insurancePolicyNumber: "SF-553221",
    insuranceCertExpiresAt: isoDaysFromNow(140),
    bondAmount: "$5,000",
    bondSurety: "Western Surety",
    registrationDate: "2026-03-02",
    notes: "",
    createdAt: "2026-03-02T00:00:00Z",
    updatedAt: "2026-03-02T00:00:00Z",
  },
  {
    id: "contractor-4",
    tenantId: "linnvalley",
    businessName: "Sunflower HVAC Services",
    contactName: "Travis Lund",
    phone: "913-555-0199",
    email: "travis@sunflowerhvac.example",
    mailingAddress: "",
    registrationNumber: "",
    trades: ["hvac"],
    status: "Pending",
    visibility: "Public",
    insuranceCarrier: "",
    insurancePolicyNumber: "",
    insuranceCertExpiresAt: "",
    bondAmount: "",
    bondSurety: "",
    registrationDate: "",
    notes: "Awaiting insurance certificate before approval.",
    createdAt: "2026-10-18T00:00:00Z",
    updatedAt: "2026-10-18T00:00:00Z",
  },
  {
    id: "contractor-5",
    tenantId: "linnvalley",
    businessName: "Ironclad General Contracting",
    contactName: "Boone Castor",
    phone: "913-555-0177",
    email: "boone@ironcladgc.example",
    mailingAddress: "45 Prairie Rd, Linn Valley, KS",
    registrationNumber: "#26019",
    trades: ["general-construction"],
    status: "Suspended",
    visibility: "Public",
    insuranceCarrier: "Farm Bureau Financial",
    insurancePolicyNumber: "FBF-2201187",
    insuranceCertExpiresAt: isoDaysFromNow(90),
    bondAmount: "$10,000",
    bondSurety: "Western Surety",
    registrationDate: "2026-01-28",
    notes: "Suspended pending resolution of a code-compliance complaint.",
    createdAt: "2026-01-28T00:00:00Z",
    updatedAt: "2026-01-28T00:00:00Z",
  },
  {
    id: "contractor-6",
    tenantId: "linnvalley",
    businessName: "Crossroads Mechanical",
    contactName: "Wyatt Ferris",
    phone: "913-555-0163",
    email: "wyatt@crossroadsmech.example",
    mailingAddress: "9 Industrial Pkwy, Linn Valley, KS",
    registrationNumber: "#26009",
    trades: ["hvac", "metal-work"],
    // Stored status is still "Approved" — EXPIRED is derived at read time from the lapsed
    // insuranceCertExpiresAt below (effectiveContractorStatus), never pre-flipped here.
    status: "Approved",
    visibility: "Public",
    insuranceCarrier: "Farm Bureau Financial",
    insurancePolicyNumber: "FBF-7709213",
    insuranceCertExpiresAt: isoDaysFromNow(-23),
    bondAmount: "$10,000",
    bondSurety: "Western Surety",
    registrationDate: "2025-09-30",
    notes: "",
    createdAt: "2025-09-30T00:00:00Z",
    updatedAt: "2025-09-30T00:00:00Z",
  },
];

function toContractorQueueRow(c: ContractorView): ContractorQueueRow {
  return { contractor: clone(c), effectiveStatus: effectiveContractorStatus(c) };
}

function toContractorDetailView(c: ContractorView): ContractorDetailView {
  return {
    contractor: clone(c),
    effectiveStatus: effectiveContractorStatus(c),
    // Empty until PR 3 links Request.ContractorID — the section still renders with an explicit
    // empty state (spec §3b acceptance criteria), never hidden.
    permits: [],
  };
}

/** GET /api/admin/contractors mock (spec §3a). */
export function listContractors(): Promise<ContractorQueueRow[]> {
  return delay(CONTRACTORS.map(toContractorQueueRow));
}

/** GET /api/admin/contractors/{id} mock (spec §3b). */
export function getContractor(id: string): Promise<ContractorDetailView | null> {
  const c = CONTRACTORS.find((x) => x.id === id);
  return delay(c ? toContractorDetailView(c) : null);
}

/** POST (create, id="") / PUT (update, id set) mock — mirrors putContractor's server-side upsert
 *  (spec §3d): defaults Status to Pending / Visibility to Public when the caller omits them, same
 *  backstop the live handler applies. */
export function putContractor(input: PutContractorInput, id?: string): Promise<ContractorView> {
  const now = new Date().toISOString();
  const status = input.status || "Pending";
  const visibility = input.visibility || "Public";
  if (id) {
    const existing = CONTRACTORS.find((x) => x.id === id);
    if (!existing) return Promise.reject(new ApiError("We couldn't find that.", 404, "not_found", false));
    const updated: ContractorView = {
      ...existing,
      businessName: input.businessName.trim(),
      contactName: input.contactName.trim(),
      phone: input.phone.trim(),
      email: input.email?.trim() ?? "",
      mailingAddress: input.mailingAddress?.trim() ?? "",
      registrationNumber: input.registrationNumber,
      trades: input.trades,
      status,
      visibility,
      insuranceCarrier: input.insuranceCarrier?.trim() ?? "",
      insurancePolicyNumber: input.insurancePolicyNumber?.trim() ?? "",
      insuranceCertExpiresAt: input.insuranceCertExpiresAt ?? "",
      bondAmount: input.bondAmount?.trim() ?? "",
      bondSurety: input.bondSurety?.trim() ?? "",
      registrationDate: input.registrationDate ?? "",
      updatedAt: now,
    };
    CONTRACTORS = CONTRACTORS.map((x) => (x.id === id ? updated : x));
    return delay(clone(updated));
  }
  const created: ContractorView = {
    id: `contractor-${CONTRACTORS.length + 1}-${Math.floor(Math.random() * 1e6)}`,
    tenantId: "linnvalley",
    businessName: input.businessName.trim(),
    contactName: input.contactName.trim(),
    phone: input.phone.trim(),
    email: input.email?.trim() ?? "",
    mailingAddress: input.mailingAddress?.trim() ?? "",
    registrationNumber: input.registrationNumber,
    trades: input.trades,
    status,
    visibility,
    insuranceCarrier: input.insuranceCarrier?.trim() ?? "",
    insurancePolicyNumber: input.insurancePolicyNumber?.trim() ?? "",
    insuranceCertExpiresAt: input.insuranceCertExpiresAt ?? "",
    bondAmount: input.bondAmount?.trim() ?? "",
    bondSurety: input.bondSurety?.trim() ?? "",
    registrationDate: input.registrationDate ?? "",
    notes: "",
    createdAt: now,
    updatedAt: now,
  };
  CONTRACTORS = [...CONTRACTORS, created];
  return delay(clone(created));
}

/** POST /api/admin/contractors/{id}/renew mock (spec §3b "Renew insurance cert" — staff-confirmed,
 *  not automatic: flips Expired back to Approved, but leaves every OTHER status, e.g. Suspended,
 *  untouched). */
export function renewContractor(id: string, input: RenewContractorInput): Promise<ContractorView> {
  const existing = CONTRACTORS.find((x) => x.id === id);
  if (!existing) return Promise.reject(new ApiError("We couldn't find that.", 404, "not_found", false));
  const now = new Date().toISOString();
  const wasExpired = effectiveContractorStatus(existing) === "Expired";
  const updated: ContractorView = {
    ...existing,
    insuranceCertExpiresAt: input.insuranceCertExpiresAt,
    status: wasExpired ? "Approved" : existing.status,
    updatedAt: now,
  };
  CONTRACTORS = CONTRACTORS.map((x) => (x.id === id ? updated : x));
  return delay(clone(updated));
}

/** POST /api/admin/contractors/{id}/notes mock (spec §3b append-only internal note log). */
export function addContractorNote(id: string, body: string): Promise<{ id: string; createdAt: string }> {
  const existing = CONTRACTORS.find((x) => x.id === id);
  if (!existing) return Promise.reject(new ApiError("We couldn't find that.", 404, "not_found", false));
  const now = new Date().toISOString();
  const stamped = `${now} — ${body}`;
  existing.notes = existing.notes ? `${stamped}\n\n${existing.notes}` : stamped;
  existing.updatedAt = now;
  return delay({ id: `note-${Date.now()}`, createdAt: now });
}

/** GET /api/public/contractors?trade=&q= mock — the SAME visibility=='Public' AND
 *  effectiveStatus=='Approved' predicate as the live handler (spec §5), applied here so a Private,
 *  Pending, Suspended, or Expired contractor is simply absent from this list in mock mode too. */
export function listPublicContractors(trade?: string, q?: string): Promise<PublicContractorView[]> {
  const needle = q?.trim().toLowerCase();
  const rows = CONTRACTORS.filter(isContractorPubliclyListed)
    .filter((c) => !trade || (c.trades ?? []).includes(trade))
    .filter((c) => !needle || c.businessName.toLowerCase().includes(needle))
    .map(toPublicContractor);
  return delay(clone(rows));
}

/** GET /api/public/contractors/{id} mock — resolves `null` (never a distinguishable error) for
 *  EVERY non-matching case: doesn't exist, Private, Pending, Suspended, or Expired (spec §3c's
 *  indistinguishability acceptance criterion). */
export function getPublicContractor(id: string): Promise<PublicContractorView | null> {
  const c = CONTRACTORS.find((x) => x.id === id);
  if (!c || !isContractorPubliclyListed(c)) return delay(null);
  return delay(clone(toPublicContractor(c)));
}
